/**
 * World view — connects to RealmRoom on mount, renders self + remote
 * players + mobs, sends move intent every 80 ms. Self movement is
 * client-predicted (instant feel) and reconciled when server position
 * arrives.
 */

import {
  Color3,
  Color4,
  Matrix,
  MeshBuilder,
  Ray,
  StandardMaterial,
  Texture,
  Vector3,
  ArcRotateCamera,
  TransformNode,
  PointerEventTypes,
  type Mesh,
} from '@babylonjs/core';
import { useEffect, useRef, useState } from 'react';
import { CLASSES } from '../data/classes.js';
import { SIDES, subFactionById } from '../data/factions.js';
import { levelFromXp } from '../data/leveling.js';
import { connectToRealm, disconnect, sendAbility, sendAttack, sendMove, getRoom } from '../game/realmClient.js';
import { installPersistSync } from '../game/persistSync.js';
import { CURSOR } from '../game/cursors.js';
import { installAtmosphere } from '../scene/Atmosphere.js';
import { installPostFX, setDeadModeFx } from '../scene/PostFX.js';
import { Auction } from './Auction.js';
import { Chat } from './Chat.js';
import { CombatText } from './CombatText.js';
import { Crafting } from './Crafting.js';
import { DungeonFinder } from './DungeonFinder.js';
import { LootRollModal } from './LootRollModal.js';
import { Endgame } from './Endgame.js';
import { FpsCounter } from './FpsCounter.js';
import { DebugTerrainHud } from './DebugTerrainHud.js';
// R232h: AuthPanel убран из мирового UI. Available только на Splash.
// import { AuthPanel } from './AuthPanel.js';
import { DungeonHud } from './DungeonHud.js';
import { Inventory } from './Inventory.js';
import { MobileControls } from './MobileControls.js';
import { useTouchInput } from '../state/touch-input-store.js';
import { Leaderboard } from './Leaderboard.js';
import { Minimap, CurrentZoneBadge } from './Minimap.js';
import { MicroMenu, BagSlots } from './MicroMenu.js';
import { NpcNameplates } from './NpcNameplates.js';
import { MobNameplates } from './MobNameplates.js';
import { NpcQuestMarkers } from './NpcQuestMarkers.js';
import { HoverTooltip } from './HoverTooltip.js';
import { WorldMapV2 } from './WorldMapV2.js';
import { QuestTracker } from './QuestTracker.js';
import { Spellbook } from './Spellbook.js';
import { FactionLore } from './FactionLore.js';
import { Achievements, recordZoneVisited } from './Achievements.js';
import { AchievementToast } from './AchievementToast.js';
import { SocialOverlay } from './SocialOverlay.js';
import { ReputationPanel } from './ReputationPanel.js';
import { Bank } from './Bank.js';
import { DailyQuests } from './DailyQuests.js';
import { TradeWindow } from './TradeWindow.js';
import { FriendsList } from './FriendsList.js';
import { MacroPanel } from './MacroPanel.js';
import { KeybindEditor } from './KeybindEditor.js';
import { GameClock } from './GameClock.js';
import { CombatLog } from './CombatLog.js';
import { MountSelector } from './MountSelector.js';
import { MountPicker } from './MountPicker.js';
import { CurrencyTracker } from './CurrencyTracker.js';
import { DpsMeter } from './DpsMeter.js';
import { IgnoreList } from './IgnoreList.js';
import { GroupFinder } from './GroupFinder.js';
import { Harvester } from './Harvester.js';
import { GatherNodeFeedback } from './GatherNodeFeedback.js';
import { Skinner } from './Skinner.js';
import { ProfessionTrainer } from './ProfessionTrainer.js';
import { MatBagPanel } from './MatBagPanel.js';
import { ClassQuests } from './ClassQuests.js';
import { FlightMaster } from './FlightMaster.js';
import { InnkeeperDialog } from './InnkeeperDialog.js';
import { StableMaster } from './StableMaster.js';
import { PetFrame } from './PetFrame.js';
import { SpiritHealer } from './SpiritHealer.js';
import { BattlegroundBrowser } from './BattlegroundBrowser.js';
import { HolidayCalendar } from './HolidayCalendar.js';
import { HolidayActiveBadge } from './HolidayActiveBadge.js';
import { GuestBanner } from './GuestBanner.js';
import { HonorPanel } from './HonorPanel.js';
import { HelpScreen } from './HelpScreen.js';
import { Quartermaster } from './Quartermaster.js';
import { StatsPanel } from './StatsPanel.js';
import { SettingsPanel } from './SettingsPanel.js';
import { ChangeLog } from './ChangeLog.js';
import { DeathRecap } from './DeathRecap.js';
import { RecentLoot } from './RecentLoot.js';
import { LegendaryQuestPanel } from './LegendaryQuestPanel.js';
import { LootPrompt } from './LootPrompt.js';
import { LootToast } from './LootToast.js';
import { DeathOverlay } from './DeathOverlay.js';
import { TutorialBanner } from './TutorialBanner.js';
import { CombatDebugHud } from './CombatDebugHud.js';
import { NpcDialog } from './NpcDialog.js';
import { VendorShop } from './VendorShop.js';
import { TalentTree } from './TalentTree.js';
import { MailBox } from './MailBox.js';
import { Guild } from './Guild.js';
import { CharacterPanel } from './CharacterPanel.js';
import { Battleground } from './Battleground.js';
import { VanillaCastBar } from './CastBar.js';
import { BuffsRow } from './BuffsRow.js';
import { BgFlagTracker } from './BgFlagTracker.js';
import { BgHudOverlay } from './BgHudOverlay.js';
import { BattlegroundMap } from './BattlegroundMap.js';
import { BattlegroundScore } from './BattlegroundScore.js';
// R232q5w: legacy QuestLog (L-key) удалён — используем server-driven ServerQuestLog (J).
// R232h: WalletBadge убран. Currency через Inventory display.
// import { WalletBadge } from './WalletBadge.js';
import { WorldBossBanner } from './WorldBossBanner.js';
import { ZoneToast } from './ZoneToast.js';
import { DailyLoginToast } from './DailyLoginToast.js';
// R232q76: Phase 0 demolition — Sirus 1:1 HUD rebuild in progress.
// Old self-rolled HUD (ActionBar/buffs/cast/XP) is short-circuited until
// sirus/* panels land in Phase 2-3.
import { PreSirusGameplayBlocked } from './PreSirusGameplayBlocked.js';
import { SirusHud } from './sirus/SirusHud.js';
import { QUESTS } from '../data/quests.js';
import { useNet, interpolatedPos } from '../state/net-store.js';
import { useBg } from '../state/bg-store.js';
import { useScene } from '../scene/Engine.js';
import { createPlaceholderCharacter, type PlaceholderCharacterRig } from '../scene/PlaceholderCharacter.js';
import { loadKayKitCharacter, type KayKitRig } from '../scene/KayKitCharacter.js';
import { loadGlbMob, clearGlbMobCache, type GlbMob } from '../scene/GlbMob.js';
import { loadKayKitMob, isKayKitSkeletonKind } from '../scene/KayKitMob.js';
import { scatterProps } from '../scene/NaturePropLoader.js';
import { useUI } from '../state/store.js';
import { useActiveModals } from '../state/active-modals-store.js';
import { useEquip } from '../state/equip-store.js';
import { useAuth } from '../game/auth.js';
import { useCosmetic } from '../state/cosmetic-store.js';
import { playSfx } from '../audio/SfxEngine.js';
import { useAbilityCD } from '../state/ability-cd-store.js';
import { useHotbar, HOTBAR_DND_MIME, HOTBAR_SIZE } from '../state/hotbar-store.js';
import { useDbcAbilities } from '../state/dbc-abilities-store.js';
import { useMacros, MACRO_DND_MIME } from '../state/macros-store.js';
import { spawnAbilityVFX, spawnProjectileVFX, spawnFootstepDust, spawnSwingTrail } from '../scene/AbilityVFX.js';
import { createSpellEffect, defaultSchoolForClass, inferSpellSchool } from '../scene/SpellEffects.js';
import { resolveCollision, clearBlockers } from '../scene/Collisions.js';
import { sampleGroundY, resolvePlayerVsWorld, invalidatePlayerCollisionCache } from '../scene/PlayerCollision.js';
import { startMusic, setMusicZone } from '../audio/MusicEngine.js';

const MOVE_SEND_MS = 80;

// R228: NPC rig registry для distance-culling. Каждый NPC (trainer/questgiver/
// faction leader/profession trainer) пушит сюда {root, x, z, anims}. Render loop
// проверяет дистанцию каждую секунду и setEnabled(false) для дальних. Skeleton
// + animationGroup тоже паузятся → перфоманс не страдает.
interface NpcRigEntry {
  root: import('@babylonjs/core').TransformNode;
  /** Опционально — KayKit rig с setAnimEnabled чтобы паузить anims при cull. */
  rig?: { setAnimEnabled?: (en: boolean) => void };
  x: number;
  z: number;
  /** Текущий enabled-state для авоидинга redundant calls. */
  enabled: boolean;
}
const NPC_RIG_REGISTRY: NpcRigEntry[] = [];
// R232q3: 40 слишком мало — quest giver на 50m был invisible.
// R232q86zi1 PERF: 60→45. У Аббатства ~50 ригов по 100+ геосетов —
// каждый метр радиуса дорог; 45 м на глаз неотличим (капсулы NPC с
// именами читаются дальше, тело догружается при подходе).
const NPC_VIEW_DIST = 45;
const NPC_VIEW_DIST_SQ = NPC_VIEW_DIST * NPC_VIEW_DIST;
(window as unknown as { __aiwariNpcRigs?: NpcRigEntry[] }).__aiwariNpcRigs = NPC_RIG_REGISTRY;

/**
 * R232q86d: safe shadow-caster registration. Babylon's
 * `addShadowCaster(root, true)` walks descendants and adds every node — but
 * TransformNodes lack `getBoundingInfo`, so the next render frame throws
 * `TypeError: p.getBoundingInfo is not a function` from
 * ShadowGenerator._setDefaultAutoExtendShadowProjectionMatrix.
 *
 * The throw fires every single frame for every offending caster — at 60 Hz
 * that's the inner-loop bottleneck that drops the world to 1-2 FPS.
 *
 * This helper only registers actual Mesh descendants (everything that
 * implements getBoundingInfo). Cheap & safe to call on any character / mob
 * rig where the root is a plain TransformNode.
 */
type ShadowGeneratorLike = {
  addShadowCaster: (m: import('@babylonjs/core').AbstractMesh, includeDescendants?: boolean) => void;
};
function addShadowCastersFromRoot(
  sg: ShadowGeneratorLike | undefined | null,
  root: { getChildMeshes?: (directOnly?: boolean) => import('@babylonjs/core').AbstractMesh[] } | null | undefined,
): void {
  if (!sg || !root || typeof root.getChildMeshes !== 'function') return;
  const meshes = root.getChildMeshes(false);
  for (const m of meshes) {
    if (typeof (m as { getBoundingInfo?: unknown }).getBoundingInfo !== 'function') continue;
    try { sg.addShadowCaster(m, false); } catch { /* defensive */ }
  }
}

/**
 * R232q86zx: live-tunable walking direction offset. The Sirus M2 GLBs
 * have a small native facing offset that makes the body walk slightly
 * diagonal (user-reported «идёт боком, ~10° вправо»). Read from URL
 * `?walkOff=N` (in radians) so a tester can dial it without rebuilding.
 * Default 0 = no compensation. Once a stable per-race table is found
 * we'll bake it into RealisticCharacter.ts and remove this dial.
 */
// R232q86zg5: the old `?walkOff` root-yaw dial is retired — the camera
// follows rig.root, so a root offset rotated the camera with the body and
// changed nothing on screen. Use `?facing=N` (RealisticCharacter inner node).

export function World() {
  const { scene } = useScene();
  const draft = useUI((s) => s.draft);
  const players = useNet((s) => s.players);
  const mobs = useNet((s) => s.mobs);
  const selfId = useNet((s) => s.selfId);
  const connected = useNet((s) => s.connected);
  const factionCounts = useNet((s) => s.factionCounts);
  const [targetMobId, setTargetMobId] = useState<string | null>(null);
  const targetMobIdRef = useRef<string | null>(null);
  useEffect(() => { targetMobIdRef.current = targetMobId; }, [targetMobId]);
  // R232q86zh5: bridge the legacy World-local target into the sirus
  // target-store — TargetFrame (рамка цели) and range checks subscribe to
  // useTarget, so Tab/click targeting silently produced NO visible frame
  // (user: «нельзя выделить в таргет никого»).
  useEffect(() => {
    void import('../state/target-store.js').then(({ useTarget }) => {
      useTarget.getState().setTarget?.(targetMobId);
    }).catch(() => { /* best-effort */ });
  }, [targetMobId]);
  // R226: target для player/NPC — отдельный state, рендерится в parallel с mob target frame.
  const [targetPlayerId, setTargetPlayerId] = useState<string | null>(null);
  // NPC target пока не отображается отдельно — клик по NPC сразу открывает диалог.
  // setTargetNpcId оставляю как hook для будущего расширения (RP-режим без диалога).
  const setTargetNpcId = (_id: string | null) => { void _id; };
  const lmbHeldRef = useRef(false);
  const footstepClockRef = useRef(0);  // R131x: SFX cadence accumulator
  const mountVisualRef = useRef<import('@babylonjs/core').Mesh | null>(null);  // R150
  const petMeshRef = useRef<TransformNode | null>(null);  // R166: pet
  const weatherClockRef = useRef(0);  // R169: weather check accumulator
  const npcCullClockRef = useRef(0);  // R228: NPC distance-cull accumulator
  const deadModeRef = useRef(false);  // R232: dead-state FX toggle ref
  const rmbHeldRef = useRef(false);

  const sub = draft.subFactionId ? subFactionById(draft.subFactionId) : undefined;
  const cls = draft.classId ? CLASSES.find((c) => c.id === draft.classId) : undefined;
  const authUserId = useAuth((s) => s.userId);
  // Apply Storm Bonus visually — read in render (already subscribed via faction panel).
  void factionCounts;

  // Connect to realm on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Storm Crystals binding: prefer the live auth user id (set by
        // initAuth on session changes); else legacy ?stormId= URL param;
        // else localStorage; else guest. Only real UUIDs trigger
        // crystal awards server-side.
        const authUserId = useAuth.getState().userId;
        const url = new URL(location.href);
        const fromUrl = url.searchParams.get('stormId');
        let stormId = authUserId ?? fromUrl ?? localStorage.getItem('aiwari-storm-id') ?? '';
        if (fromUrl && !authUserId && /^[0-9a-fA-F-]{36}$/.test(fromUrl)) {
          try { localStorage.setItem('aiwari-storm-id', fromUrl); } catch {}
        }
        if (!stormId) {
          stormId = 'guest-' + Math.random().toString(36).slice(2, 8);
        }
        await connectToRealm({
          stormId,
          displayName: draft.name || 'Странник',
          side: draft.side ?? 'tidal',
          subFactionId: draft.subFactionId ?? '',
          classId: draft.classId ?? 'warrior',
          // R232q5r: per-realm shard. useUI.realm выбран на realm-select экране,
          // persist'ится в localStorage. null → legacy `realm` room (eldran).
          realmId: useUI.getState().realm ?? undefined,
          gender: draft.gender,
          bodyType: draft.bodyType,
          skinTone: draft.skinTone,
          hairStyle: draft.hairStyle,
          hairColour: draft.hairColour,
          race: draft.raceId ?? undefined,
          faction: draft.faction ?? undefined,
        });
        if (cancelled) disconnect();
        // R218: Install client → server persist sync (prof skills / mat-bag / class-quests / flights).
        installPersistSync();
        // R232: ALWAYS overwrite class-default weapon на каждый connect.
        // Раньше seed не перезаписывал если equipped уже непустой → охотник
        // оставался с warrior'ским мечом если поменял класс. Теперь meaningful
        // mainhand всегда matches class.
        {
          const cls = draft.classId ?? 'warrior';
          // R232m: WotLK 3.3.5a Classic стартовое оружие по классу.
          const seed: Record<string, string> =
            cls === 'ranger'                       ? { mainhand: 'oak-bow' }
            : cls === 'mage'                       ? { mainhand: 'apprentice-staff' }
            : cls === 'cleric'                     ? { mainhand: 'wooden-staff' }
            : cls === 'paladin'                    ? { mainhand: 'iron-sword', offhand: 'wooden-shield' }
            : cls === 'rogue'                      ? { mainhand: 'apprentice-dagger', offhand: 'apprentice-dagger-oh' }
            : cls === 'shaman'                     ? { mainhand: 'iron-sword' }
            : cls === 'warlock'                    ? { mainhand: 'wooden-staff' }
            : cls === 'druid'                      ? { mainhand: 'wooden-staff' }
            : { mainhand: 'iron-sword', offhand: 'wooden-shield' };
          const { useEquip } = await import('../state/equip-store.js');
          // Force re-equip — это починит неправильное оружие из старых saves.
          useEquip.getState().setEquipped(seed);
          // Triple-tick — иногда subscribe не срабатывает на самой первой
          // установке (Babylon scene не готова). Re-fire через 1с чтобы rig
          // обновил attached weapon.
          setTimeout(() => useEquip.getState().setEquipped({ ...seed }), 1500);
          setTimeout(() => useEquip.getState().setEquipped({ ...seed }), 3000);
        }
        // Hydrate equipment from DB if logged in. Slight delay so the
        // server's ensureClassWeapon swap has time to finish.
        if (authUserId && draft.name) {
          void (async () => {
            try {
              await new Promise((r) => setTimeout(r, 1200));
              const apiBase = import.meta.env.DEV ? 'http://localhost:2570' : '/aiwari-api';
              const cr = await fetch(`${apiBase}/cosmetics/equipped/${encodeURIComponent(authUserId)}/${encodeURIComponent(draft.name)}`);
              if (cr.ok) {
                const j = await cr.json() as { cape?: string | null; pet?: string | null };
                const { useCosmetic } = await import('../state/cosmetic-store.js');
                useCosmetic.getState().hydrate({ cape: j.cape ?? null, pet: j.pet ?? null });
              }
              const ir = await fetch(`${apiBase}/inventory/${encodeURIComponent(authUserId)}/${encodeURIComponent(draft.name)}`);
              if (ir.ok) {
                const j = await ir.json() as { rows?: Array<{ item_id: string; slot: string | null }> };
                const eq: Record<string, string> = {};
                for (const r of (j.rows ?? [])) {
                  if (r.slot) eq[r.slot] = r.item_id;
                }
                if (Object.keys(eq).length > 0) {
                  const { useEquip } = await import('../state/equip-store.js');
                  useEquip.getState().setEquipped(eq);
                }
              }
            } catch {}
          })();
        }
      } catch (e) {
        console.error('[aiwari] connect failed', e);
      }
    })();
    return () => {
      cancelled = true;
      disconnect();
    };
  }, [draft.name, draft.side, draft.subFactionId, draft.classId, authUserId]);

  // Babylon scene setup — runs once, lives until World unmounts.
  useEffect(() => {
    if (!scene) return;
    const engine = scene.getEngine();
    // R232q81: warm heraldic black to match Engine.tsx clearColor — earlier
    // (0.06,0.09,0.14) cold-blue flashed for the first 1-2 frames before
    // Atmosphere's skydome rendered, which the user perceived as a "blue
    // screen" at world entry. Aligning the two clearColors removes the
    // flash regardless of mesh-load order.
    scene.clearColor = new Color4(0.058, 0.035, 0.0, 1);

    // R232q81: only dispose the legacy 'ground' name (older Engine versions
    // shipped the 50m disc under that name). The new Engine.tsx ground is
    // 'engine-ground' and stays alive as a horizon filler beyond the 7000m
    // World ground footprint.
    const old = scene.getMeshByName('ground');
    if (old) old.dispose();
    // R130: WotLK 3.3.5a-WoW scale — Greenwald Vale (~3.2km×3.2km) west of axis,
    // Sharpcrest (~3.4km×3.5km) east, ~600m neutral border between.
    // Final world: 7000m × 3500m. Subdivisions 280×140 = 39 200 vertices,
    // similar density to old 3000m world (60fps target).
    // Biomes: X<-200 = Greenwald (lime meadow forest), X>+200 = Sharpcrest
    // (rust desert), -200..+200 = Stormveil border strip.
    const ground = MeshBuilder.CreateGround('world-ground', { width: 7000, height: 3500, subdivisions: 200, subdivisionsX: 280 }, scene);
    // 21.07: pre-ADT procedural world (Greenwald/Sharpcrest biome plane).
    // With full ADT coverage it only OCCLUDES the real terrain (the blue /
    // milk full-screen veil). Keep the mesh for collision-rescue raycasts,
    // but never render it.
    ground.isVisible = false;
    {
      // R128 rebuild: WotLK 3.3.5a-vibe biome shading.
      //   Greenwald (Greenwald): bright spring meadow green (#7BB942-style), light yellow flecks
      //   Sharpcrest (Sharpcrest): Mars-red baked clay (#C8542A-style), dust patches
      //   Smooth gradient through neutral 200m central strip.
      // Sky/light is brightened in Atmosphere.ts.
      const positions = ground.getVerticesData('position');
      if (positions) {
        const colours: number[] = [];
        const lerp = (a: number, b: number, t: number) => a * (1 - t) + b * t;
        // Pseudorandom flecks based on world position (deterministic stable noise)
        const fleck = (x: number, z: number, scale: number) => {
          const n = Math.sin(x * 12.9898 + z * 78.233) * 43758.5453;
          return ((n - Math.floor(n)) - 0.5) * scale;
        };
        for (let i = 0; i < positions.length; i += 3) {
          const x = positions[i] ?? 0;
          const z = positions[i + 2] ?? 0;
          const r = Math.hypot(x, z);

          // Gentle rolling hills, clear of starter towns.
          const flatten = r < 80 ? 0.3 : 1.0;
          const baseH =
            Math.sin(x * 0.018) * Math.cos(z * 0.024) * 1.2 +
            Math.sin(x * 0.05 + 1.3) * 0.35 +
            Math.cos(z * 0.06 + 0.7) * 0.30;
          const farRing = r > 800 ? Math.min(8.0, (r - 800) * 0.025) : 0;
          const h = baseH * flatten + farRing;
          positions[i + 1] = h;

          // R130: 200m halfwidth at WotLK 3.3.5a scale — neutral Stormveil
          // border is now 400m wide instead of 160m, matches new 7000m world.
          const elwynnBias = x < -10 ? Math.min(1, (-x - 10) / 200) : 0;
          const desertBias = x > 10  ? Math.min(1, ( x - 10) / 200) : 0;

          // === GREENWALD === SUNLIT bright meadow, NOT dark forest.
          //   Base: bright fresh grass — saturated, almost lime
          const elwR = 0.55, elwG = 0.82, elwB = 0.32;
          //   Flat meadows: even lighter, almost sunny lemon-green tint
          const meadowR = 0.66, meadowG = 0.88, meadowB = 0.38;

          // === SHARPCREST === bright Mars-red, not dark
          const rustR = 0.85, rustG = 0.40, rustB = 0.20;
          const sandR = 0.92, sandG = 0.62, sandB = 0.34;

          // Default neutral grass-tan for the narrow 80m strip
          let cr = 0.72, cg = 0.65, cb = 0.42;

          if (elwynnBias > 0) {
            // mix base elwynn with lighter meadow on flat ground
            const flatLerp = 1 - Math.min(1, Math.abs(h) / 1.5);
            const r2 = lerp(elwR, meadowR, flatLerp);
            const g2 = lerp(elwG, meadowG, flatLerp);
            const b2 = lerp(elwB, meadowB, flatLerp);
            // dirt patches on slopes
            const slope = Math.min(1, Math.abs(h) / 1.5);
            const dirtR = 0.42, dirtG = 0.30, dirtB = 0.18;
            const sR = lerp(r2, dirtR, slope * 0.55);
            const sG = lerp(g2, dirtG, slope * 0.55);
            const sB = lerp(b2, dirtB, slope * 0.55);
            cr = lerp(cr, sR, elwynnBias);
            cg = lerp(cg, sG, elwynnBias);
            cb = lerp(cb, sB, elwynnBias);
          }

          if (desertBias > 0) {
            // mix rust with sand on flat areas
            const flatLerp = 1 - Math.min(1, Math.abs(h) / 1.5);
            const r2 = lerp(rustR, sandR, flatLerp * 0.4);
            const g2 = lerp(rustG, sandG, flatLerp * 0.4);
            const b2 = lerp(rustB, sandB, flatLerp * 0.4);
            // dark cracked-clay rock patches
            const slope = Math.min(1, Math.abs(h) / 1.5);
            const rockR = 0.45, rockG = 0.20, rockB = 0.10;
            const sR = lerp(r2, rockR, slope * 0.5);
            const sG = lerp(g2, rockG, slope * 0.5);
            const sB = lerp(b2, rockB, slope * 0.5);
            cr = lerp(cr, sR, desertBias);
            cg = lerp(cg, sG, desertBias);
            cb = lerp(cb, sB, desertBias);
          }

          // Per-vertex micro-fleck for organic variation (±5%)
          const f = fleck(x, z, 0.05);
          cr = Math.max(0, Math.min(1, cr + f));
          cg = Math.max(0, Math.min(1, cg + f * 0.8));
          cb = Math.max(0, Math.min(1, cb + f * 0.6));

          // Path/road near plaza (still flat)
          if (r > 14 && r < 22) {
            const t = 1 - Math.abs(r - 18) / 4;
            cr = lerp(cr, 0.78, t * 0.5);
            cg = lerp(cg, 0.68, t * 0.5);
            cb = lerp(cb, 0.42, t * 0.5);
          }

          colours.push(cr, cg, cb, 1);
        }
        ground.updateVerticesData('position', positions);
        ground.setVerticesData('color', colours, false, 4);
        ground.createNormals(false);
      }
    }
    const groundMat = new StandardMaterial('world-ground-mat', scene);
    groundMat.diffuseColor = new Color3(1, 1, 1);
    groundMat.specularColor = new Color3(0, 0, 0);
    // R232q5aw: emissive 0.65 + Atmosphere.ts lighting (sun 4.3, hemi 2.05,
    // envIntensity 1.2) was over-exposing the ground to a flat white sheet
    // («белая мутная terrain»).  The lighting alone is bright enough now,
    // so we drop emissive close to zero — keep a sliver so the ground
    // doesn't crush black on screens with low gamma.
    groundMat.emissiveColor = new Color3(0.08, 0.08, 0.08);
    // Stylised tileable PBR-style grass texture. Vertex colours multiply
    // on top so biome regions still tint toward dirt/sand.
    // R232q5dm: HTTP2 errors на первой загрузке — добавил error handler
    // + fallback к solid colour чтобы земля не была прозрачной.
    {
      const grass = new Texture(`${import.meta.env.BASE_URL}textures/grass-tile.png`, scene,
        false, false, undefined,
        () => { grass.uScale = 18; grass.vScale = 18; groundMat.diffuseTexture = grass; },
        (msg, e) => {
          console.warn('[grass-tile] failed, using solid colour:', msg, e);
          groundMat.diffuseColor = Color3.FromHexString('#5A6B3A'); // зелёный fallback
        });
      grass.uScale = 18;
      grass.vScale = 18;
      groundMat.diffuseTexture = grass;
    }
    ground.material = groundMat;
    ground.useVertexColors = true;

    // R232q5aE+R232q5aR+R232q54: Sprint 4 + Wave 3b #2 — load every shipped
    // ADT JSON via AdtTerrainCache. Each tile is a 16×16-MCNK splat-blended
    // mesh (built inside AdtTerrain.ts: 4-layer GLSL shader + per-chunk
    // alpha atlas). Ships: Elwynn (Azeroth 31-32 × 50-51), Tirisfal (30-31 ×
    // 31-32), Durotar (Kalimdor 36-37 × 22-23), Mulgore (35 × 30-31). The
    // procedural ground stays as graceful backstop for any zone without ADT
    // data (Northrend, Outland, etc.).
    type AdtCacheRef = {
      disposeAll: () => void;
      loadZoneAdtSet: (opts: {
        map: string;
        centerX: number;
        centerY: number;
        radius?: number;
        bounds?: [number, number, number, number];
      }) => Promise<unknown>;
      evictByDistance: (playerX: number, playerZ: number) => number;
    };
    let adtCache: AdtCacheRef | null = null;
    void (async () => {
      try {
        const mod = await import('../scene/AdtTerrain.js');
        const cache = new mod.AdtTerrainCache({
          scene,
          // R232q86zcd: cut maxTiles 25 → 9 to bound memory + draw calls.
          maxTiles: 9,
          textureResolver: mod.defaultBlpResolver(scene),
          fallbackTexture: groundMat.diffuseTexture as Texture | null,
          // R232q86zce: aggressive eviction (3km → 1.2km) so tiles
          // outside player view get dropped fast. Each tile = 16k verts
          // + 1 texture; 70+ tiles in scene caused FPS=1.
          evictDistanceM: 1200,
        });
        adtCache = cache;
        // Real Sirus doodads (trees/fences) + WMO buildings per tile.
        void import('../scene/AdtPlacements.js').then(({ installTilePlacements }) => {
          mod.setTilePlacementsHook((_m, _x, _y, json, key) =>
            installTilePlacements(scene, json as unknown as Parameters<typeof installTilePlacements>[1], key));
          (window as unknown as { __aiwariDebug?: Record<string, unknown> }).__aiwariDebug ??= {};
          (window as unknown as { __aiwariDebug: Record<string, unknown> }).__aiwariDebug.placementsHook = 'set';
        }).catch((err) => {
          (window as unknown as { __aiwariDebug?: Record<string, unknown> }).__aiwariDebug ??= {};
          (window as unknown as { __aiwariDebug: Record<string, unknown> }).__aiwariDebug.placementsHook = 'import-failed: ' + String(err).slice(0, 200);
        });
        // Load every shipped starter region in parallel — each is its own
        // splat-blended sub-grid anchored at the right Azeroth/Kalimdor
        // tile coordinates. AdtTerrainCache.load() de-dupes concurrent
        // calls for the same key.
        // R232q86e3: cut from 14 → 4 starter tiles. Each ADT tile carries
        // ~20-30 MB heap (16k-vert mesh + 4-8 BLP textures + alpha atlas).
        // Loading 14 at boot = ~300 MB just for terrain that the player can't
        // even see (8 of 14 are 10 km from spawn). User reported repeated
        // "Опаньки" OOM after my scatter+NPC cuts — ADT was the next big
        // RAM consumer. AdtTerrainCache.evictDistanceM=3000 disposes far
        // tiles automatically; the cap is just to bound BOOT memory until
        // the player walks somewhere.
        // R232q86zb2: canonical WoTLK 3.3.5a tile coordinates (server now lives
        // in WoTLK world space 1:1). Boot-load:
        //   • Northshire / Elwynn: Azeroth (31, 48), (31, 49), (32, 48), (32, 49)
        //     — covers tidal spawn (-8918, -132).
        //   • Coldridge Valley:    Azeroth (33, 49) — covers rooted spawn
        //     (-6065, -1131).
        //   • Tirisfal / Deathknell: Azeroth (30, 31), (31, 31) — covers
        //     anchorless spawn (1676, 1677).
        //   Dynamic loader (below) streams the rest via player position.
        // R232q86zg3: Tirisfal boot tiles (30/31, 31/31) removed — that legacy
        // faction spawn is retired and they only burned boot RAM + spawned
        // placements 10 km from the player.
        // R232q86zg4: boot set trimmed to the spawn tile + its two road
        // neighbours — the dynamic streamer pulls the rest as you walk.
        // Every extra boot tile is ~1.2 MB gz on the world-load critical path.
        const shippedTiles: Array<{ map: string; x: number; y: number }> = [
          { map: 'Azeroth', x: 32, y: 48 }, { map: 'Azeroth', x: 31, y: 48 },
          { map: 'Azeroth', x: 32, y: 49 },
        ];
        const settled = await Promise.allSettled(
          shippedTiles.map((t) => cache.load(t.map, t.x, t.y)),
        );
        const loaded = settled.filter((r) => r.status === 'fulfilled' && r.value).length;
        if (loaded > 0) {
          // R232q86s: keep engine-ground visible as flat baseline; ADT mostly
          // off-camera в этой стартовой зоне до полной геодаты.
          ground.position.y = -0.05;
          // R232q86zE: engine-ground COVERS ±3500m around origin only,
          // but most starter zones live at |x| > 8000 (Northshire,
          // Coldridge, Durotar). When the player roams there the engine
          // ground is far away — but its Y-snap raycast hit is at y=-0.05
          // which would override real ADT terrain (~80m) if it ranks
          // first. Mark engine-ground as NOT pickable so Y-snap reaches
          // through it to the actual ADT mesh underneath/nearby.
          ground.isPickable = false;
          if (typeof console !== 'undefined') {
            console.info(`[AdtTerrain] R232q86s: ${loaded}/${shippedTiles.length} ADT tiles loaded; engine-ground kept visible (isPickable=false) as horizon filler.`);
          }
        }
      } catch (exc) {
        if (typeof console !== 'undefined') {
          console.warn('[AdtTerrain] init failed (procedural ground stays):', exc);
        }
      }
    })();

    // R232q86zb2: ADT dynamic streaming — registered после selfRigBox init
    // ниже, чтобы closure захватывал уже-инициализированный rig.

    // Self rig — placeholder first (instant), GLB swaps in once loaded.
    // We hold the rig in a mutable box so the render loop closure picks up
    // the swapped instance.
    // R232q5dm: HIDE placeholder geometry — раньше юзер видел "палки/сферы"
    // 2 минуты пока UBC+UAL грузились. Теперь placeholder невидимый, но
    // root TransformNode остаётся (для position tracking).
    const selfRigBox = {
      rig: createPlaceholderCharacter(scene, { gender: draft.gender, body: draft.bodyType, scale: 1.0 }) as PlaceholderCharacterRig,
    };
    selfRigBox.rig.root.position = new Vector3(0, 0, 0);
    const accent = Color3.FromHexString(sub?.colour ?? '#3D4A6E');
    selfRigBox.rig.setColours(Color3.FromHexString('#F2C896'), Color3.FromHexString(draft.hairColour), accent);
    // Hide all child meshes of placeholder — invisible до swap на realistic.
    for (const m of selfRigBox.rig.root.getChildMeshes(false)) {
      m.setEnabled(false);
    }

    (window as unknown as { __aiwariSelfRig?: PlaceholderCharacterRig }).__aiwariSelfRig = selfRigBox.rig;

    // R232q86zb2: ADT dynamic streaming — каждые 4с подтягиваем 3×3 ring
    // вокруг игрока, каждые 12с вытесняем тайлы дальше evictDistanceM (3 km).
    // Server space теперь 1:1 WoTLK, поэтому player.pos.x = world_x,
    // player.pos.z = world_y. Tile (X, Y) covers world_x ∈ [(32-Y-1)*TILE,
    // (32-Y)*TILE], world_y ∈ [(32-X-1)*TILE, (32-X)*TILE].
    {
      const ADT_TILE_SIZE = 533.3333;
      let lastAdtLoad = 0;
      let lastAdtEvict = 0;
      // R232q86zd6 fix #3: tracked-set of Geoset/group meshes so the
      // orphan-prune walk doesn't scan all 10k+ scene.meshes every 6s.
      // Hooked at scene.onNewMeshAddedObservable so any newly-imported
      // RealisticCharacter rig auto-registers; pruned on dispose.
      const trackedGeosetMeshes = new Set<import('@babylonjs/core').AbstractMesh>();
      scene.onNewMeshAddedObservable.add((m) => {
        const n = m.name ?? '';
        if (n.startsWith('Geoset') || n.startsWith('group')) {
          trackedGeosetMeshes.add(m);
          // Auto-deregister on mesh dispose to prevent unbounded growth.
          m.onDisposeObservable.addOnce(() => { trackedGeosetMeshes.delete(m); });
        }
      });
      // Cleanup при scene.dispose() автоматический — observable die вместе со сценой.
      scene.onBeforeRenderObservable.add(() => {
        if (!adtCache) return;
        const root = selfRigBox.rig?.root;
        if (!root) return;
        const px = root.position.x;
        const pz = root.position.z;
        // Until the server position sync lands, the rig idles near the world
        // origin (legacy island at ~(0,42)) — streaming there pulled map-centre
        // tiles, evicted the real starter tiles, then re-loaded everything
        // after the sync (a load/evict carousel that tanked FPS every login).
        // The Azeroth map centre is empty ocean — nothing legit to stream
        // within 600 m of (0,0).
        if (Math.abs(px) < 600 && Math.abs(pz) < 600) return;
        const now = performance.now();
        // R232q86zce: dynamic streaming spammed loadZoneAdtSet despite
        // evictByDistance — 124 tiles in scene → FPS 1.0. Throttle 4s→8s
        // and force evict-by-distance BEFORE each load to keep the
        // working-set tight (max 9 active tiles at any time).
        if (now - lastAdtLoad > 8000) {
          lastAdtLoad = now;
          const tileY = Math.floor(32 - px / ADT_TILE_SIZE);
          const tileX = Math.floor(32 - pz / ADT_TILE_SIZE);
          const mapName = px >= -12000 && px <= 5000 && pz >= -7000 && pz <= 4500
            ? 'Azeroth'
            : 'Kalimdor';
          // Force eviction first.
          adtCache.evictByDistance(px, pz);
          adtCache.loadZoneAdtSet({ map: mapName, centerX: tileX, centerY: tileY, radius: 1 }).catch((e) => {
            console.warn('[AdtTerrain] streaming load failed:', (e as Error).message);
          });
        }
        if (now - lastAdtEvict > 6000) {
          lastAdtEvict = now;
          const dropped = adtCache.evictByDistance(px, pz);
          if (dropped > 0) console.info(`[AdtTerrain] evicted ${dropped} far tiles`);
          // R232q86zcl / R232q86zd6 fix #3: prune ORPHAN Geoset* meshes that
          // linger from disposed RealisticCharacter rigs. Walks the
          // pre-registered `trackedGeosetMeshes` set (typically <500 entries)
          // instead of scene.meshes (10k+) — eviction was 18ms+ per cycle on
          // big scenes. Also blocks Babylon's active-mesh recompute during
          // the dispose burst so each removal isn't a full rebuild.
          let orphans = 0;
          const prevBlock = scene.blockfreeActiveMeshesAndRenderingGroups;
          scene.blockfreeActiveMeshesAndRenderingGroups = true;
          try {
            const dead: import('@babylonjs/core').AbstractMesh[] = [];
            for (const m of trackedGeosetMeshes) {
              const p = m.parent as { _isDisposed?: boolean } | null;
              if (!p || p._isDisposed) dead.push(m);
            }
            for (const m of dead) {
              try { m.dispose(false, true); orphans++; } catch { /* nop */ }
              trackedGeosetMeshes.delete(m);
            }
          } finally {
            scene.blockfreeActiveMeshesAndRenderingGroups = prevBlock;
          }
          if (orphans > 0) console.info(`[AdtTerrain] pruned ${orphans} orphan Geoset/group meshes (tracked-set walk)`);
        }
      });
    }

    // R232q5i: ВСЕ player rigs теперь грузятся из Sirus AIWARI character set
    // (/aiwari/models/characters/aiwari_{race}_{gender}.glb). Никаких
    // Quaternius UBC / Sketchfab fan-art / KayKit Adventurer fallbacks.
    // RaceModelMap резолвит legacy race id (human/dwarf/...) в одну из 6
    // AIWARI рас (felclan/boneborn/mistborn/steppefolk/frostkin/woodkin).
    let cancelled = false;
    const raceId = (draft.raceId ?? 'human') as import('../scene/RaceModelMap.js').Race;
    const gender = (draft.gender ?? 'male') as import('../scene/RaceModelMap.js').Gender;
    const loadPromise: Promise<{ root: import('@babylonjs/core').TransformNode; setColours?: (...a: unknown[]) => void; setMoving: (m: boolean, r?: boolean) => void; update: (dt: number) => void; setEquipment: (e: Record<string, string>) => void; setCosmetics: (e: { cape: string|null; pet: string|null }) => void; triggerAttack: () => void; dispose: () => void }> = Promise.all([
          import('../scene/RaceModelMap.js'),
          import('../scene/RealisticCharacter.js'),
        ]).then(async ([rmm, rc]) => {
          const pick = rmm.getRaceModelGlb(raceId, gender);
          // R232q86e: detect "broken rig" — Sirus GLB загрузился без ошибки
          // но геометрия пустая / skeleton не пришёл (sanitizer выбил
          // multi-parented bones, остался корпус без рук/ног). Юзер репортил
          // "ошмётки опять вместо персонажей". Проверяем по child mesh count
          // ПЛЮС суммарному vertex count — KayKit Adventurer имеет 5+ submeshes
          // ~10k vertices, broken Sirus rig обычно <500 vertices total.
          const isBrokenRig = (rig: { root: import('@babylonjs/core').TransformNode } | null | undefined): boolean => {
            if (!rig?.root) return true;
            const meshes = rig.root.getChildMeshes(false);
            if (meshes.length < 2) return true;
            let totalVerts = 0;
            for (const m of meshes) totalVerts += (m as { getTotalVertices?: () => number }).getTotalVertices?.() ?? 0;
            return totalVerts < 500;
          };
          // R232q86o: Sirus chain RESTORED via v1 m2_to_glb.py (static T-pose,
          // no skinning) — это ровно тот path что юзер видел работающим в
          // commit e11d918 (4 May), когда Tauren рендерился корректно.
          // Без skinning/multi-parent = clean glTF, Babylon 8 загружает без
          // warnings. Animations придут отдельным AnimationGroup channel
          // позже (idle/walk/attack via procedural keyframes или from v2 GLB
          // applied as override).
          for (const step of pick.chain) {
            if (step.glb === rmm.AIWARI_CHAR_PLACEHOLDER) {
              const rig = await rc.loadRealisticCharacter(scene, { glb: step.glb, baseDir: step.baseDir, scale: pick.scale, targetTallest: pick.targetHeight, raceId, gender, skinVariant: draft.skinVariant, faceStyle: draft.faceStyle });
              if (isBrokenRig(rig)) break;
              return rig;
            }
            try {
              const res = await fetch(`${step.baseDir}${step.glb}`, { method: 'HEAD' });
              if (!res.ok) continue;
              const ct = (res.headers.get('content-type') ?? '').toLowerCase();
              if (ct.includes('text/html')) continue;
              const rig = await rc.loadRealisticCharacter(scene, { glb: step.glb, baseDir: step.baseDir, scale: pick.scale, targetTallest: pick.targetHeight, raceId, gender, skinVariant: draft.skinVariant, faceStyle: draft.faceStyle });
              if (isBrokenRig(rig)) {
                console.warn('[aiwari] Sirus rig broken, trying next step', step);
                continue;
              }
              return rig;
            } catch (e) { console.error('[aiwari] rig chain step failed', step, e); }
          }
          console.warn('[aiwari] Sirus chain exhausted — KayKit fallback');
          return await loadKayKitCharacter(scene, { classId: draft.classId ?? 'warrior', gender: draft.gender, raceId: draft.raceId ?? undefined });
        }) as never;
    void loadPromise.then((rig) => {
      if (cancelled) { rig.dispose(); return; }
      const oldPos = selfRigBox.rig.root.position.clone();
      const oldRot = selfRigBox.rig.root.rotation.y;
      selfRigBox.rig.dispose();
      selfRigBox.rig = rig as unknown as PlaceholderCharacterRig;
      selfRigBox.rig.root.position = oldPos;
      selfRigBox.rig.root.rotation.y = oldRot;
      (window as unknown as { __aiwariSelfRig?: PlaceholderCharacterRig }).__aiwariSelfRig = selfRigBox.rig;
      selfRigBox.rig.setEquipment(useEquip.getState().equipped);
      selfRigBox.rig.setCosmetics({ cape: useCosmetic.getState().cape, pet: useCosmetic.getState().pet });
      // R232q53: apply persisted customization (skin/face/hair/facialHair)
      // on top of the freshly-loaded rig. Sirus rigs expose `setCustomization`;
      // KayKit fallback rigs don't, in which case we silently skip.
      try {
        const realistic = selfRigBox.rig as unknown as {
          setCustomization?: (c: import('../scene/CharCustomization.js').CharCustomization) => Promise<void>;
        };
        if (typeof realistic.setCustomization === 'function') {
          void realistic.setCustomization({
            skinVariant: draft.skinVariant,
            faceStyle: draft.faceStyle,
            hairStyle: draft.hairStyle,
            hairColourIdx: draft.hairColourIdx,
            facialHair: draft.facialHair,
          });
        }
      } catch { /* customization is purely cosmetic */ }
      try {
        const sg = (scene as unknown as { __aiwariShadowGen?: ShadowGeneratorLike }).__aiwariShadowGen;
        addShadowCastersFromRoot(sg, selfRigBox.rig.root);
      } catch { /* shadow gen optional */ }
    }).catch((err) => {
      console.warn('[aiwari] AIWARI character load failed, keeping placeholder:', err);
      // Placeholder visible-toggle: при полном fail — раскрываем placeholder
      // primitives чтобы юзер хотя бы что-то видел.
      for (const m of selfRigBox.rig.root.getChildMeshes(false)) m.setEnabled(true);
    });

    // Mirror equipment store → visible gear on self rig.
    selfRigBox.rig.setEquipment(useEquip.getState().equipped);
    const unsubEquip = useEquip.subscribe((s) => {
      selfRigBox.rig.setEquipment(s.equipped);
      // R232i: send computed gear bonuses to server для применения к stats.
      void import('../state/effective-stats.js').then(({ computeEquipmentBonuses }) => {
        const bonuses = computeEquipmentBonuses(s.equipped);
        const room = getRoom();
        if (room) {
          room.send('gear-stats', {
            hp: bonuses.hp, pow: bonuses.pow, armor: bonuses.armor,
            resource: bonuses.resource, crit: bonuses.crit,
            haste: bonuses.haste, mastery: bonuses.mastery, vers: bonuses.vers,
          });
        }
      }).catch(() => {});
    });
    // Initial fire — send gear-stats для текущего equipped (через 1s после connect).
    setTimeout(() => {
      void import('../state/effective-stats.js').then(({ computeEquipmentBonuses }) => {
        const bonuses = computeEquipmentBonuses(useEquip.getState().equipped);
        const room = getRoom();
        if (room) {
          room.send('gear-stats', {
            hp: bonuses.hp, pow: bonuses.pow, armor: bonuses.armor,
            resource: bonuses.resource, crit: bonuses.crit,
            haste: bonuses.haste, mastery: bonuses.mastery, vers: bonuses.vers,
          });
        }
      }).catch(() => {});
    }, 1500);

    // Mirror cosmetic-equip store → cosmetic overlays.
    {
      const c = useCosmetic.getState();
      selfRigBox.rig.setCosmetics({ cape: c.cape, pet: c.pet });
    }
    const unsubCosmetic = useCosmetic.subscribe((s) => selfRigBox.rig.setCosmetics({ cape: s.cape, pet: s.pet }));

    // Atmosphere — skybox + fog + dynamic sun.
    const atmosphere = installAtmosphere(scene);
    // Post-FX — bloom, FXAA, tone mapping, glow on emissive materials.
    const postFX = installPostFX(scene);
    // Ambient music — kicks in after the next user gesture (audio context
    // gating). Faction-flavoured zone is set via setMusicZone below.
    setMusicZone(draft.side === 'tidal' ? 'tidal' : draft.side === 'rooted' ? 'rooted' : draft.side === 'anchorless' ? 'anchorless' : 'overworld');
    // R232m: immediately try startMusic — если AudioContext уже unlocked
    // (юзер тыкнул "Войти в мир"), музыка стартанёт сразу без второго gesture.
    startMusic();
    // Sirus 1:1 zone ambience (Elwynn forest birds) alongside the music.
    void import('../audio/AudioManager.js').then(({ audio }) => audio.playAmbient('forest_birds'));
    const musicGesture = () => {
      startMusic();
      window.removeEventListener('pointerdown', musicGesture);
      window.removeEventListener('keydown', musicGesture);
    };
    window.addEventListener('pointerdown', musicGesture);
    window.addEventListener('keydown', musicGesture);

    // Scenery.
    clearBlockers();
    invalidatePlayerCollisionCache();
    const scenery = new TransformNode('scenery-root', scene);
    addTreesAndRocks(scene, scenery);
    // R232q86zb2: 1:1 metr-в-метр coord. Старые AIWARI custom villages
    // (Greenfield / Sharpcrest / Cogwarren) сидели в compressed-space
    // координатах (-1500/+1500/(-3300,-1100)). После перехода на WoTLK
    // world space они попадают в случайные места (Hillsbrad / Tirisfal /
    // Durotar). До extraction Stormwind / Orgrimmar / Ironforge WMO их
    // отключаем, чтобы не торчать в чистом поле. Северо-Темрик —
    // единственный stage с Sirus WMO.
    // R232q86zg2: hand-placed Northshire abbey retired — AdtPlacements now
    // spawns every WMO (incl. NSAbbey) from the tile's real MODF data.
    // Keeping both produced a duplicate abbey at a hand-tuned offset.
    // R232q86e3: addQuestGivers + 8 batch-spawners disabled — каждый грузил
    // Sirus character GLB (5-7 MB binary + 50-100 MB decoded textures) per
    // entity без distance gate → 149 skeletons + 22 933 anim groups + 1.4 GB
    // texture heap → Chrome OOM. Server NPCs всё равно появятся через
    // attachNpcMeshes (capsule body + nameplate). Когда базовый gameplay
    // стабилизируется, будем поэтапно возвращать static spawners с
    // distance-gate + master cache LRU.
    void addQuestGivers; // keep import alive
    // R232q5h Phase C+D: WotLK 3.3.5a-style biome floors + procedural landmarks
    // (towers/totems/moonwells/witch huts) per zone. Uses Babylon primitives
    // + tile textures — no external GLB dependency. Biome floors first so
    // landmarks land on the right colour underneath. Lazy 3.5s for floors
    // R232q5h3: tightened lazy schedule — landscape decor visible <2s after
    // spawn so the world feels alive immediately. Pre-fix the user saw a
    // bare grass plain at spawn because biome floors / roads / decor /
    // landmarks were on a 3.5-6.5s timer.
    window.setTimeout(() => {
      void import('../scene/BiomeFloors.js').then(({ installBiomeFloors }) => {
        installBiomeFloors(scene, scenery);
      });
    }, 600);
    window.setTimeout(() => {
      void import('../scene/RoadsAndRivers.js').then(({ installRoadsAndRivers }) => {
        installRoadsAndRivers(scene, scenery);
      });
    }, 1000);
    // R232q5t: procedural NPC bodies — server NPCs (state.npcs) get capsule
    // bodies coloured by faction with role-glyph cube above. Until proper
    // NPC GLBs are extracted from Sirus, this gives the player something
    // visible to walk up to (currently NPCs only appeared as floating
    // nameplates without 3D mesh).
    window.setTimeout(() => {
      void import('../scene/NpcMeshes.js').then(({ attachNpcMeshes }) => {
        attachNpcMeshes(scene);
      });
    }, 1200);
    // R232q5aP-spawn: gather node meshes (mining veins / herb patches).
    // Subscribes to useGatherNodes store, populated by realmClient onAdd.
    window.setTimeout(() => {
      void import('../scene/GatherNodeMeshes.js').then(({ attachGatherNodeMeshes }) => {
        attachGatherNodeMeshes(scene);
      });
    }, 1300);
    // Procedural crate/fence/sign decor DISABLED (owner call 21.07): the
    // untextured boxes read as «чёрные кубики» in the world. Real Sirus
    // doodads from the ADT placements replace this set.
    // window.setTimeout(() => {
    //   void import('../scene/VillageDecor.js').then(({ installVillageDecor }) => {
    //     installVillageDecor(scene, scenery);
    //   });
    // }, 1400);
    // 21.07: procedural landmark props (giant stretched watchtowers) disabled —
    // real Sirus WMOs replace them zone by zone.
    window.setTimeout(() => {
      void 0 && import('../scene/VanillaLandmarks.js').then(({ installVanillaLandmarks }) => {
        installVanillaLandmarks(scene, scenery);
      });
    }, 1800);
    // R232q86e3: 9 static-NPC batch spawners disabled (см. addQuestGivers
    // выше). Каждый грузил отдельный Sirus character GLB → OOM. Восстановим
    // когда добавим distance gating + master LRU cache.
    addFlightMasters(scene, scenery);
    addDungeonPortals(scene, scenery);
    void addClassTrainers; void addResourceNodes; void addProfessionTrainers;
    void addGeneralGoodsVendors; void addInnkeepers; void addStableMasters;
    void addSpiritHealers; void addQuartermasters; void addFactionLeaders;
    void addCityProps;
    // Pick up grass clusters for wind-sway animation in the render loop.
    const swayables: { node: TransformNode; phase: number; baseY: number }[] = [];
    for (const t of scene.transformNodes) {
      if (t.name.startsWith('grass-cluster-')) {
        swayables.push({ node: t, phase: t.position.x * 0.31 + t.position.z * 0.17, baseY: t.rotation.y });
      }
    }
    // R131b: denser biome scatter, smaller skip radius so trees grow
    // right up to the village edge. ~2200 props total — instances are
    // mass-cloned and freezeWorldMatrix'd, so perf stays in budget.
    // R232q5h T3.2: lazy-load by player distance — starter zones spawn
    // immediately, distant biomes wait until player approaches OR until
    // 30s after world boot (whichever first). Cuts boot-time CPU by ~70%.
    {
      const rng = mulberry32(2026);
      // R232q5h T3.2: tell scatterProps where the player is so it can defer
      // far-away clusters without each call site needing to opt-in.
      const selfStart = (() => {
        const s = useNet.getState().selfId ? useNet.getState().players[useNet.getState().selfId!] : null;
        return s ? { x: s.pos.x, z: s.pos.z } : { x: 0, z: 0 };
      })();
      (window as unknown as { __aiwariLazyOrigin?: { x: number; z: number } }).__aiwariLazyOrigin = selfStart;
      // R232q5i: ВСЕ scatter теперь только Sirus extracted .obj из
      // /models/wow-vanilla/nature/. Никаких Quaternius CommonTree_*/Bush_*/
      // Rock_*/PalmTree_*/PineTree_Snow_*/Flowers.obj и т.д.
      // ── Greenwald (west) — лес из Elwynn/Arathi/AZR/IronForge sets.
      void scatterProps(scene, {
        fileNames: [
          'AZR_Tree01.obj','AZR_Tree02.obj','AZR_Tree03.obj','AZR_Tree07.obj',
          'ArathiTree03.obj','ArathiTree04.obj','CanopylessTree03.obj',
          'ElwynnTreeCanopy02.obj','ElwynnTreeCanopy03.obj','ElwynnTreeCanopy04.obj',
          'ElwynnPine02.obj','IronForgeTree01.obj','IronForgeTree02.obj',
        ],
        count: 600, scaleMin: 2.4, scaleMax: 3.8, skipRadius: 40, rng,
        centerX: -1500, centerZ: 0, diskRadius: 600,
      });
      void scatterProps(scene, {
        fileNames: [
          'ElwynnTreeCanopy02.obj','ElwynnTreeCanopy03.obj','ElwynnTreeCanopy04.obj',
          'ElwynnPine02.obj','IronForgeTree01.obj','IronForgeTree02.obj','IronForgeTree03.obj','IronforgeTree04.obj',
          'SilverPineTree02.obj','SilverPineTree04.obj','StoneTree05.obj','StoneTree06.obj','StoneTree08.obj',
        ],
        count: 600, scaleMin: 2.0, scaleMax: 3.4, skipRadius: 0, rng,
        centerX: -2000, centerZ: 0, diskRadius: 1400,
      });
      void scatterProps(scene, {
        fileNames: [
          'ElwynnBush04.obj','ElwynnBush09.obj','SilverPineBush01.obj',
          'IronForgeBush01.obj','DarkshoreBush01.obj','DarkshoreBush05.obj',
          'ElwynnDetailFlowers01.obj','ElwynnFlower02.obj',
        ],
        count: 600, scaleMin: 0.7, scaleMax: 1.4, skipRadius: 20, rng,
        centerX: -1500, centerZ: 0, diskRadius: 800,
      });
      void scatterProps(scene, {
        fileNames: [
          'ArathiFlatRock01.obj','ArathiRock04.obj','ArathiRock13.obj','AzsharaRock03.obj',
          'IronForgeCliff01.obj','IronForgeCliff03.obj','MullgoreRock02.obj','MullgoreRock03.obj',
          'FlatRock01.obj',
        ],
        count: 350, scaleMin: 0.7, scaleMax: 2.0, skipRadius: 25, rng,
        centerX: -1500, centerZ: 0, diskRadius: 1500,
      });
      // ── Sharpcrest (east, Horde/Undead starter) — Durotar+Barrens.
      void scatterProps(scene, {
        fileNames: [
          'DurotarPalm01.obj','DurotarPalm02.obj','CactusApple01.obj',
          'BadlandsShrub03.obj','BadlandsShrub04.obj','BarrensBush01.obj',
        ],
        count: 500, scaleMin: 1.5, scaleMax: 3.0, skipRadius: 30, rng,
        centerX: 1500, centerZ: 0, diskRadius: 1700,
      });
      void scatterProps(scene, {
        fileNames: [
          'DurotarTree02.obj','DurotarTree03.obj','DurotarTree05.obj','DurotarTree06.obj','DurotarTree08.obj',
          'BarrensTree07.obj','BarrensTree08.obj',
          'ArathiTree_dead_001.obj','ArathiTree_dead_002.obj','BadlandsTreeStump01.obj',
        ],
        count: 320, scaleMin: 2.4, scaleMax: 3.6, skipRadius: 50, rng,
        centerX: 1500, centerZ: 0, diskRadius: 1600,
      });
      void scatterProps(scene, {
        fileNames: [
          'DurotarRock01.obj','DurotarRock02.obj','DurotarRock07.obj','DurotarCliffRock04.obj',
          'BadlandsRock02.obj','BadlandsRock03.obj','BadlandsRock05.obj',
          'TanarisRock02.obj','TanarisRock03.obj','TanarisRock05.obj',
        ],
        count: 350, scaleMin: 0.8, scaleMax: 2.2, skipRadius: 30, rng,
        centerX: 1500, centerZ: 0, diskRadius: 1900,
      });
      // Снежные зоны (Альтерак/Хинтерхиллс/Зимний Оплот) — Sirus снега
      // имеет ограниченный набор; используем что есть, дополняем
      // IronForge зимними бушами.
      void scatterProps(scene, {
        fileNames: ['WinterSmallTree01.obj','WinterTree03.obj','IronForgePine02.obj','SilverPineTree02.obj'],
        count: 400, scaleMin: 2.8, scaleMax: 4.2, skipRadius: 40, rng,
        centerX: -1300, centerZ: -1200, diskRadius: 800,
      });
      void scatterProps(scene, {
        fileNames: ['SnowyTowerRock01.obj','SnowyTowerRock02.obj','WinterSpringRock06.obj','IronForgeCliff01.obj','IronForgeCliff03.obj'],
        count: 250, scaleMin: 0.9, scaleMax: 2.4, skipRadius: 30, rng,
        centerX: -1300, centerZ: -1200, diskRadius: 1000,
      });
      void scatterProps(scene, {
        fileNames: ['IronForgeSnowyBush02.obj','SilverPineBush01.obj','WinterSmallTree01.obj'],
        count: 300, scaleMin: 1.0, scaleMax: 2.4, skipRadius: 25, rng,
        centerX: -1300, centerZ: -1200, diskRadius: 900,
      });
      // Болота / Сумрачное Болото (запад/юг) — spooky dead trees.
      void scatterProps(scene, {
        fileNames: [
          'DuskwoodSpookyTree01.obj','DuskwoodSpookyTree03.obj','DuskwoodTreeCanopy03.obj',
          'ArathiTree_dead_001.obj','ArathiTree_dead_002.obj','ArathiTree_dead_004.obj',
          'BurntStoneTree01.obj','DarkshoreFallenTree01.obj','SilverPineTree02Fallen.obj',
          'StoneTreeLog02.obj','RedRidgeFallenTree01.obj','RedRidgeFallenTree01Moss.obj',
        ],
        count: 360, scaleMin: 2.4, scaleMax: 3.4, skipRadius: 40, rng,
        centerX: -900, centerZ: -900, diskRadius: 600,
      });
      void scatterProps(scene, {
        fileNames: [
          'ElwynnLillyPad01.obj','ElwynnReeds01.obj','ElwynnSeaWeed01.obj',
          'DuskwoodCatails01.obj','DuskwoodSeaWeed01.obj','DuskwoodGrass01.obj',
          'WetlandGrass01.obj','WetlandGrass02.obj','WetlandGrass03.obj',
        ],
        count: 250, scaleMin: 0.6, scaleMax: 1.4, skipRadius: 15, rng,
        centerX: -900, centerZ: -900, diskRadius: 700,
      });
      // Тропические джунгли (Стрэнглхоллоу/Феральпуть) — пальмы+феррn.
      void scatterProps(scene, {
        fileNames: ['DurotarPalm01.obj','DurotarPalm02.obj','StranglethornTree02.obj','StrangleThornFern05.obj'],
        count: 350, scaleMin: 2.6, scaleMax: 4.0, skipRadius: 40, rng,
        centerX: -900, centerZ: -1300, diskRadius: 600,
      });
      void scatterProps(scene, {
        fileNames: ['DurotarPalm01.obj','DurotarPalm02.obj','StranglethornTree02.obj','StranglethornPlant04.obj','StrangleThornFern05.obj','StrangleThornRoot01.obj'],
        count: 350, scaleMin: 2.4, scaleMax: 3.8, skipRadius: 30, rng,
        centerX: 1050, centerZ: 850, diskRadius: 600,
      });
      // Пустыня (Танарис/Силинтус) — единственный Sirus cactus = CactusApple01.
      void scatterProps(scene, {
        fileNames: ['CactusApple01.obj','BadlandsShrub03.obj','BadlandsShrub03Dead.obj','BadlandsShrub04.obj'],
        count: 280, scaleMin: 1.8, scaleMax: 3.2, skipRadius: 35, rng,
        centerX: 500, centerZ: -1300, diskRadius: 600,
      });
      void scatterProps(scene, {
        fileNames: ['TanarisRock02.obj','TanarisRock03.obj','TanarisRock05.obj','UngoroRock07.obj','UngoroRock10.obj','UngoroRock11.obj','UngoroRock13.obj'],
        count: 200, scaleMin: 0.9, scaleMax: 2.5, skipRadius: 30, rng,
        centerX: 500, centerZ: -1300, diskRadius: 800,
      });
      // Осенний лес (Хинтерхиллс/Холмы Равнины) — Duskwood + Dustwallow.
      void scatterProps(scene, {
        fileNames: [
          'DuskwoodTree07.obj','DuskwoodTreeCanopy03.obj','DuskwoodSpookyTree01.obj',
          'DustwallowTree02.obj','DustwallowTree04.obj','DustwallowTree07.obj',
          'WetlandTree03.obj','RedridgeTreeCanopy02.obj','RedridgeTreeCanopy03.obj','RedridgeTreeCanopy04.obj',
        ],
        count: 420, scaleMin: 2.8, scaleMax: 4.0, skipRadius: 40, rng,
        centerX: -1250, centerZ: 850, diskRadius: 600,
      });
      // Пепельная Долина — Калимдор лес.
      void scatterProps(scene, {
        fileNames: ['ElwynnPine02.obj','SilverPineTree02.obj','SilverPineTree04.obj','IronForgePine02.obj'],
        count: 400, scaleMin: 2.8, scaleMax: 4.0, skipRadius: 40, rng,
        centerX: 1050, centerZ: 350, diskRadius: 600,
      });
      // Чумные Земли — мёртвый лес + plagueland.
      void scatterProps(scene, {
        fileNames: [
          'ArathiTree_dead_001.obj','ArathiTree_dead_002.obj','ArathiTree_dead_004.obj',
          'BurntStoneTree01.obj','BurntStoneTreeStump02.obj','BurntStoneTreeStump03.obj',
          'BadlandsTreeStump01.obj','DarkshoreFallenTree01.obj',
          'PlaguelandTree02.obj','PlaguelandTree03.obj','PlaguelandStump04.obj','PlaguelandPuffs01.obj',
        ],
        count: 380, scaleMin: 2.2, scaleMax: 3.4, skipRadius: 40, rng,
        centerX: -1300, centerZ: 1300, diskRadius: 700,
      });
      // Цветочные луга — Меднорощь / Мулхорн.
      void scatterProps(scene, {
        fileNames: ['ElwynnDetailFlowers01.obj','ElwynnFlower02.obj','Windblossom01.obj','ArathiPlant05.obj','AshenvalePlantStardust.obj'],
        count: 600, scaleMin: 0.5, scaleMax: 1.0, skipRadius: 8, rng,
        centerX: -1500, centerZ: 0, diskRadius: 1100,
      });
      void scatterProps(scene, {
        fileNames: ['ElwynnGrass1.obj','ElwynnDetailGrass03.obj','DuskwoodGrass01.obj'],
        count: 800, scaleMin: 0.5, scaleMax: 1.0, skipRadius: 6, rng,
        centerX: -1500, centerZ: 0, diskRadius: 1300,
      });
      void scatterProps(scene, {
        fileNames: ['ElwynnGrass1.obj','ElwynnDetailGrass03.obj','WetlandGrass01.obj','WetlandGrass02.obj'],
        count: 600, scaleMin: 0.5, scaleMax: 1.0, skipRadius: 6, rng,
        centerX: 1500, centerZ: 0, diskRadius: 1300,
      });
      // R232q5h3: плотный слой травы и цветов вокруг faction spawn.
      void scatterProps(scene, {
        fileNames: ['ElwynnDetailFlowers01.obj','ElwynnFlower02.obj','Windblossom01.obj','ArathiPlant05.obj'],
        count: 800, scaleMin: 0.6, scaleMax: 1.2, skipRadius: 2, rng,
        centerX: -1500, centerZ: 0, diskRadius: 200,
      });
      void scatterProps(scene, {
        fileNames: ['ElwynnGrass1.obj','ElwynnDetailGrass03.obj'],
        count: 1200, scaleMin: 0.6, scaleMax: 1.1, skipRadius: 2, rng,
        centerX: -1500, centerZ: 0, diskRadius: 220,
      });
      void scatterProps(scene, {
        fileNames: ['ElwynnBush04.obj','ElwynnBush09.obj','DarkshoreBush01.obj','DarkshoreBush05.obj'],
        count: 220, scaleMin: 0.7, scaleMax: 1.3, skipRadius: 6, rng,
        centerX: -1500, centerZ: 0, diskRadius: 200,
      });
      void scatterProps(scene, {
        fileNames: ['ArathiFlatRock01.obj','ArathiRock04.obj','ArathiRock13.obj','FlatRock01.obj','MullgoreRock02.obj'],
        count: 80, scaleMin: 0.5, scaleMax: 1.4, skipRadius: 8, rng,
        centerX: -1500, centerZ: 0, diskRadius: 220,
      });
      // Same dense ring у horde spawn — Durotar/Barrens flora.
      void scatterProps(scene, {
        fileNames: ['BarrensBush01.obj','BadlandsShrub03.obj','BadlandsShrub04.obj','CactusApple01.obj','ArathiPlant05.obj','AshenvalePlantStardust.obj'],
        count: 700, scaleMin: 0.6, scaleMax: 1.2, skipRadius: 2, rng,
        centerX: 1500, centerZ: 0, diskRadius: 200,
      });
      void scatterProps(scene, {
        fileNames: ['ElwynnGrass1.obj','ElwynnDetailGrass03.obj','WetlandGrass01.obj'],
        count: 1000, scaleMin: 0.5, scaleMax: 1.0, skipRadius: 2, rng,
        centerX: 1500, centerZ: 0, diskRadius: 220,
      });
      void scatterProps(scene, {
        fileNames: ['BadlandsShrub03.obj','BadlandsShrub04.obj','BarrensBush01.obj','DustwallowBush01.obj','DustwallowBush02.obj'],
        count: 200, scaleMin: 0.6, scaleMax: 1.2, skipRadius: 6, rng,
        centerX: 1500, centerZ: 0, diskRadius: 200,
      });
      void scatterProps(scene, {
        fileNames: ['DurotarRock01.obj','DurotarRock02.obj','DurotarRock07.obj','BadlandsRock02.obj','BadlandsRock03.obj'],
        count: 80, scaleMin: 0.5, scaleMax: 1.4, skipRadius: 8, rng,
        centerX: 1500, centerZ: 0, diskRadius: 220,
      });
      // Tree-stumps как accent props — Sirus extracted.
      void scatterProps(scene, {
        fileNames: ['BurntStoneTreeStump02.obj','BurntStoneTreeStump03.obj','BadlandsTreeStump01.obj','IronStump04.obj','PlaguelandStump04.obj','WetlandsTreeStump02.obj','WetlandsTreeStump03.obj'],
        count: 60, scaleMin: 0.9, scaleMax: 1.4, skipRadius: 8, rng,
        centerX: -1500, centerZ: 0, diskRadius: 240,
      });
      void scatterProps(scene, {
        fileNames: ['BurntStoneTreeStump02.obj','BurntStoneTreeStump03.obj','BadlandsTreeStump01.obj','IronStump04.obj'],
        count: 50, scaleMin: 0.9, scaleMax: 1.4, skipRadius: 8, rng,
        centerX: 1500, centerZ: 0, diskRadius: 240,
      });
      // Тыквенные грядки у Меднорощи (WotLK 3.3.5a Westfall vibe — Sirus pumpkins).
      void scatterProps(scene, {
        fileNames: ['Pumpkin01.obj','PumpkinPatch01.obj'],
        count: 350, scaleMin: 0.8, scaleMax: 1.4, skipRadius: 4, rng,
        centerX: -1300, centerZ: 250, diskRadius: 200,
      });
      // R232q5i: Polyhaven CC0 scatter удалён по требованию юзера
      // («только Sirus»). Атмосфера набирается за счёт расширенного
      // mix Sirus rock/cliff/stump props выше.
    }

    // ── Camera (WoW-style, custom inputs) ──
    // R232q86zc: spawn radius 18→6 — на радиусе 18m персонаж занимал
    // ~5% экрана (юзер: «крошечная фигурка ползает»). 6m даёт читаемый
    // 3rd-person view характерный для WoW.
    // R232q86ze: camera FRONT of player (alpha=+π/2 instead of -π/2) so user
    // sees the body's face/chest on first paint instead of just the back.
    // After WASD movement the camera follows player rotation natively.
    const cam = new ArcRotateCamera('follow-cam', Math.PI / 2, Math.PI / 2.5, 6, selfRigBox.rig.root.position, scene);
    cam.lowerRadiusLimit = 2.5;
    cam.upperRadiusLimit = 35;
    // R232q86zbw: cap beta at horizon. Going past π/2 puts camera
    // BELOW the player (geometry says so for ArcRotateCamera) → user
    // sees underground / dirt. To "look up at sky", we instead need to
    // KEEP camera above target and TILT IT UP. ArcRotateCamera doesn't
    // support that natively without inverting axes — so for now we
    // restrict beta to [0.18 .. π/2 - 0.05] and let the player see sky
    // by keeping camera level + raising fov briefly via Z key (TODO).
    // Old π·0.92 was the cause of «камера под землёй» from user 2026-05-09.
    cam.lowerBetaLimit = 0.18;
    cam.upperBetaLimit = Math.PI / 2 - 0.05;
    cam.fov = 0.9;
    // R232q86zD: camera collision back ON now that ADT terrain meshes
    // explicitly enable mesh.checkCollisions + isPickable
    // (AdtTerrain.ts R232q86zD). Combined with the per-frame floor-
    // rescue raycast in the render loop (R232q86zD) this prevents the
    // camera from punching through terrain backfaces.
    cam.checkCollisions = true;
    cam.collisionRadius = new Vector3(0.6, 0.6, 0.6);
    // Enable scene-wide collision system for the camera to actually
    // bounce off mesh.checkCollisions=true geometry.
    scene.collisionsEnabled = true;
    scene.activeCamera = cam;
    // We do NOT attachControl — Babylon's default LMB-rotates conflicts with
    // click-to-target. Custom mouse handlers below give a WoW-style RMB-rotate.

    const canvasEl = engine.getRenderingCanvas() as HTMLCanvasElement;
    // Suppress context menu globally on the canvas so RMB-drag works.
    const onContextMenu = (e: Event) => e.preventDefault();
    canvasEl.addEventListener('contextmenu', onContextMenu);

    // Pointer state — drives WoW-style camera. Pointer events come through
    // Babylon's scene observable so they reliably fire on the canvas
    // regardless of HUD pointer-events settings.
    let lastPx = 0;
    let lastPy = 0;
    let downPx = 0;
    let downPy = 0;
    let lmbDown = false;
    let rmbDown = false;
    let lmbCanClick = false;

    const pointerObs = scene.onPointerObservable.add((info) => {
      const evt = info.event as PointerEvent;
      if (info.type === PointerEventTypes.POINTERDOWN) {
        lastPx = evt.clientX;
        lastPy = evt.clientY;
        downPx = evt.clientX;
        downPy = evt.clientY;
        if (evt.button === 0) { lmbDown = true; lmbHeldRef.current = true; lmbCanClick = true; }
        if (evt.button === 2) { rmbDown = true; rmbHeldRef.current = true; canvasEl.style.cursor = 'grabbing'; }
      } else if (info.type === PointerEventTypes.POINTERUP) {
        if (evt.button === 0) {
          lmbDown = false; lmbHeldRef.current = false;
          if (lmbCanClick) {
            const drift = Math.hypot(evt.clientX - downPx, evt.clientY - downPy);
            const liveSelf = useNet.getState().selfId ? useNet.getState().players[useNet.getState().selfId!] : null;
            const amDead = !!(liveSelf as { dead?: boolean } | null)?.dead;
            if (drift < 4 && !amDead) {
              const directMeta = info.pickInfo?.hit ? info.pickInfo.pickedMesh?.metadata : null;
              // R232q5az M05: even direct mesh-picks must sanity-check
              // world distance — a quest-giver mesh 1.3km away should not
              // open its dialog when clicked through fog. Mobs handle
              // their own range gating below; only gate NPC interactions.
              let meta: Record<string, unknown> | null = (directMeta ?? null) as Record<string, unknown> | null;
              if (meta) {
                const isNpc = meta.questGiverId || meta.trainerId || meta.professionTrainerId
                  || meta.flightMasterId || meta.vendorId || meta.innkeeperId
                  || meta.stableMasterId || meta.spiritHealerId || meta.quartermasterId
                  || meta.dungeonPortalId;
                if (isNpc) {
                  const ap = info.pickInfo?.pickedMesh?.getAbsolutePosition?.();
                  const sp = selfRigBox.rig?.root?.position;
                  if (ap && sp) {
                    const dWorld = Math.hypot(ap.x - sp.x, ap.z - sp.z);
                    if (dWorld > 40) meta = null; // ignore far NPC picks
                  }
                }
              }
              // R232: SAFETY-NET таргет-снап. Если pickRay не попал в моба
              // (моб движется/спайкает или очень мелкий), ищем ближайшего
              // моба в screen-space в радиусе 60px от клика. Это убирает
              // главную жалобу юзера: "жму в него — не выделяется".
              const hasTarget = !!(meta && (meta.mobId || meta.playerId || meta.questGiverId || meta.trainerId || meta.resourceNodeId || meta.flightMasterId || meta.vendorId || meta.innkeeperId || meta.stableMasterId || meta.spiritHealerId || meta.quartermasterId || meta.professionTrainerId || meta.dungeonPortalId));
              if (!hasTarget) {
                const cam = scene.activeCamera;
                if (cam) {
                  const canvasRect = canvasEl.getBoundingClientRect();
                  const cx = evt.clientX - canvasRect.left;
                  const cy = evt.clientY - canvasRect.top;
                  let bestDist = 60; // px
                  let bestId: string | null = null;
                  // Iterate live mobs.
                  const mobsState = useNet.getState().mobs;
                  for (const mid of Object.keys(mobsState)) {
                    const mb = mobsState[mid];
                    if (!mb || mb.dead) continue;
                    const entry = mobMeshes.get(mid);
                    const vx = entry ? entry.node.position.x : mb.pos.x;
                    const vz = entry ? entry.node.position.z : mb.pos.z;
                    const v3 = new Vector3(vx, 1.2, vz);
                    const proj = Vector3.Project(
                      v3,
                      Matrix.Identity(),
                      scene.getTransformMatrix(),
                      cam.viewport.toGlobal(canvasEl.clientWidth, canvasEl.clientHeight),
                    );
                    if (proj.z < 0 || proj.z > 1) continue;
                    const d = Math.hypot(proj.x - cx, proj.y - cy);
                    if (d < bestDist) { bestDist = d; bestId = mid; }
                  }
                  if (bestId) meta = { mobId: bestId };
                  // R232p: Также ищем nearest quest giver / NPC в screen-space.
                  // Юзер: "клик на NPC не открывает диалог". Если pickRay
                  // не попал в маленькую модель NPC — снап к ближайшему.
                  if (!bestId) {
                    let bestNpcDist = 80; // px
                    let bestNpcMeta: Record<string, unknown> | null = null;
                    // R232q5az M05: also reject NPCs >40m away in world space
                    // — fixes the "click on empty canvas opens distant NPC
                    // dialog" bug from the playtest. The previous handler
                    // accepted any NPC whose screen-projection landed within
                    // 80px of the click, even if the NPC was 1.3km away.
                    const NPC_WORLD_MAX = 40; // metres
                    const selfPos = selfRigBox.rig?.root?.position;
                    for (const m of scene.meshes) {
                      const md = m.metadata;
                      if (!md) continue;
                      if (!md.questGiverId && !md.trainerId && !md.professionTrainerId
                          && !md.flightMasterId && !md.vendorId && !md.innkeeperId
                          && !md.stableMasterId && !md.spiritHealerId && !md.quartermasterId
                          && !md.dungeonPortalId) continue;
                      if (!m.isEnabled?.()) continue;
                      const ap = m.getAbsolutePosition?.();
                      if (!ap) continue;
                      // Skip NPCs whose world distance exceeds the click cap.
                      if (selfPos) {
                        const dWorld = Math.hypot(ap.x - selfPos.x, ap.z - selfPos.z);
                        if (dWorld > NPC_WORLD_MAX) continue;
                      }
                      const v3 = new Vector3(ap.x, ap.y + 1.0, ap.z);
                      const proj = Vector3.Project(
                        v3,
                        Matrix.Identity(),
                        scene.getTransformMatrix(),
                        cam.viewport.toGlobal(canvasEl.clientWidth, canvasEl.clientHeight),
                      );
                      if (proj.z < 0 || proj.z > 1) continue;
                      const d = Math.hypot(proj.x - cx, proj.y - cy);
                      if (d < bestNpcDist) { bestNpcDist = d; bestNpcMeta = md; }
                    }
                    if (bestNpcMeta) meta = bestNpcMeta;
                  }
                }
              }
              const id = meta?.mobId as string | undefined;
              const npcId = meta?.questGiverId as string | undefined;
              // R226: клик по другому игроку → target frame показывается мгновенно.
              if (meta?.playerId && meta.playerId !== useNet.getState().selfId) {
                setTargetPlayerId(meta.playerId as string);
                setTargetMobId(null);
                setTargetNpcId(null);
                playSfx('click');
                return;
              }
              if (id) {
                const tm = useNet.getState().mobs[id];
                // R230: клик по ТРУПУ моба → ВСЕГДА отправляем server-side
                // loot/skin запрос. Server валидирует дистанцию (3м для loot,
                // 4м для skinning). Раньше client-side dT использовал tm.pos
                // (server-lagged) и отклонял клик ещё до отправки.
                if (tm && tm.dead) {
                  const corpseEntry = mobMeshes.get(id);
                  const visX = corpseEntry ? corpseEntry.node.position.x : tm.pos.x;
                  const visZ = corpseEntry ? corpseEntry.node.position.z : tm.pos.z;
                  const dT = Math.hypot(visX - selfRigBox.rig.root.position.x, visZ - selfRigBox.rig.root.position.z);
                  // С запасом +2м чтобы visual lag не блокировал клик —
                  // server всё равно перепроверит.
                  if (dT <= 7) {
                    const room = getRoom();
                    if (room) {
                      room.send('loot', { mobId: id });
                      playSfx('click');
                    }
                    // R207: Skinning — снять шкуру если skinnable mob.
                    window.dispatchEvent(new CustomEvent('aiwari-try-skin', {
                      detail: { mobId: id, kind: tm.kind, level: tm.level ?? 1 },
                    }));
                  } else {
                    // Out of range — показать тост.
                    window.dispatchEvent(new CustomEvent('aiwari-toast', {
                      detail: { kind: 'warn', text: 'Слишком далеко — подойди ближе.' },
                    }));
                  }
                  return;
                }
                // R223 FIX: target frame ОБЯЗАТЕЛЬНО показывается при клике (вне range check).
                setTargetMobId(id);
                setTargetPlayerId(null);
                setTargetNpcId(null);
                // R223 FIX: client-side range check использует ВИЗУАЛЬНУЮ позицию моба
                // (lerped entry.node.position), а не server tm.pos (lagged).
                // А лучше — всегда отправлять attack: server validates сам.
                const entry = mobMeshes.get(id);
                const visMobX = entry ? entry.node.position.x : (tm?.pos.x ?? 0);
                const visMobZ = entry ? entry.node.position.z : (tm?.pos.z ?? 0);
                const cls = draft.classId ?? 'warrior';
                // R223: WotLK 3.3.5a reach — match server reachForClass (5y + 3y hitbox).
                // R232o: matches server reachForClass (bump melee +2m лаг компенсация).
                const reach =
                  (cls === 'ranger') ? 25
                  : (cls === 'mage' || cls === 'cleric') ? 28
                  : (cls === 'shaman' || cls === 'warlock' || cls === 'druid') ? 27
                  : cls === 'rogue' ? 7
                  : 8;
                const dT = Math.hypot(visMobX - selfRigBox.rig.root.position.x, visMobZ - selfRigBox.rig.root.position.z);
                if (tm && dT <= reach + 1.5) {
                  // С небольшим запасом — server-side validation окончательная.
                  sendAttack(id);
                  selfRigBox.rig.triggerAttack();
                  playSfx(cls === 'ranger' ? 'arrow-loose' : 'swing');
                } else if (tm) {
                  // Out of range — но target всё равно set'нут чтобы frame появился.
                  // Auto-attack движок будет долбить когда подойдёт.
                }
              } else if (npcId) {
                // R232q23: route NPC clicks to the server-driven dialogue
                // tree (ServerNpcDialog) instead of the legacy NpcDialog.
                // ServerNpcDialog falls back to gossip menu if the NPC has
                // no authored tree, so legacy NPCs still work.
                window.dispatchEvent(new CustomEvent('aiwari-server-npc-open', { detail: { npcId } }));
                playSfx('click');
              } else if (meta?.trainerId) {
                // R152: trainer click — открывает Spellbook (tab «Тренировка» в TODO).
                window.dispatchEvent(new CustomEvent('aiwari-open-spellbook', { detail: { trainerId: meta.trainerId, classId: meta.classId } }));
                playSfx('click');
              } else if (meta?.resourceNodeId) {
                // R174: harvest resource node — отправит ивент в Inventory.
                window.dispatchEvent(new CustomEvent('aiwari-harvest-node', {
                  detail: {
                    nodeId: meta.resourceNodeId,
                    kind: meta.kind,
                    yields: meta.yields,
                    skillRequired: meta.skillRequired,
                    mesh: info.pickInfo?.pickedMesh?.name ?? '',
                  },
                }));
                playSfx(meta.kind === 'mining' ? 'hit' : 'click');
              } else if (meta?.gatherNodeId) {
                // R232q5aP-spawn: server-authoritative gather node click.
                // Server validates skill+proximity; we just send the opcode
                // and play a feedback sound. UI toast comes from
                // gather-node-result handler in GatherNodeFeedback.tsx.
                const ap = info.pickInfo?.pickedMesh?.getAbsolutePosition?.();
                const sp = selfRigBox.rig?.root?.position;
                if (ap && sp) {
                  const dWorld = Math.hypot(ap.x - sp.x, ap.z - sp.z);
                  if (dWorld > 8) {
                    window.dispatchEvent(new CustomEvent('aiwari-toast', {
                      detail: { kind: 'warn', text: 'Слишком далеко — подойди ближе.' },
                    }));
                  } else {
                    void import('../game/realmClient.js').then(({ sendGatherNode }) => {
                      sendGatherNode(meta.gatherNodeId as string);
                    });
                    playSfx(meta.gatherNodeProf === 'mining' ? 'hit' : 'click');
                  }
                }
              } else if (meta?.professionTrainerId) {
                // R174: profession trainer dialog.
                window.dispatchEvent(new CustomEvent('aiwari-open-profession-trainer', {
                  detail: { trainerId: meta.professionTrainerId, profession: meta.profession },
                }));
                playSfx('click');
              } else if (meta?.flightMasterId) {
                // R183: flight master dialog.
                window.dispatchEvent(new CustomEvent('aiwari-open-flight-master', {
                  detail: { originId: meta.flightMasterId },
                }));
                playSfx('click');
              } else if (meta?.dungeonPortalId) {
                // R232q5o: dungeon portal click → open DungeonFinder.
                window.dispatchEvent(new CustomEvent('aiwari-open-dungeon-finder', {
                  detail: { dungeonId: meta.dungeonPortalId },
                }));
                playSfx('click');
              } else if (meta?.vendorId) {
                // R185: general goods vendor — open shop.
                window.dispatchEvent(new CustomEvent('aiwari-open-vendor', {
                  detail: { vendorId: meta.vendorId },
                }));
                playSfx('click');
              } else if (meta?.innkeeperId) {
                // R186: innkeeper — bind/return dialog.
                window.dispatchEvent(new CustomEvent('aiwari-open-innkeeper', {
                  detail: { innId: meta.innkeeperId, innName: meta.innName, pos: meta.innPos, faction: meta.innFaction },
                }));
                playSfx('click');
              } else if (meta?.stableMasterId) {
                // R187: stable master — pet management.
                window.dispatchEvent(new CustomEvent('aiwari-open-stable-master', {
                  detail: { id: meta.stableMasterId },
                }));
                playSfx('click');
              } else if (meta?.spiritHealerId) {
                // R187: spirit healer — resurrect dialog.
                window.dispatchEvent(new CustomEvent('aiwari-open-spirit-healer', {
                  detail: { id: meta.spiritHealerId },
                }));
                playSfx('cast');
              } else if (meta?.quartermasterId) {
                // R195: PvP rewards.
                window.dispatchEvent(new CustomEvent('aiwari-open-quartermaster', {
                  detail: { faction: meta.faction, city: meta.city },
                }));
                playSfx('click');
              }
            }
          }
          lmbCanClick = false;
        }
        if (evt.button === 2) {
          rmbDown = false; rmbHeldRef.current = false; canvasEl.style.cursor = '';
        }
      } else if (info.type === PointerEventTypes.POINTERMOVE) {
        const dx = evt.clientX - lastPx;
        const dy = evt.clientY - lastPy;
        lastPx = evt.clientX;
        lastPy = evt.clientY;
        // Re-sync from e.buttons in case a button was released outside the canvas.
        const lmbBit = (evt.buttons & 1) !== 0;
        const rmbBit = (evt.buttons & 2) !== 0;
        if (!lmbBit && lmbDown) { lmbDown = false; lmbHeldRef.current = false; }
        if (!rmbBit && rmbDown) { rmbDown = false; rmbHeldRef.current = false; canvasEl.style.cursor = ''; }
        if (Math.abs(dx) + Math.abs(dy) > 3) lmbCanClick = false;
        if (rmbDown || lmbDown) {
          cam.alpha -= dx * 0.0055;
          const lo = cam.lowerBetaLimit ?? 0.18;
          const hi = cam.upperBetaLimit ?? Math.PI / 2.05;
          const wantBeta = cam.beta - dy * 0.0055;
          // R232q86zcp: «look-up at sky» without putting camera underground.
          // Original behaviour clamped beta at horizon (π/2 - 0.05) — going
          // further put cam BELOW target = inside terrain. Now: when at
          // upper limit AND user keeps dragging down (wantBeta > hi), we
          // raise the camera target Y instead, so cam climbs higher above
          // player, naturally tilting view upward to expose the sky.
          // Drag-up resets target Y back to player root.
          const selfRoot = selfRigBox.rig?.root;
          if (wantBeta > hi && selfRoot) {
            cam.beta = hi;
            // Lift target Y up to ~+30m max above player; each drag-px adds ~0.5m
            const overshoot = (wantBeta - hi) * 50; // metres delta this frame
            const baseY = selfRoot.position.y + 1.0;
            const maxLift = 30;
            const newTargetY = Math.min(baseY + maxLift, cam.target.y + overshoot);
            cam.target.y = newTargetY;
          } else {
            cam.beta = Math.max(lo, Math.min(hi, wantBeta));
            // Drift target Y back to player when not looking up
            if (selfRoot) {
              const baseY = selfRoot.position.y + 1.0;
              if (cam.target.y > baseY + 0.1) {
                // ease back toward player
                cam.target.y += (baseY - cam.target.y) * 0.20;
              }
            }
          }
        } else {
          // R221: hover cursor — WotLK 3.3.5a-style icons (sword/loot-bag/skin-knife/leaf/pick/rod/scroll).
          const hovMeta = info.pickInfo?.hit ? info.pickInfo.pickedMesh?.metadata : null;
          // R232i: dispatch tooltip event с детALями. HoverTooltip subscribes.
          let tipData: { kind: 'mob' | 'player' | 'npc'; ruName?: string; level?: number; hp?: number; hpMax?: number; classId?: string; side?: string; label?: string; desc?: string } | null = null;
          const RU_KIND: Record<string, string> = {
            wolf: 'Волк', fox: 'Лис', deer: 'Олень', stag: 'Олень',
            bull: 'Бык', cow: 'Корова', boar: 'Бык', spider: 'Кактоид',
            bandit: 'Разбойник', tribal: 'Дикарь', goblin: 'Гоблин',
            orc: 'Орк', orc_skull: 'Орк-Череп', yeti: 'Йети', golem: 'Голем',
            demon: 'Демон', wraith: 'Призрак', dragonkin: 'Драконид',
            'world-boss': 'Мировой Босс',
          };
          const KIND_DESC: Record<string, string> = {
            wolf: 'Хищный лесной зверь, охотится в стаях.',
            fox: 'Шустрый охотник на грызунов.',
            deer: 'Травоядное лесное животное.',
            stag: 'Гордый рогач со степей.',
            bull: 'Массивное рогатое травоядное.',
            cow: 'Спокойная коровка с пастбища.',
            boar: 'Дикий кабан, опасен в ярости.',
            spider: 'Ядовитый паукообразный обитатель пустошей.',
            bandit: 'Дорожный разбойник вне закона.',
            tribal: 'Дикий воин из забытых племён.',
            goblin: 'Хитрый зеленокожий торгаш-механик.',
            orc: 'Воин из степей, силён и выносливый.',
            orc_skull: 'Элитный орк-страж в боевой раскраске.',
            yeti: 'Снежный гигант высокогорья.',
            golem: 'Каменный страж древних руин.',
            demon: 'Тварь Бездны, питается страхом.',
            wraith: 'Беспокойный дух, ищущий свою месть.',
            dragonkin: 'Чешуйчатый потомок древних драконов.',
            'world-boss': '★ Легендарный противник для рейдовой группы.',
          };
          if (hovMeta?.mobId) {
            const hovMob = useNet.getState().mobs[hovMeta.mobId];
            if (hovMob?.dead) {
              canvasEl.style.cursor = CURSOR.loot;
            } else {
              canvasEl.style.cursor = CURSOR.attack;
            }
            if (hovMob) {
              tipData = {
                kind: 'mob',
                ruName: (hovMob as { displayName?: string }).displayName || RU_KIND[hovMob.kind] || hovMob.kind,
                level: hovMob.level,
                hp: hovMob.hp,
                hpMax: hovMob.hpMax,
                desc: KIND_DESC[hovMob.kind],
              };
            }
          } else if (hovMeta?.playerId) {
            const hovP = useNet.getState().players[hovMeta.playerId];
            canvasEl.style.cursor = 'pointer';
            if (hovP) {
              const side = (hovP as { side?: string }).side;
              const SIDE_DESC: Record<string, string> = {
                tidal: 'Союзник Серебряной Зари. Прибой моря и сталь.',
                rooted: 'Нейтральный странник. Корни леса и камня.',
                anchorless: 'Воин Бурного Племени. Кочевник и буря степи.',
              };
              tipData = {
                kind: 'player',
                ruName: hovP.name,
                level: hovP.level,
                classId: (hovP as { classId?: string }).classId,
                side,
                desc: side ? SIDE_DESC[side] : undefined,
              };
            }
          } else if (hovMeta?.questGiverId) {
            canvasEl.style.cursor = CURSOR.talk;
            tipData = {
              kind: 'npc',
              label: (hovMeta.leaderName as string) ?? 'Квестодатель',
              desc: 'Поговори, чтобы взять или сдать задание.',
            };
          } else if (hovMeta?.resourceNodeId) {
            // Type подсказывает какой курсор — kind в metadata.
            const rkind = hovMeta.kind as string;
            if (rkind === 'mining') canvasEl.style.cursor = CURSOR.mining;
            else if (rkind === 'fishing') canvasEl.style.cursor = CURSOR.fishing;
            else canvasEl.style.cursor = CURSOR.herb;
            const yieldsRu = hovMeta.yieldsRu as string | undefined;
            const skill = hovMeta.skillRequired as number | undefined;
            const icon = rkind === 'mining' ? '⛏' : rkind === 'fishing' ? '🎣' : '🌿';
            const action = rkind === 'mining' ? 'Добыча' : rkind === 'fishing' ? 'Рыбалка' : 'Сбор трав';
            const skillSuffix = skill ? ` · навык ${skill}+` : '';
            tipData = {
              kind: 'npc',
              label: `${icon} ${action}${skillSuffix}`,
              desc: yieldsRu ? `Даёт: ${yieldsRu}` : undefined,
            };
          } else if (hovMeta?.dungeonPortalId) {
            canvasEl.style.cursor = CURSOR.talk;
            const ru = (hovMeta.ru as string) ?? 'Подземелье';
            const lvl = hovMeta.level as { min: number; max: number } | undefined;
            const lvlSuffix = lvl ? ` (Ур. ${lvl.min}-${lvl.max})` : '';
            const desc = hovMeta.blurb as string | undefined;
            tipData = { kind: 'npc', label: `🚪 ${ru}${lvlSuffix}`, desc };
          } else if (hovMeta?.resourceNodeId) {
            // R232q5do: resource nodes (травы/руда/рыба) hover-tooltip с
            // подсказкой требуемого скилла + предложением пойти к учителю
            // профессий если профа не изучена.
            canvasEl.style.cursor = CURSOR.talk;
            const kind = (hovMeta.kind as string) ?? 'unknown';
            const skillRequired = (hovMeta.skillRequired as number) ?? 1;
            const yields = (hovMeta.yields as string) ?? '?';
            const kindLabel: Record<string, string> = {
              herbalism: '🌿 Трава',
              mining: '⛏️ Рудная жила',
              fishing: '🎣 Косяк рыбы',
            };
            const profLabel: Record<string, string> = {
              herbalism: 'Травничество',
              mining: 'Горное дело',
              fishing: 'Рыболовство',
            };
            // Check профa изучена. Stored in localStorage 'aiwari-professions'.
            const prof = (() => {
              try {
                const raw = localStorage.getItem('aiwari-professions');
                return raw ? (JSON.parse(raw) as Record<string, number>) : {};
              } catch { return {}; }
            })();
            const ourSkill = prof[kind] ?? 0;
            const learned = ourSkill > 0;
            const enoughSkill = ourSkill >= skillRequired;
            let desc: string;
            if (!learned) {
              desc = `Сначала изучи ${profLabel[kind] ?? 'профессию'} у тренера в столице.`;
            } else if (!enoughSkill) {
              desc = `Нужен скилл ${skillRequired} (у тебя ${ourSkill}). Тренируй профессию.`;
            } else {
              desc = `Добыча: ${yields}. Скилл ${ourSkill}/300.`;
            }
            tipData = { kind: 'npc', label: kindLabel[kind] ?? '📦 Ресурс', desc };
          } else if (hovMeta?.npcId) {
            // R232q5v: dynamic NPC hitbox from NpcMeshes.ts
            canvasEl.style.cursor = CURSOR.talk;
            const hovNpc = useNet.getState().npcs[hovMeta.npcId as string];
            if (hovNpc) {
              const roleDesc: Record<string, string> = {
                questGiver: 'Поговори чтобы взять или сдать задание.',
                vendor: 'Торгует припасами, расходниками, экипировкой.',
                trainer: 'Учит новым способностям.',
                innkeeper: 'Привяжи Камень возвращения здесь.',
                flightmaster: 'Мгновенный полёт между точками.',
                banker: 'Доступ к банковскому хранилищу.',
                repair: 'Ремонт экипировки.',
                spiritHealer: 'Воскрешение духа.',
              };
              tipData = {
                kind: 'npc',
                label: hovNpc.displayName,
                desc: hovNpc.subtitle || roleDesc[hovNpc.npcType] || hovNpc.npcType,
              };
            }
          } else if (hovMeta?.professionTrainerId || hovMeta?.trainerId || hovMeta?.flightMasterId || hovMeta?.vendorId || hovMeta?.innkeeperId || hovMeta?.stableMasterId || hovMeta?.spiritHealerId || hovMeta?.quartermasterId) {
            canvasEl.style.cursor = CURSOR.talk;
            const labelMap: Record<string, string> = {
              professionTrainerId: 'Тренер профессий',
              trainerId: 'Тренер класса',
              flightMasterId: 'Грифоний мастер',
              vendorId: 'Торговец',
              innkeeperId: 'Хозяин таверны',
              stableMasterId: 'Конюх',
              spiritHealerId: 'Дух-целитель',
              quartermasterId: 'Интендант',
            };
            const descMap: Record<string, string> = {
              professionTrainerId: 'Изучи профессию: горное дело, травничество, кузнечное дело и др.',
              trainerId: 'Изучи новые способности и таланты своего класса.',
              flightMasterId: 'Мгновенный полёт между открытыми точками за золото.',
              vendorId: 'Купи припасы, расходники, начальную экипировку.',
              innkeeperId: 'Привяжи Камень возвращения к этой таверне.',
              stableMasterId: 'Хранилище до 5 питомцев Охотника. 1 активный + 4 в стойле.',
              spiritHealerId: 'Воскрешение в виде духа за -25% к прочности экипировки.',
              quartermasterId: 'Продаёт награды за PvP за очки чести (по рангу).',
            };
            // R232q5aa: для flight master показываем zone name из fpName.
            if (hovMeta.flightMasterId && hovMeta.fpName) {
              tipData = { kind: 'npc', label: `🪶 ${hovMeta.fpName as string}`, desc: descMap.flightMasterId };
            } else if (hovMeta.trainerId && hovMeta.trainerName) {
              // R232q5ab: trainer hover показывает имя + title.
              const t = hovMeta.trainerTitle ? ` — ${hovMeta.trainerTitle as string}` : '';
              tipData = { kind: 'npc', label: `📚 ${hovMeta.trainerName as string}${t}`, desc: descMap.trainerId };
            } else {
              for (const k of Object.keys(labelMap)) {
                if (hovMeta[k]) { tipData = { kind: 'npc', label: labelMap[k], desc: descMap[k] }; break; }
              }
            }
          } else {
            canvasEl.style.cursor = '';
          }
          // Fire tooltip event.
          if (tipData) {
            window.dispatchEvent(new CustomEvent('aiwari-hover-set', {
              detail: { x: evt.clientX, y: evt.clientY, data: tipData },
            }));
          } else {
            window.dispatchEvent(new CustomEvent('aiwari-hover-clear'));
          }
        }
      } else if (info.type === PointerEventTypes.POINTERWHEEL) {
        const wheelEvt = evt as unknown as WheelEvent;
        const factor = wheelEvt.deltaY > 0 ? 1.12 : 0.88;
        cam.radius = Math.max(cam.lowerRadiusLimit ?? 1, Math.min(cam.upperRadiusLimit ?? 30, cam.radius * factor));
      }
    });

    // Remote rigs.
    const remoteRigs = new Map<string, PlaceholderCharacterRig>();
    // R223: position buffer per mob (entity interpolation Half-Life style).
    // Server broadcasts at 20Hz (50ms intervals); we render mob at "now - 110ms"
    // by interpolating BETWEEN two known past samples. Eliminates the
    // dt-proportional lerp jitter at server-tick boundaries.
    interface PosSample { ts: number; x: number; z: number; heading: number; }
    interface MobEntry {
      node: TransformNode;
      glb: GlbMob | null;
      kind: string;
      lastMoving: boolean;
      posBuf: PosSample[];          // up to ~6 recent samples
      lastServerX: number;
      lastServerZ: number;
      lastServerHeading: number;
      farSinceTs?: number;          // R232q5: hysteresis для dispose-on-far
    }
    const mobMeshes = new Map<string, MobEntry>();
    // R232q86zdd: mob terrain Y cache — server sends y=0 for vanilla spawn
    // data, so client must raycast. 32m grid (one chunk-ish).
    const mobYCache = new Map<string, number>();
    void mobYCache; // referenced inside render loop below
    // R232: server BROADCAST_HZ 20→30 (33ms между samples). Interp delay
    // 60→45ms = 1.36 broadcast periods. Smoother чем раньше при той же
    // perceived latency.
    const INTERP_DELAY_MS = 45;
    const POS_BUF_MAX = 4;
    // Per-kind material, kept on the room scope so meshes share where possible.
    const mobMats: Record<string, StandardMaterial> = {};
    const ensureMat = (key: string, hex: string, emissive?: string) => {
      if (mobMats[key]) return mobMats[key];
      const m = new StandardMaterial(`mob-mat-${key}`, scene);
      m.diffuseColor = Color3.FromHexString(hex);
      m.specularColor = new Color3(0.04, 0.04, 0.04);
      if (emissive) m.emissiveColor = Color3.FromHexString(emissive);
      mobMats[key] = m;
      return m;
    };
    const worldBossMat = ensureMat('world-boss', '#9A1A2E', '#7A0E1C');

    // Keyboard.
    const keys: Record<string, boolean> = {};
    const onDown = (e: KeyboardEvent) => { keys[e.key.toLowerCase()] = true; };
    const onUp = (e: KeyboardEvent) => { keys[e.key.toLowerCase()] = false; };
    // R232q5do: при потере фокуса (alt-tab, кликнул вне canvas, нажал Win+L)
    // keyup не приходит → клавиша «залипает» → персонаж сам идёт.
    // Очищаем все movement-keys при window.blur.
    const onBlur = () => {
      for (const k of Object.keys(keys)) keys[k] = false;
    };
    // R232p: clear all keys + mouse holds on:
    //  - Escape (юзер: "Escape не помогает, бежит сам")
    //  - window blur (focus lost — например Alt-Tab. keyup может не fire'нуть)
    //  - document visibility change (tab hidden)
    const clearAllInput = () => {
      for (const k of Object.keys(keys)) keys[k] = false;
      lmbHeldRef.current = false;
      rmbHeldRef.current = false;
    };
    const onEscapeStop = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        clearAllInput();
        // R232q: Escape — также сбрасывает таргет. Авто-атака гейтится
        // на (targetMid != null), так что null таргета достаточно чтобы
        // прекратить swing. Юзер: "Escape должен отменять действие".
        setTargetMobId(null);
        setTargetPlayerId(null);
        setTargetNpcId(null);
      }
    };
    const onVisibilityChange = () => {
      if (document.hidden) clearAllInput();
    };
    window.addEventListener('keydown', onDown);
    window.addEventListener('keydown', onEscapeStop);
    window.addEventListener('keyup', onUp);
    window.addEventListener('blur', onBlur);
    window.addEventListener('blur', clearAllInput);
    document.addEventListener('visibilitychange', onVisibilityChange);

    let yaw = 0;
    let lastSent = 0;
    let lastAutoAttack = 0;
    // Smoothed movement — each frame we lerp current velocity to target.
    let vx = 0, vz = 0;
    // Vertical: jump impulse + gravity.
    let vy = 0;
    // R232q86zA: track whether the player has touched terrain at least once.
    // Until they have, gravity must NOT clamp position.y to `groundedY=0`,
    // because that drops the rig 130-300m under the surface (terrain in
    // Northshire sits at y≈130) → camera sees only terrain backfaces from
    // below and the entire world looks «перевёрнутым» (user-reported
    // 2026-05-10). Only enable gravity-clamping after the first successful
    // sampleGroundY hit so the rig never falls through the floor.
    let hasTouchedGround = false;
    let groundedY = 0;
    let lastGroundSample = 0;
    // R232q86zK: anti-streaming-race safety net — see expanded block below.
    let lastRescueSample = 0;
    let groundFloor = -50;
    // R232q86zN: PEAK-anchor — track the highest confirmed surface Y this
    // session ever saw. groundedY can never drop more than 30 m below the
    // peak. This kills the cascading-low bug where each accepted sample
    // lowered the junk-low threshold for the next, draining groundedY all
    // the way to ground floor over a few seconds.
    let maxEverGroundedY = -Infinity;
    const PEAK_DROP_LIMIT = 30; // max metres rig can «walk down» from peak
    const GRAVITY = 22;
    const JUMP_V0 = 8.2;

    // Movement + remote sync.
    let windT = 0;
    let initialSpawnApplied = false;
    let lastVisitTick = 0;
    let lastYSnap = 0;
    // R232q86zd6 fix #2: per-rig child-mesh cache + prev-state tracking so
    // the stealth visibility update can skip getChildMeshes() every frame
    // (200×40 = 8000 mesh allocs/sec at 30 remote players) and bail out
    // entirely when nothing has changed.
    const _selfRigCachedMeshes = new WeakMap<TransformNode, { meshes: import('@babylonjs/core').AbstractMesh[]; childCount: number }>();
    let _selfStealthPrev = false;
    let _selfStealthLerping = false;
    const _remoteStealthPrev = new Map<string, boolean>();
    const _remoteStealthLerping = new Set<string>();
    function getCachedChildMeshes(
      root: TransformNode,
      cache: WeakMap<TransformNode, { meshes: import('@babylonjs/core').AbstractMesh[]; childCount: number }>,
    ): import('@babylonjs/core').AbstractMesh[] {
      const existing = cache.get(root);
      // Re-fetch when child count changes (rig swap, equipment change, etc).
      // Babylon's `_children` length is a cheap O(1) sentinel.
      const liveCount = (root as unknown as { _children?: unknown[] })._children?.length ?? 0;
      if (existing && existing.childCount === liveCount) return existing.meshes;
      const meshes = root.getChildMeshes(false);
      cache.set(root, { meshes, childCount: liveCount });
      return meshes;
    }
    // R232q86zd6 fix #1: hoisted Ray + Vector3 instances so the per-frame
    // ground-snap and camera-floor raycasts don't allocate (was 4 fresh
    // Vector3 + 2 Ray per frame at 60 fps = ~360 alloc/sec, GC churn).
    // These are mutated in place via `.set()` before each pickWithRay.
    const _snapRayDir = new Vector3(0, -1, 0);
    const _snapRayOrigin = new Vector3(0, 1500, 0);
    const _snapRay = new Ray(_snapRayOrigin, _snapRayDir, 3500);
    const _camRayOrigin = new Vector3(0, 1500, 0);
    const _camRay = new Ray(_camRayOrigin, _snapRayDir, 3500);
    // Throttle ground-snap raycast to ≤30 Hz (was unbounded — fired per
    // frame ≈ 60-240×/s on high-refresh displays, dominating the loop).
    // 33 ms gap = 30 Hz max; visually indistinguishable from 60 Hz at
    // running speeds (≤7 m/s × 0.033 s = 0.23 m drift between samples).
    let _lastSnapAt = 0;
    let _lastCamSnapAt = 0;
    // R232q86zd6 fix #1: dedicated array of "ground-only" meshes so the
    // pickWithRay predicate doesn't have to walk all 10k scene meshes
    // matching by name. Rebuilt opportunistically: any time the scene
    // gains/loses an `adt-*` / `ground-*` / `world-ground` mesh we
    // invalidate (lastBuildAt) and re-collect on next snap.
    const _groundMeshes: import('@babylonjs/core').AbstractMesh[] = [];
    let _groundMeshesRebuiltAt = 0;
    const rebuildGroundMeshes = () => {
      _groundMeshes.length = 0;
      for (const m of scene.meshes) {
        const n = m.name ?? '';
        if (n.startsWith('adt-') || n.startsWith('ground-') || n === 'world-ground') {
          _groundMeshes.push(m);
        }
      }
      _groundMeshesRebuiltAt = performance.now();
    };
    // Rebuild every 2 s — cheap walk over scene.meshes once vs per-frame.
    // This catches ADT streaming load/unload without explicit hooks.
    const pickFromGroundMeshes = (ray: Ray) => {
      const now = performance.now();
      if (now - _groundMeshesRebuiltAt > 2000 || _groundMeshes.length === 0) {
        rebuildGroundMeshes();
      }
      let bestY = -Infinity;
      let bestPoint: { y: number } | null = null;
      for (let i = 0; i < _groundMeshes.length; i++) {
        const mesh = _groundMeshes[i];
        if (!mesh || mesh.isDisposed()) continue;
        const info = mesh.intersects(ray, false);
        if (info?.hit && info.pickedPoint && info.pickedPoint.y > bestY) {
          bestY = info.pickedPoint.y;
          bestPoint = { y: info.pickedPoint.y };
        }
      }
      return bestPoint;
    };
    // R232q86zb5: server-pushed self spawn position (set via the
    // 'aiwari-self-pos' window event from realmClient). Used by the
    // render loop's snap-to-spawn logic — bypasses Colyseus Schema
    // nested-decode fragility.
    let serverSelfPos: { x: number; y: number; z: number } | null = null;
    const onSelfPos = (e: Event) => {
      const d = (e as CustomEvent<{ x: number; y: number; z: number }>).detail;
      if (d && typeof d.x === 'number' && typeof d.z === 'number') {
        serverSelfPos = { x: d.x, y: d.y ?? 0, z: d.z };
      }
    };
    window.addEventListener('aiwari-self-pos', onSelfPos);
    scene.onDisposeObservable.add(() => window.removeEventListener('aiwari-self-pos', onSelfPos));
    const obs = scene.onBeforeRenderObservable.add(() => {
      const dt = Math.min(0.05, engine.getDeltaTime() / 1000);
      // R232q5e3: fire visit-tick event every 500ms with player position so
      // QuestLog 'visit' tracker can mark waypoint-reach objectives.
      const visitNow = performance.now();
      if (visitNow - lastVisitTick > 500) {
        lastVisitTick = visitNow;
        window.dispatchEvent(new CustomEvent('aiwari-quest-visit-tick', {
          detail: { x: selfRigBox.rig.root.position.x, z: selfRigBox.rig.root.position.z },
        }));
      }
      // R131: snap self rig to server's spawn position the first time
      // server state arrives. Without this the rig stays at (0,0,0) —
      // 1500m away from Coppergrove/Sharpridge — so the village/decor
      // around the player look "missing" until the player walks 1.5km.
      //
      // R232q86zb3: добавлен continuous re-sync с pollovorom 100m. После
      // 1:1 coord migration любой existing char получает legacy-pos →
      // server respawn at canonical, но первый client snapshot мог
      // прийти ещё до server-side migration. Re-sync покрывает этот
      // edge case + guard от любого desync (camera teleport, anchor
      // resnap и т.д.). Условие `now > rigSwapDoneAt + 1000` — даём
      // секунду на rig swap чтобы не конфликтовать с oldPos restore.
      {
        // R232q86zb5: snap from `aiwari-self-pos` event (one-shot server
        // message — bypasses unreliable nested PositionSchema decode on
        // client). The event handler installed below stashes the latest
        // server-known pos in `serverSelfPos`; the render loop applies it.
        if (serverSelfPos) {
          if (!initialSpawnApplied) {
            selfRigBox.rig.root.position.x = serverSelfPos.x;
            selfRigBox.rig.root.position.z = serverSelfPos.z;
            // R232q86zl: also snap Y to the server-supplied spawn-Y.
            if (typeof serverSelfPos.y === 'number' && Number.isFinite(serverSelfPos.y)) {
              selfRigBox.rig.root.position.y = serverSelfPos.y;
              // R232q86zC: ALSO seed groundedY + flip hasTouchedGround.
              // Otherwise sampleGroundY may never find an ADT (zone tile
              // not loaded, sampler offline, etc), gravity stays gated
              // forever, and rig hangs at spawn-y in the sky.
              // Seeding groundedY = spawn.y means: gravity is on
              // immediately, but the rig is already AT groundedY so it
              // doesn't fall. As soon as a real terrain sample arrives,
              // groundedY lerps to actual surface and rig follows down.
              groundedY = serverSelfPos.y;
              hasTouchedGround = true;
              groundFloor = serverSelfPos.y - 30;
              maxEverGroundedY = serverSelfPos.y;
              // R232q86zS: tell the React layer the rig is now positioned
              // at the server-supplied spawn — UI can hide the «Загрузка
              // мира» overlay so the user never sees the (0,0) intermediate
              // frame that looks like «персонаж падает в текстуры».
              window.dispatchEvent(new CustomEvent('aiwari-spawn-applied'));
            }
            initialSpawnApplied = true;
            console.log(`[aiwari] snap self to spawn (${serverSelfPos.x.toFixed(0)}, ${serverSelfPos.y?.toFixed?.(0) ?? '?'}, ${serverSelfPos.z.toFixed(0)})`);
          } else {
            const dx = serverSelfPos.x - selfRigBox.rig.root.position.x;
            const dz = serverSelfPos.z - selfRigBox.rig.root.position.z;
            if (Math.hypot(dx, dz) > 100) {
              selfRigBox.rig.root.position.x = serverSelfPos.x;
              selfRigBox.rig.root.position.z = serverSelfPos.z;
              console.log(`[aiwari] re-snap to server (${serverSelfPos.x.toFixed(0)}, ${serverSelfPos.z.toFixed(0)}) — Δ=${Math.hypot(dx, dz).toFixed(0)}m`);
            }
          }
        }
        // R232q86zh3: NO server snap yet (cold server boot / slow onJoin) —
        // freeze the rig hidden instead of letting gravity drop it onto the
        // legacy origin island («спавн не там», empty HP bars). The world
        // resumes the moment the first server position lands.
        if (!initialSpawnApplied) {
          if (selfRigBox.rig.root.isEnabled()) selfRigBox.rig.root.setEnabled(false);
          return;
        }
        if (!selfRigBox.rig.root.isEnabled()) selfRigBox.rig.root.setEnabled(true);
        {
          // R232q86zca: throttle Y-snap to 5 Hz (was every-frame at 240 FPS
          // = 240 raycasts/sec → FPS 1.0 regression). Snapping at 5 Hz
          // keeps player visually pinned to terrain without burning GPU.
          // Once converged we won't drift more than 1m between snaps even
          // at running speed (~7 m/s × 0.2s = 1.4m).
          // R232q86zD: Y-snap UNTHROTTLED — was 5Hz which caused visible
          // bobbing on slopes when player ran faster than 1m per snap.
          // Per-frame raycast at 60fps is ~1ms — cheap. Only player
          // position uses pickWithRay; camera floor rescue uses the same
          // hit result (computed once below).
          let groundHit: { y: number } | null = null;
          // R232q86zd6 fix #1: throttle to ≤30 Hz + reuse pre-allocated
          // Ray/Vector3 + iterate the cached `_groundMeshes` array directly
          // instead of scene.pickWithRay (which walks all 10k+ meshes).
          const _snapNow = performance.now();
          if (_snapNow - _lastSnapAt > 33) {
            _lastSnapAt = _snapNow;
            try {
              _snapRayOrigin.set(
                selfRigBox.rig.root.position.x,
                1500,
                selfRigBox.rig.root.position.z,
              );
              _snapRay.origin = _snapRayOrigin;
              _snapRay.direction = _snapRayDir;
              _snapRay.length = 3500;
              const hit = pickFromGroundMeshes(_snapRay);
              if (hit) {
                groundHit = hit;
                // R232q86zde: anchor rig AT terrain Y, not +1m above. The
                // +1.0 offset caused "плавает над землёй" — player visible
                // 1m above grass. First-touch (line ~2098) already uses
                // hit.y exact, so ongoing snap must match.
                const targetY = hit.y;
                const dy = targetY - selfRigBox.rig.root.position.y;
                if (Math.abs(dy) > 30) {
                  selfRigBox.rig.root.position.y = targetY;
                } else if (Math.abs(dy) > 0.05) {
                  selfRigBox.rig.root.position.y += dy * Math.min(1, dt * 12);
                }
                // R232q86zN: keep gravity-clamp + peak-floor systems in sync
                // with the per-frame Y-snap result. Without this they fight:
                // Y-snap pulls rig DOWN to terrain (80m), gravity-clamp then
                // shoves it back UP to a stale groundedY (100m from spawn),
                // visible as a bouncing fall.
                // R232q86zdc: SOFT groundedY updates. Hard `groundedY = hit.y`
                // caused «floats then catches» bobbing on rolling terrain:
                // gravity clamp at line ~2354 snaps rig UP to groundedY in
                // 1 frame, then the next snap lowers groundedY, and the
                // pattern repeats. Lerp 12/s matches the rig.y lerp above
                // so both follow the surface in lockstep.
                const dGround = hit.y - groundedY;
                if (Math.abs(dGround) > 30) {
                  groundedY = hit.y;
                } else if (Math.abs(dGround) > 0.05) {
                  groundedY += dGround * Math.min(1, dt * 12);
                }
                if (groundedY > maxEverGroundedY) maxEverGroundedY = groundedY;
                if (!hasTouchedGround) {
                  hasTouchedGround = true;
                  vy = 0;
                  groundFloor = groundedY - 30;
                  // First-touch: hard-snap so spawn is exact, not a slow lerp
                  // from y=0 up to terrain (would visibly fall in).
                  groundedY = hit.y;
                  selfRigBox.rig.root.position.y = hit.y;
                }
              }
            } catch { /* raycast safe-fail */ }
          }
          // R232q86zD: camera floor rescue. If the ArcRotateCamera ends
          // up below the terrain at its (x, z) — happens at extreme
          // betas on steep slopes — pull beta upward 1° per frame until
          // the camera surfaces. Prevents the «мир перевёрнут» artifact
          // where the player sees terrain backfaces from underground.
          // R232q86zd6 fix #1: throttled to 30 Hz + reused Ray/Vector3.
          if (_snapNow - _lastCamSnapAt > 33) {
            _lastCamSnapAt = _snapNow;
            try {
              _camRayOrigin.set(cam.position.x, 1500, cam.position.z);
              _camRay.origin = _camRayOrigin;
              _camRay.direction = _snapRayDir;
              _camRay.length = 3500;
              const camHit = pickFromGroundMeshes(_camRay);
              if (camHit && cam.position.y < camHit.y + 0.5) {
                // Camera is under terrain — reduce beta (steeper top-down)
                // until it surfaces. lowerBetaLimit honoured.
                cam.beta = Math.max(cam.lowerBetaLimit ?? 0.05, cam.beta - dt * 1.5);
              }
            } catch { /* raycast safe-fail */ }
          }
          // R232q86zE: publish debug numbers for DebugTerrainHud overlay.
          (globalThis as { __aiwariDebug?: Record<string, unknown> }).__aiwariDebug = {
            selfRigX: selfRigBox.rig.root.position.x,
            selfRigY: selfRigBox.rig.root.position.y,
            selfRigZ: selfRigBox.rig.root.position.z,
            groundedY,
            hasTouchedGround,
            lastGroundHitY: groundHit?.y ?? null,
            camY: cam.position.y,
            camTargetY: (cam.target as { y?: number } | undefined)?.y ?? null,
          };
        }
      }
      // Wind sway — cheap per-cluster rotation oscillation.
      windT += dt;
      for (const s of swayables) {
        s.node.rotation.y = s.baseY + Math.sin(windT * 1.6 + s.phase) * 0.18;
      }

      // Detect held mouse buttons each frame — needed for both-buttons auto-run.
      // We don't get a steady mousemove if the mouse is stationary; so read
      // the latest cached state via document.pointerLockElement is unreliable.
      // Instead, expose lmbHeld/rmbHeld closure vars synced in the move handler.
      // We pull them via these locals updated above. (Held flags aren't in scope
      // here — derive them from the latest e.buttons captured per move.)

      // ── Direction input ──
      let ix = 0, iz = 0;
      // W/S or arrow up/down: forward/back
      if (keys['w'] || keys['ц'] || keys['arrowup']) iz += 1;
      if (keys['s'] || keys['ы'] || keys['arrowdown']) iz -= 1;
      // Q/E always strafe.
      if (keys['q'] || keys['й']) ix -= 1;
      if (keys['e'] || keys['у']) ix += 1;
      // A/D — strafe by default (WoW post-2010 behaviour).
      if (keys['a'] || keys['ф'] || keys['arrowleft']) ix -= 1;
      if (keys['d'] || keys['в'] || keys['arrowright']) ix += 1;

      // Both-buttons-held auto-run — when both LMB and RMB are down, the
      // character runs forward without W. (Classic WoW.)
      const lmbHeld = lmbHeldRef.current;
      const rmbHeld = rmbHeldRef.current;
      if (lmbHeld && rmbHeld) iz = Math.max(iz, 1);

      const ti = useTouchInput.getState();
      if (ti.active && (Math.abs(ti.moveX) > 0.05 || Math.abs(ti.moveY) > 0.05)) {
        ix = ti.moveX;
        iz = -ti.moveY;
      }
      const ilen = Math.hypot(ix, iz);
      let isMoving = ilen > 0.05;
      const running = (keys['shift'] || ti.running) === true;
      // R232q5h T2.2: when mounted, +60% (matches server tick).
      // Pre-fix: client moved at 7 m/s while server moved at 11.2 m/s → server
      // would teleport you forward as it caught up. Now they match.
      const selfPm = useNet.getState().selfId ? useNet.getState().players[useNet.getState().selfId!] : null;
      const isMountedSpeed = !!(selfPm as { mounted?: boolean } | null)?.mounted;
      const baseTarget = running ? 7 : 4;
      const targetSpeed = isMountedSpeed ? baseTarget * 1.6 : baseTarget;

      // ── Camera-relative directional vectors ──
      // forward = direction from camera toward target, projected onto XZ
      const camPos = cam.position;
      const camTgt = cam.target as Vector3;
      let fx = camTgt.x - camPos.x;
      let fz = camTgt.z - camPos.z;
      const fLen = Math.hypot(fx, fz);
      if (fLen > 0.0001) { fx /= fLen; fz /= fLen; }
      // right = forward rotated -90° in XZ plane
      const rx = fz;
      const rz = -fx;

      // World-space target velocity. Только WASD — никакого auto-walk.
      // R74: WoW WotLK 3.3.5a не двигает персонажа автоматически к таргету.
      // Выделил → стоишь → подбегаешь сам.
      let wxTarget = 0, wzTarget = 0;
      if (isMoving) {
        wxTarget = ((iz / ilen) * fx + (ix / ilen) * rx) * targetSpeed;
        wzTarget = ((iz / ilen) * fz + (ix / ilen) * rz) * targetSpeed;
      }
      // Smooth velocity (accelerate/decelerate).
      const accel = isMoving ? 18 : 22;
      vx += (wxTarget - vx) * Math.min(1, dt * accel);
      vz += (wzTarget - vz) * Math.min(1, dt * accel);
      // R232q5do: hard-clamp velocity к 0 когда нет input — иначе fp дрейф
      // оставлял ~0.001 m/s «ползание» персонажа в одну сторону.
      if (!isMoving) {
        if (Math.abs(vx) < 0.05) vx = 0;
        if (Math.abs(vz) < 0.05) vz = 0;
      }

      // Apply XZ + resolve building collisions.
      // R232q86zbg — two layers of horizontal collision:
      //   1. Legacy XZ-circle blockers (Collisions.ts) — village-tuned hand
      //      lists for fences/inn corners.  Cheap & explicit.
      //   2. Generic mesh collision (PlayerCollision.ts) — sphere vs every
      //      WMO mesh with checkCollisions=true.  Catches Abbey walls,
      //      Stormwind buildings, etc., without per-village registration.
      selfRigBox.rig.root.position.x += vx * dt;
      selfRigBox.rig.root.position.z += vz * dt;
      const resolved = resolveCollision({ x: selfRigBox.rig.root.position.x, z: selfRigBox.rig.root.position.z });
      if (resolved.collided) {
        selfRigBox.rig.root.position.x = resolved.x;
        selfRigBox.rig.root.position.z = resolved.z;
      }
      const _meshPos = {
        x: selfRigBox.rig.root.position.x,
        y: selfRigBox.rig.root.position.y,
        z: selfRigBox.rig.root.position.z,
      };
      // R232q86zb7: bumped collision radius 0.55 → 0.75m. Player был too
      // narrow → угловые WMO walls (Northshire Abbey columns / corner
      // pillars) недостаточно push back. 0.75m ≈ shoulder-width WoTLK
      // human male, matches actual visual silhouette.
      const _hitWall = resolvePlayerVsWorld(scene, _meshPos, 0.75, 1.9);
      if (_hitWall) {
        selfRigBox.rig.root.position.x = _meshPos.x;
        selfRigBox.rig.root.position.z = _meshPos.z;
        // Bleed velocity into the wall — keeps the WoW "slide along
        // surface" feel instead of hard-stop pop.
        const bv = Math.hypot(vx, vz);
        if (bv > 0.01) {
          vx *= 0.4;
          vz *= 0.4;
        }
      }

      // ── Ground sampling ──
      // R232q86zK: HARDENED против streaming-race. Симптом: при первом
      // входе текстуры ADT грузятся постепенно; на полсекунды
      // sampleGroundY возвращает мусор (или мало) → groundedY snap-ится
      // вниз → персонаж падает «до самого дна». Гарантии теперь:
      //   1. Игнорируем sampled, который НИЖЕ текущего groundedY больше
      //      чем на 50м (очевидно мусор: пробитая под-террейн декорация
      //      или вода на дне ущелья).
      //   2. После hasTouchedGround никогда не даём groundedY уплыть
      //      ниже floor = (initialGroundedY - 200). Иначе streaming
      //      LoD-swap может выкинуть terrain mesh из cache на 1-2 кадра
      //      и groundedY обнулится.
      //   3. Аварийный re-sample с y=2000 раз в 1с — если рig.y вдруг
      //      ушёл ниже groundedY на > 8м (т.е. через текстуру),
      //      перепроверяем и тащим обратно.
      {
        const nowG = performance.now();
        if (nowG - lastGroundSample > 160) {
          lastGroundSample = nowG;
          const sampled = sampleGroundY(
            scene,
            selfRigBox.rig.root.position.x,
            selfRigBox.rig.root.position.z,
            selfRigBox.rig.root.position.y,
          );
          if (sampled !== null) {
            // R232q86zN guard #1: junk-low filter against the PEAK seen
            // this session, not against the (mutable) current groundedY.
            // Otherwise a single accepted low sample lowers the threshold
            // for the next, cascading down to the floor over a few sec.
            const peakRef = Number.isFinite(maxEverGroundedY) ? maxEverGroundedY : groundedY;
            const looksJunkLow = hasTouchedGround && sampled < peakRef - PEAK_DROP_LIMIT;
            if (!looksJunkLow) {
              const dyG = sampled - groundedY;
              if (Math.abs(dyG) > 4) groundedY = sampled;
              else groundedY += dyG * Math.min(1, dt * 12);
              if (sampled > maxEverGroundedY) maxEverGroundedY = sampled;
              if (!hasTouchedGround) {
                hasTouchedGround = true;
                selfRigBox.rig.root.position.y = sampled;
                vy = 0;
                groundFloor = sampled - 30;
                maxEverGroundedY = sampled;
              }
            }
          }
        }
        // R232q86zL: aggressive rescue raycast every ~500ms — DIRECT
        // scene.pickWithRay from y=2000, 4000 m length. Catches the case
        // where rig sank below terrain through a streaming gap and the
        // throttled sampler's pass-2 (origin currentY+600, length 1200)
        // still doesn't reach back to the surface above.
        if (hasTouchedGround && nowG - lastRescueSample > 500) {
          lastRescueSample = nowG;
          const px = selfRigBox.rig.root.position.x;
          const pz = selfRigBox.rig.root.position.z;
          const ray = new Ray(new Vector3(px, 2000, pz), new Vector3(0, -1, 0), 4000);
          // R232q86zN: TIGHTENED predicate. Earlier `checkCollisions`-match
          // false-fired on tree trunks / decoration / WMO walls, snapping
          // groundedY onto a roof or low branch. Now ONLY real terrain
          // surfaces: adt-* (Sirus 1:1 ADT chunks), ground-* (procedural
          // chunks), world-ground (the 7×3.5 km horizon disc). WMO floors
          // are still resolved by the throttled sampleGroundY's cached
          // intersect path — that one IS WMO-aware via checkCollisions.
          const hit = scene.pickWithRay(ray, (m) => {
            const n = m.name ?? '';
            return n.startsWith('adt-') || n.startsWith('ground-') || n === 'world-ground';
          });
          if (hit?.hit && hit.pickedPoint) {
            const surfY = hit.pickedPoint.y;
            if (selfRigBox.rig.root.position.y < surfY - 8) {
              console.warn(`[ground rescue] rig.y=${selfRigBox.rig.root.position.y.toFixed(1)} → ${surfY.toFixed(1)} (Δ=${(surfY - selfRigBox.rig.root.position.y).toFixed(1)})`);
              selfRigBox.rig.root.position.y = surfY;
              groundedY = surfY;
              vy = 0;
              groundFloor = Math.max(groundFloor, surfY - 200);
            }
          }
        }
      }

      // ── Jump + gravity ──
      let isGrounded = true;
      if (hasTouchedGround) {
        // R232q86zO: ABSOLUTE SANITY. Regardless of every other guard, if
        // rig.y ever ends a physics frame below sea level (-1m), teleport
        // it back up to 80m. This is the «if every other safeguard breaks
        // this one MUST hold» rule — only fires in pathological streaming
        // races where serverSelfPos was never sent, ADT never loaded, and
        // peak-anchor never seeded. Visible 1-frame jump beats infinite
        // fall; player ends up safely above terrain on next Y-snap pass.
        if (selfRigBox.rig.root.position.y < -1) {
          console.warn(`[ABS-SANITY] rig.y=${selfRigBox.rig.root.position.y.toFixed(1)} < -1; teleport to 80`);
          selfRigBox.rig.root.position.y = 80;
          groundedY = 80;
          maxEverGroundedY = Math.max(maxEverGroundedY, 80);
          vy = 0;
          hasTouchedGround = true;
          groundFloor = 50;
        }
        // R232q86zN guard #2: TWO floors stacked.
        //   • groundFloor — absolute, set on first touch / spawn.
        //   • maxEverGroundedY - PEAK_DROP_LIMIT — relative to the highest
        //     surface we've ever stood on this session.
        // The tighter of the two wins. Net effect: rig can never drop
        // more than 30m below the highest confirmed terrain — kills the
        // «cascade junk-low → fall to abyss» bug at the source.
        const peakFloor = Number.isFinite(maxEverGroundedY)
          ? maxEverGroundedY - PEAK_DROP_LIMIT
          : -Infinity;
        const effectiveFloor = Math.max(groundFloor, peakFloor);
        if (groundedY < effectiveFloor) groundedY = effectiveFloor;
        isGrounded = selfRigBox.rig.root.position.y <= groundedY + 0.001;
        if ((keys[' '] || keys['spacebar']) && isGrounded && vy <= 0) {
          vy = JUMP_V0;
        }
        vy -= GRAVITY * dt;
        // R232q86zK: cap fall velocity. Real WoW caps at ~60 m/s; capping
        // here means even a 10s freefall can't put us 10km under the
        // map before the next rescue raycast lands.
        if (vy < -60) vy = -60;
        selfRigBox.rig.root.position.y += vy * dt;
        if (selfRigBox.rig.root.position.y < groundedY) {
          selfRigBox.rig.root.position.y = groundedY;
          vy = 0;
        }
      }

      // R232q86zbb: paired with inner.rotation.y=0 (RealisticCharacter.ts).
      // pywowlib v8 swizzle gives mesh facing +Z natively → root yaw =
      // atan2(vx, vz) directly aligns body forward с motion direction.
      // Earlier 3π/2 offset compensated inner=-π/2 which is now removed.
      // R232q86zx: walking-direction calibration. The Sirus M2 GLBs have a
      // ~10° native facing offset on some races (user-reported: «идёт
      // боком»). We add a per-session offset readable from URL
      // `?walkOff=N` (radians) so a tester can dial in the exact value
      // without rebuilding. Default 0 = no compensation. Once we find
      // the correct value per race we'll replace this with a per-race
      // table.
      // NB: no visual-facing offset here — the camera follows rig.root, so a
      // root offset is invisible on screen. Body-twist compensation lives in
      // RealisticCharacter's `inner` node (?facing=N to fine-tune).
      if (rmbHeld) {
        const targetYaw = Math.atan2(fx, fz);
        let dy = targetYaw - yaw;
        while (dy > Math.PI) dy -= Math.PI * 2;
        while (dy < -Math.PI) dy += Math.PI * 2;
        yaw += dy * Math.min(1, dt * 14);
      } else if (Math.hypot(vx, vz) > 0.1) {
        const targetYaw = Math.atan2(vx, vz);
        let dy = targetYaw - yaw;
        while (dy > Math.PI) dy -= Math.PI * 2;
        while (dy < -Math.PI) dy += Math.PI * 2;
        yaw += dy * Math.min(1, dt * 10);
      }
      selfRigBox.rig.root.rotation.y = yaw;

      // Throttle network — send actual current velocity (not raw input).
      const now = performance.now();
      if (now - lastSent > MOVE_SEND_MS) {
        lastSent = now;
        const sLen = Math.hypot(vx, vz);
        if (sLen > 0.05) {
          sendMove(vx / Math.max(targetSpeed, sLen), vz / Math.max(targetSpeed, sLen), yaw, running);
        } else {
          sendMove(0, 0, yaw, false);
        }
      }

      selfRigBox.rig.setMoving(isMoving || !isGrounded);
      // R150/R152: mount 3D mesh — Quaternius animal под self rig когда mounted.
      const selfP = useNet.getState().selfId ? useNet.getState().players[useNet.getState().selfId!] : null;
      const isMounted = !!(selfP as { mounted?: boolean } | null)?.mounted;
      if (isMounted && !mountVisualRef.current) {
        const mountId = (selfP as { mountId?: string } | null)?.mountId ?? '';
        // Map mount-id → Quaternius animals/glTF mesh file.
        const mountMeshes: Record<string, string> = {
          'mount-stormwind-steed':  'Horse.gltf',
          'mount-ironforge-ram':    'Bull.gltf',           // ram-proxy (нет ram в pack)
          'mount-darnassus-saber':  'Wolf.gltf',           // saber-cat → wolf-shape
          'mount-mechanostrider':   'Stag.gltf',           // long-leg silhouette
          'mount-orgrimmar-wolf':   'Wolf.gltf',
          'mount-tauren-kodo':      'Bull.gltf',           // кодо ≈ bull
          'mount-undead-skelhorse': 'Horse_White.gltf',
          'mount-troll-raptor':     'Stag.gltf',           // raptor proxy
        };
        const meshFile = mountMeshes[mountId] ?? 'Horse.gltf';
        const placeholder = MeshBuilder.CreateBox('mount-visual', { width: 0.9, height: 0.6, depth: 1.5 }, scene);
        placeholder.parent = selfRigBox.rig.root;
        placeholder.position.y = -0.5;
        placeholder.isPickable = false;
        mountVisualRef.current = placeholder;
        // Async load real mesh, replace placeholder.
        void import('@babylonjs/core/Loading/sceneLoader.js').then(({ SceneLoader }) => {
          void SceneLoader.ImportMeshAsync('', `${import.meta.env.BASE_URL}models/animals/glTF/`, meshFile, scene)
            .then((r) => {
              if (mountVisualRef.current !== placeholder) return;  // Already disposed.
              const real = new TransformNode(`mount-real-${mountId}`, scene);
              real.parent = selfRigBox.rig.root;
              real.position.y = -0.5;
              real.scaling = new Vector3(1.2, 1.2, 1.2);
              for (const m of r.meshes) {
                if (!m.parent) m.parent = real;
                m.isPickable = false;
              }
              placeholder.dispose(false, true);
              mountVisualRef.current = real as unknown as import('@babylonjs/core').Mesh;
            })
            .catch(() => { /* keep placeholder */ });
        });
      } else if (!isMounted && mountVisualRef.current) {
        mountVisualRef.current.dispose(false, true);
        mountVisualRef.current = null;
      }
      selfRigBox.rig.update(dt);
      // R166: pet follows player at 2m offset behind, smooth lerp.
      if (petMeshRef.current) {
        const px = selfRigBox.rig.root.position.x - 2;
        const pz = selfRigBox.rig.root.position.z;
        petMeshRef.current.position.x += (px - petMeshRef.current.position.x) * Math.min(1, dt * 4);
        petMeshRef.current.position.z += (pz - petMeshRef.current.position.z) * Math.min(1, dt * 4);
        petMeshRef.current.position.y = 0;
      }
      // R166: expose scene globally for pet summon onClick handler.
      (window as unknown as { __aiwariScene?: typeof scene }).__aiwariScene = scene;
      // R228: NPC distance-cull — каждые 0.5с проверяем дистанцию до self
      // и setEnabled(false) для NPC > 60м. Ключ к перформансу:
      // KayKit characters имеют тяжёлые skeletons + animation groups, и
      // 600+ NPC на карте без cull → 23k meshes / 1.2M draw calls / 12 FPS.
      npcCullClockRef.current += dt;
      if (npcCullClockRef.current >= 0.5) {
        npcCullClockRef.current = 0;
        const sx = selfRigBox.rig.root.position.x;
        const sz = selfRigBox.rig.root.position.z;
        for (const npc of NPC_RIG_REGISTRY) {
          const dx = npc.x - sx;
          const dz = npc.z - sz;
          const dSq = dx * dx + dz * dz;
          const wantEnabled = dSq < NPC_VIEW_DIST_SQ;
          if (wantEnabled !== npc.enabled) {
            npc.root.setEnabled(wantEnabled);
            // Pause/resume animations — это убирает skeleton-update
            // нагрузку для невидимых NPC. На карте 600+ NPC.
            npc.rig?.setAnimEnabled?.(wantEnabled);
            npc.enabled = wantEnabled;
          }
        }
      }
      // R169: weather check каждые 2 сек по позиции игрока.
      weatherClockRef.current += dt;
      if (weatherClockRef.current >= 2.0) {
        weatherClockRef.current = 0;
        const px = selfRigBox.rig.root.position.x;
        const pz = selfRigBox.rig.root.position.z;
        void import('../scene/Weather.js').then(({ setWeather, weatherForPosition }) => {
          const kind = weatherForPosition(px, pz);
          setWeather(scene, kind, selfRigBox.rig.root);
        });
      }
      // R131x/R137: footstep SFX, surface-aware.
      if (isMoving) {
        const cadence = running ? 0.32 : 0.42;
        footstepClockRef.current += dt;
        if (footstepClockRef.current >= cadence) {
          footstepClockRef.current = 0;
          // R137: pick surface by current zone — города/dungeon = stone,
          // снежные зоны = snow, лесные dead/swamp = wood, остальное grass.
          const px = selfRigBox.rig.root.position.x;
          const pz = selfRigBox.rig.root.position.z;
          const surface: 'grass' | 'stone' | 'wood' | 'snow' | 'carpet' = pickSurface(px, pz);
          import('../audio/AudioManager.js').then(({ audio: a }) => a.playFootstep(surface)).catch(() => {});
          // R232q5d: footstep dust VFX (только когда running чтобы не спамить).
          if (running) {
            const surfTint: 'grass' | 'sand' | 'snow' | 'stone' =
              surface === 'snow' ? 'snow'
              : surface === 'stone' || surface === 'wood' ? 'stone'
              : px > 200 ? 'sand'  // sharpcrest — пустыня
              : 'grass';
            spawnFootstepDust(scene, new Vector3(px, 0, pz), surfTint);
          }
        }
      } else {
        footstepClockRef.current = 0;
      }
      // R87: smooth camera follow вместо мгновенного snap. Это убирает
      // jitter когда персонаж движется неравномерно (например, в серии
      // одиночных WASD-toggle).
      const ct = cam.target as Vector3;
      const sk = Math.min(1, dt * 18);
      ct.x += (selfRigBox.rig.root.position.x - ct.x) * sk;
      ct.y += (selfRigBox.rig.root.position.y - ct.y) * sk;
      ct.z += (selfRigBox.rig.root.position.z - ct.z) * sk;
      // R232q5h T3.6 / R232q5h2 hotfix: camera-pullin raycast disabled.
      // The 7000×3500 world-ground was pickable, ray cast from (ct.y+0.6)
      // toward the camera could clip the ground when terrain dipped, and
      // pulled cam.radius down to ~1m → camera stuck inside terrain →
      // "blue empty screen" bug. Babylon's built-in cam.checkCollisions
      // already handles the common case for meshes with collisions enabled.
      // Re-enable later with a strict mesh-name allowlist (buildings only).

      // R228: Stealth visual — если у self активен buff с id "stealth:*",
      // снижаем visibility всех meshes до 0.30 (полупрозрачный). Тёмная
      // дымка = WotLK 3.3.5a rogue stealth feedback.
      // R232q86zd6 fix #2: cache child meshes per-rig (WeakMap, refreshed
      // on rig swap when child count changes) + read useNet ONCE per frame
      // for both self+remote stealth + skip the lerp loop unless either
      // self.buffs or remote.buffs changed since last visit.
      const _netStateThisFrame = useNet.getState();
      const _selfP2 = _netStateThisFrame.selfId ? _netStateThisFrame.players[_netStateThisFrame.selfId] : null;
      const _selfBuffs = (_selfP2 as { buffs?: Array<{ id: string }> } | null)?.buffs ?? [];
      const _selfStealthed = _selfBuffs.some((b) => typeof b?.id === 'string' && b.id.startsWith('stealth:'));
      const _selfSide = (_selfP2 as { side?: string } | null)?.side ?? '';
      // Track stealth state across frames so we can skip the lerp once
      // visibility has converged.
      const _selfStealthChanged = _selfStealthPrev !== _selfStealthed;
      _selfStealthPrev = _selfStealthed;
      {
        const targetVis = _selfStealthed ? 0.30 : 1.0;
        // Skip the lerp if state hasn't changed AND visibility has settled
        // (within 0.01 of target). One scan is cheap; lerping on every mesh
        // every frame is what burned cycles.
        const cachedSelfMeshes = getCachedChildMeshes(selfRigBox.rig.root, _selfRigCachedMeshes);
        const needsLerp = _selfStealthChanged || _selfStealthLerping;
        if (needsLerp) {
          let stillLerping = false;
          for (let i = 0; i < cachedSelfMeshes.length; i++) {
            const mesh = cachedSelfMeshes[i];
            if (!mesh || mesh.isDisposed()) continue;
            const delta = targetVis - mesh.visibility;
            if (Math.abs(delta) > 0.01) {
              mesh.visibility += delta * Math.min(1, dt * 6);
              stillLerping = true;
            } else if (mesh.visibility !== targetVis) {
              mesh.visibility = targetVis;
            }
          }
          _selfStealthLerping = stillLerping;
        }
      }

      // ── Auto-attack ── when a mob is targeted, in range and alive,
      // queue an attack every 1.4s without requiring extra clicks. Skip
      // entirely if the player is currently a ghost.
      const liveSelfForAA = useNet.getState().selfId ? useNet.getState().players[useNet.getState().selfId!] : null;
      const amDead = !!(liveSelfForAA as { dead?: boolean } | null)?.dead;
      // R232: dead-mode FX (vignette + heavy bloom = "spirit realm look").
      // Toggle при изменении состояния, не каждый кадр.
      if (deadModeRef.current !== amDead) {
        deadModeRef.current = amDead;
        setDeadModeFx(amDead);
      }
      // R63: target ставится ТОЛЬКО явно — клик мышкой по мобу или
      // нажатие TAB. Раньше пассивный авто-таргет находил ближайшего
      // моба в 30м и сразу запускал auto-walk → персонаж сам бежал
      // к врагу без спроса. WoW WotLK 3.3.5a так не делает; мы тоже.
      // Если текущий target умер — просто очищаем, не подбираем нового.
      let targetMid = targetMobIdRef.current;
      const cls = draft.classId ?? 'warrior';
      const reachClass =
        (cls === 'ranger') ? 25
        : (cls === 'mage' || cls === 'cleric') ? 28
        : (cls === 'shaman' || cls === 'warlock' || cls === 'druid') ? 27
        : cls === 'rogue' ? 7
        : 8;
      if (!amDead && targetMid) {
        const tmCheck = useNet.getState().mobs[targetMid];
        if (!tmCheck || tmCheck.hp <= 0 || tmCheck.dead) {
          targetMid = null;
          targetMobIdRef.current = null;
          setTargetMobId(null);
        }
      }
      if (targetMid && !amDead) {
        const tm = useNet.getState().mobs[targetMid];
        if (tm && tm.hp > 0 && !tm.dead) {
          const dxMob = tm.pos.x - selfRigBox.rig.root.position.x;
          const dzMob = tm.pos.z - selfRigBox.rig.root.position.z;
          const dMob = Math.hypot(dxMob, dzMob);
          const reach = reachClass;
          if (dMob <= reach && performance.now() - lastAutoAttack > 1400) {
            lastAutoAttack = performance.now();
            sendAttack(targetMid);
            selfRigBox.rig.triggerAttack();
            // R232q5d: VFX feedback — swing trail в melee, projectile уже есть для ranged.
            const isMelee = draft.classId === 'warrior' || draft.classId === 'paladin' || draft.classId === 'rogue';
            if (isMelee) {
              const tint = draft.classId === 'rogue' ? 'shadow' : 'steel';
              spawnSwingTrail(scene, selfRigBox.rig.root.position.clone(), tint);
            }
            // R232e: real .ogg/.mp3 audio через AudioManager — раньше synth.
            const cls = draft.classId;
            void import('../audio/AudioManager.js').then(({ audio: a }) => {
              if (cls === 'ranger') {
                a.playRanged('bow-draw');
                setTimeout(() => a.playRanged('bow-shoot'), 200);
              } else if (cls === 'mage' || cls === 'cleric') {
                a.playCast('arcane');
              } else if (cls === 'rogue') {
                a.playMelee('dagger', true);
              } else {
                a.playMelee('sword-1h', true);
              }
            }).catch(() => {});
            // R232q5an / R232q5ay: Sirus-parity auto-attack VFX —
            //   melee (warrior, paladin, rogue, dk) → ONLY white swing trail, NO projectile.
            //   ranged/caster (ranger, mage, cleric, warlock, shaman, druid) → projectile.
            //
            // R232q5ay: triple-gate — class whitelist + weapon-type fallback.
            // Юзер repeatedly видел balls у melee — гарантируем что любой
            // melee-weapon (sword/axe/mace/dagger/polearm/fist) подавляет
            // projectile даже если classId неизвестен/незнаком.
            const meleeAttackClasses = new Set(['warrior', 'paladin', 'rogue', 'dk', 'death-knight']);
            const rangedAttackClasses = new Set(['ranger', 'mage', 'cleric', 'warlock', 'shaman', 'druid', 'hunter', 'priest']);
            // Weapon-type derivation from equipped mainhand id (e.g. "sword-2h-001").
            const mhId = (useEquip.getState().equipped.mainhand ?? '').toLowerCase();
            const isMeleeWeapon = /sword|axe|mace|dagger|polearm|fist|hammer|maul|club|staff/.test(mhId);
            const classIsMelee = !!draft.classId && meleeAttackClasses.has(draft.classId);
            const classIsRanged = !!draft.classId && rangedAttackClasses.has(draft.classId);
            // Projectile only when class is ranged AND not in melee set AND no melee weapon equipped.
            const allowProjectile = classIsRanged && !classIsMelee && !isMeleeWeapon;
            if (allowProjectile) {
              const projAb = draft.classId === 'ranger' ? 'arrow-shot'
                : draft.classId === 'cleric' ? 'smite'
                : draft.classId === 'warlock' ? 'shadow-bolt'
                : draft.classId === 'shaman' ? 'lightning-bolt'
                : draft.classId === 'druid' ? 'wrath'
                : 'arcane-missile';
              spawnProjectileVFX({
                scene,
                from: selfRigBox.rig.root.position.clone(),
                toResolver: () => {
                  const cur = useNet.getState().mobs[targetMid];
                  return cur ? new Vector3(cur.pos.x, 0, cur.pos.z) : null;
                },
                abilityId: projAb,
              });
            }
          }
        } else if (!tm) {
          targetMobIdRef.current = null;
          setTargetMobId(null);
        }
      }

      // ── Remote players: sync rigs to net store ──
      const netState = useNet.getState();
      for (const id of Object.keys(netState.players)) {
        if (id === netState.selfId) continue;
        const p = netState.players[id];
        if (!p) continue;
        let rig = remoteRigs.get(id);
        if (!rig) {
          // Spawn placeholder immediately, swap to KayKit when loaded.
          rig = createPlaceholderCharacter(scene, { scale: 1.0 });
          rig.setColours(Color3.FromHexString('#E8C39A'), Color3.FromHexString('#3F2417'), Color3.FromHexString('#3D4A6E'));
          // R226: тегаем mesh-ы remote-игрока с playerId — клик → target frame.
          for (const mesh of rig.root.getChildMeshes(false)) {
            mesh.metadata = { ...(mesh.metadata ?? {}), playerId: id };
            mesh.isPickable = true;
          }
          remoteRigs.set(id, rig);
          const capturedId = id;
          // R232q5i: remote players also load from AIWARI Sirus character set.
          const remoteGender = ((p as { gender?: 'male' | 'female' }).gender ?? 'male') as import('../scene/RaceModelMap.js').Gender;
          const remoteRace = ((p as { race?: string }).race ?? 'human') as import('../scene/RaceModelMap.js').Race;
          const remoteLoadPromise: Promise<unknown> = Promise.all([
            import('../scene/RaceModelMap.js'),
            import('../scene/RealisticCharacter.js'),
          ]).then(async ([rmm, rc]) => {
            const pick = rmm.getRaceModelGlb(remoteRace, remoteGender);
            // R232q5b0: iterate Sirus-only chain (see self-rig pathway above).
            for (const step of pick.chain) {
              if (step.glb === rmm.AIWARI_CHAR_PLACEHOLDER) {
                return await rc.loadRealisticCharacter(scene, { glb: step.glb, baseDir: step.baseDir, scale: pick.scale, targetTallest: pick.targetHeight, raceId: remoteRace, gender: remoteGender });
              }
              try {
                const res = await fetch(`${step.baseDir}${step.glb}`, { method: 'HEAD' });
                if (!res.ok) continue;
                const ct = (res.headers.get('content-type') ?? '').toLowerCase();
                if (ct.includes('text/html')) continue;
                return await rc.loadRealisticCharacter(scene, { glb: step.glb, baseDir: step.baseDir, scale: pick.scale, targetTallest: pick.targetHeight, raceId: remoteRace, gender: remoteGender });
              } catch (e) { console.error('[aiwari] rig chain step failed (remote)', step, e); }
            }
            return await loadKayKitCharacter(scene, { classId: p.classId ?? 'warrior', raceId: (p as { race?: string }).race || undefined });
          });
          void remoteLoadPromise.then((kk) => {
            const cur = remoteRigs.get(capturedId);
            if (!cur || cur === (kk as unknown as PlaceholderCharacterRig)) return;
            const oldPos = cur.root.position.clone();
            const oldRot = cur.root.rotation.y;
            cur.dispose();
            const next = kk as unknown as PlaceholderCharacterRig;
            next.root.position = oldPos;
            next.root.rotation.y = oldRot;
            // R226: тегаем mesh-ы свежего KayKit/GLB-rig после загрузки.
            for (const mesh of next.root.getChildMeshes(false)) {
              mesh.metadata = { ...(mesh.metadata ?? {}), playerId: capturedId };
              mesh.isPickable = true;
            }
            remoteRigs.set(capturedId, next);
            // R232q5h T3.1: register remote rig as shadow caster.
            try {
              const sg = (scene as unknown as { __aiwariShadowGen?: ShadowGeneratorLike }).__aiwariShadowGen;
              addShadowCastersFromRoot(sg, next.root);
            } catch {}
          }).catch(() => { /* keep placeholder */ });
        }
        // R232q5h T2.7: snapshot-interpolated target position (smoother
        // than per-frame raw catch-up). Lerp follows broadcast cadence so
        // remote players slide between packets instead of stair-stepping.
        const interp = interpolatedPos(p as Parameters<typeof interpolatedPos>[0]);
        const t = Math.min(1, dt * 18);
        rig.root.position.x += (interp.x - rig.root.position.x) * t;
        rig.root.position.z += (interp.z - rig.root.position.z) * t;
        // R223 REVERT: inner.rotation.y=π flip есть в GlbCharacter/KayKit,
        // outer должен компенсировать +π (как и self-rig). Иначе remote players
        // ходят спиной.
        const pYawTarget = p.heading + Math.PI;
        let pYDiff = pYawTarget - rig.root.rotation.y;
        while (pYDiff > Math.PI) pYDiff -= Math.PI * 2;
        while (pYDiff < -Math.PI) pYDiff += Math.PI * 2;
        rig.root.rotation.y += pYDiff * Math.min(1, dt * 10);
        rig.setMoving(p.moving);
        rig.update(dt);
        // R228: Stealth visual для remote — стелсящийся враг становится почти
        // невидимым (WotLK 3.3.5a: только rogue видит других стелсеров).
        // Self.side === p.side → союзник видим как 0.30; иначе как 0.10.
        // R232q86zd6 fix #2: re-use the once-per-frame self state captured
        // above + per-rig child-mesh cache + skip the lerp once converged.
        {
          const remoteBuffs = (p as { buffs?: Array<{ id: string }> }).buffs ?? [];
          const remoteStealthed = remoteBuffs.some((b) => typeof b?.id === 'string' && b.id.startsWith('stealth:'));
          const sameSide = !!_selfP2 && _selfSide === ((p as { side?: string }).side ?? '');
          const targetVis = remoteStealthed ? (sameSide ? 0.30 : 0.10) : 1.0;
          const prevStealth = _remoteStealthPrev.get(id);
          const stealthChanged = prevStealth !== remoteStealthed;
          if (stealthChanged) {
            _remoteStealthPrev.set(id, remoteStealthed);
            _remoteStealthLerping.add(id);
          }
          if (_remoteStealthLerping.has(id)) {
            const cached = getCachedChildMeshes(rig.root, _selfRigCachedMeshes);
            let stillLerping = false;
            for (let i = 0; i < cached.length; i++) {
              const mesh = cached[i];
              if (!mesh || mesh.isDisposed()) continue;
              const delta = targetVis - mesh.visibility;
              if (Math.abs(delta) > 0.01) {
                mesh.visibility += delta * Math.min(1, dt * 6);
                stillLerping = true;
              } else if (mesh.visibility !== targetVis) {
                mesh.visibility = targetVis;
              }
            }
            if (!stillLerping) _remoteStealthLerping.delete(id);
          }
        }
      }
      // Clean up rigs for departed players.
      for (const id of Array.from(remoteRigs.keys())) {
        if (!netState.players[id]) {
          remoteRigs.get(id)!.dispose();
          remoteRigs.delete(id);
          // R232q86zd6 fix #2: drop stealth cache entries for departed players.
          _remoteStealthPrev.delete(id);
          _remoteStealthLerping.delete(id);
        }
      }

      // ── Mobs ──
      const liveTargetId = targetMobIdRef.current;
      for (const id of Object.keys(netState.mobs)) {
        const m = netState.mobs[id];
        if (!m) continue;
        let entry = mobMeshes.get(id);
        const isWorldBoss = m.kind === 'world-boss';
        if (!entry) {
          // R232q86e3: distance gate BEFORE creating placeholder + firing GLB
          // load. Previous version unconditionally spawned all 100+ mobs in
          // server state → 149 skeletons + 22 933 animation groups + 1.4 GB
          // texture heap → Chrome OOM at 3.8 GB. Now we only spawn mobs the
          // player can actually see, plus aggressors targeting the player.
          {
            const selfRig = (window as unknown as { __aiwariSelfRig?: { root?: { position: import('@babylonjs/core').Vector3 } } }).__aiwariSelfRig;
            const sp = selfRig?.root?.position;
            const selfId2 = useNet.getState().selfId;
            const isAttackingSelf = !!selfId2 && (m as { target?: string }).target === selfId2;
            if (sp) {
              const distToSelf = Math.hypot(m.pos.x - sp.x, m.pos.z - sp.z);
              if (distToSelf > 120 && !isAttackingSelf) continue;
            }
          }
          // Place a tiny placeholder while GLB loads in the background, so
          // the click-to-pick metadata exists immediately.
          const placeholder = buildMobNode(scene, id, m.kind, ensureMat, worldBossMat);
          entry = {
            node: placeholder, glb: null, kind: m.kind, lastMoving: false,
            posBuf: [{ ts: performance.now(), x: m.pos.x, z: m.pos.z, heading: m.heading ?? 0 }],
            lastServerX: m.pos.x, lastServerZ: m.pos.z, lastServerHeading: m.heading ?? 0,
          };
          // Place placeholder at server position immediately so first frame
          // doesn't render at (0,0).
          placeholder.position.x = m.pos.x;
          placeholder.position.z = m.pos.z;
          mobMeshes.set(id, entry);
          // Async GLB swap. Skeleton-kind mobs use KayKit Skeletons
          // (shared Rig_Medium animations); other kinds use Quaternius.
          const captured = entry;
          const loadP = isKayKitSkeletonKind(m.kind)
            ? loadKayKitMob(scene, m.kind)
            : loadGlbMob(scene, m.kind, (m as { displayId?: number }).displayId ?? 0);
          void loadP.then((mob) => {
            if (!mob) return;
            // If the mob died while loading, dispose immediately.
            if (!mobMeshes.has(id) || mobMeshes.get(id) !== captured) {
              mob.dispose();
              return;
            }
            // Tag the GLB child meshes for click-pick.
            const meshes = mob.root.getChildMeshes(false);
            for (const mesh of meshes) {
              mesh.metadata = { mobId: id };
              mesh.isPickable = true;
            }
            // Move position from placeholder.
            mob.root.position.copyFrom(captured.node.position);
            captured.node.dispose(false, true);
            captured.node = mob.root;
            captured.glb = mob as GlbMob;
            // R232q5h T3.1: register mob as shadow caster too.
            try {
              const sg = (scene as unknown as { __aiwariShadowGen?: ShadowGeneratorLike }).__aiwariShadowGen;
              addShadowCastersFromRoot(sg, mob.root);
            } catch {}
            // R230 REMOVED: красные emissive шарики над мобами — юзер сообщил
            // что они выглядят как "чупа-чупсы" и засоряют экран. У мобов
            // есть nameplate + HP-bar, этого достаточно для visibility.
            // (Раньше: marker = CreateSphere 0.32 with red emissive, parent=mob.root)
            void ensureMat; // keep import alive
          });
        }
        // R232q5: distance dispose >120m — освобождает GPU memory. Раньше
        // setEnabled(false) оставлял меши в heap, OOM crash. Теперь polling
        // дистанцию и dispose когда game-state mob >120m. На обратном пути
        // entry будет пересоздан в этой же loop (placeholder + async GLB).
        const selfRig = (window as unknown as { __aiwariSelfRig?: { root?: { position: import('@babylonjs/core').Vector3 } } }).__aiwariSelfRig;
        const sp = selfRig?.root?.position;
        if (sp) {
          const distToSelf = Math.hypot(m.pos.x - sp.x, m.pos.z - sp.z);
          const selfId2 = useNet.getState().selfId;
          const isAttackingSelf = !!selfId2 && (m as { target?: string }).target === selfId2;
          // 60m — visible/animated. 60-120m — disabled (cheap state).
          // >120m — full dispose чтобы вернуть память.
          if (distToSelf > 120 && !isAttackingSelf) {
            // Hysteresis 5s — иначе на границе моб dispose/recreate каждый кадр.
            const tNow = performance.now();
            entry.farSinceTs = entry.farSinceTs ?? tNow;
            if (tNow - entry.farSinceTs > 5000) {
              if (entry.glb) entry.glb.dispose();
              else entry.node.dispose(false, true);
              mobMeshes.delete(id);
              continue;
            }
          } else {
            entry.farSinceTs = undefined;
          }
          const visible = distToSelf < 60 || isAttackingSelf;
          const wasVisible = entry.node.isEnabled();
          if (wasVisible !== visible) {
            entry.node.setEnabled(visible);
            entry.glb?.setAnimEnabled?.(visible);
          }
          if (!visible) continue;
        }

        // R223: ENTITY INTERPOLATION — eliminates рывки.
        // 1. Detect new server sample (m.pos changed) and append to buffer.
        // 2. Render at "now - 110ms" by interpolating BETWEEN two known
        //    samples in the buffer. The 110ms cushion absorbs the 50ms
        //    server broadcast period + jitter, giving truly smooth motion.
        const nowMs = performance.now();
        const sampleChanged =
          Math.abs(m.pos.x - entry.lastServerX) > 0.001 ||
          Math.abs(m.pos.z - entry.lastServerZ) > 0.001 ||
          Math.abs((m.heading ?? 0) - entry.lastServerHeading) > 0.001;
        if (sampleChanged) {
          entry.posBuf.push({ ts: nowMs, x: m.pos.x, z: m.pos.z, heading: m.heading ?? 0 });
          if (entry.posBuf.length > POS_BUF_MAX) entry.posBuf.shift();
          entry.lastServerX = m.pos.x;
          entry.lastServerZ = m.pos.z;
          entry.lastServerHeading = m.heading ?? 0;
        }
        const renderTs = nowMs - INTERP_DELAY_MS;
        const buf = entry.posBuf;
        let renderX: number, renderZ: number, renderHeading: number;
        if (buf.length === 1 || renderTs <= buf[0]!.ts) {
          renderX = buf[0]!.x; renderZ = buf[0]!.z; renderHeading = buf[0]!.heading;
        } else if (renderTs >= buf[buf.length - 1]!.ts) {
          // Newer than newest sample — extrapolate small step from last 2.
          const last = buf[buf.length - 1]!;
          const prev = buf[buf.length - 2] ?? last;
          const span = Math.max(1, last.ts - prev.ts);
          const ahead = Math.min(80, renderTs - last.ts); // cap extrapolation
          const k = ahead / span;
          renderX = last.x + (last.x - prev.x) * k;
          renderZ = last.z + (last.z - prev.z) * k;
          renderHeading = last.heading;
        } else {
          // Find pair (a, b) such that a.ts <= renderTs <= b.ts
          let a = buf[0]!, b = buf[1]!;
          for (let k = 0; k < buf.length - 1; k++) {
            if (buf[k]!.ts <= renderTs && buf[k + 1]!.ts >= renderTs) {
              a = buf[k]!; b = buf[k + 1]!; break;
            }
          }
          const span = Math.max(1, b.ts - a.ts);
          const t = Math.max(0, Math.min(1, (renderTs - a.ts) / span));
          renderX = a.x + (b.x - a.x) * t;
          renderZ = a.z + (b.z - a.z) * t;
          // Interpolate heading along shortest arc.
          let dh = b.heading - a.heading;
          while (dh > Math.PI) dh -= Math.PI * 2;
          while (dh < -Math.PI) dh += Math.PI * 2;
          renderHeading = a.heading + dh * t;
        }
        entry.node.position.x = renderX;
        // R232q86zdd: mobs were rendered at y=0 (below terrain at Northshire
        // y≈80) — invisible to player walking on surface. Raycast terrain at
        // mob XZ via cached lookup (refresh once when mob first lands on a
        // new chunk). Player rig groundY is used as a cheap approximation if
        // mob is within 32m of player (same chunk). On longer distances do
        // a proper raycast and cache it.
        if (!isWorldBoss) {
          const selfRig = (window as unknown as { __aiwariSelfRig?: { root?: { position: Vector3 } } }).__aiwariSelfRig;
          const playerY = selfRig?.root?.position?.y ?? 0;
          const dxToPlayer = renderX - (selfRig?.root?.position?.x ?? 0);
          const dzToPlayer = renderZ - (selfRig?.root?.position?.z ?? 0);
          if (Math.hypot(dxToPlayer, dzToPlayer) < 25) {
            // Close to player — terrain Y is essentially the same.
            entry.node.position.y = playerY;
          } else {
            // Farther — raycast cache via mobYCache (32m grid).
            const cx = Math.round(renderX / 32) * 32;
            const cz = Math.round(renderZ / 32) * 32;
            const key = `${cx},${cz}`;
            let cached = mobYCache.get(key);
            if (cached === undefined || cached === 0) {
              const ray = new Ray(new Vector3(cx, 2000, cz), Vector3.Down(), 4000);
              const pick = scene.pickWithRay(ray, (mesh) => {
                const n = mesh.name || '';
                return n.startsWith('adt-') || n.startsWith('ground-');
              });
              cached = pick?.hit && pick.pickedPoint ? pick.pickedPoint.y : playerY;
              if (cached !== 0) mobYCache.set(key, cached);
            }
            entry.node.position.y = cached;
          }
        } else {
          entry.node.position.y = 0;
        }
        entry.node.position.z = renderZ;
        // R223 REVERT: mobs тоже имеют inner π flip — нужен +π для outer.
        const targetYaw = renderHeading + Math.PI;
        let yDiff = targetYaw - entry.node.rotation.y;
        while (yDiff > Math.PI) yDiff -= Math.PI * 2;
        while (yDiff < -Math.PI) yDiff += Math.PI * 2;
        // Smaller smoothing — interpolation already gave us smooth heading.
        entry.node.rotation.y += yDiff * Math.min(1, dt * 14);
        // Dead corpse — плавно ложится на бок за ~0.6 сек.
        // R75: smooth death tilt + lutable glow.
        if (m.dead) {
          const targetTilt = Math.PI / 2;
          const curTilt = entry.node.rotation.x ?? 0;
          entry.node.rotation.x = curTilt + (targetTilt - curTilt) * Math.min(1, dt * 4);
          entry.node.position.y = 0.25;
        } else {
          entry.node.rotation.x = 0;
        }
        // R85: видимая иконка мешка над трупами с лутом — плавающая
        // золотая сфера с эмиссией, видна с любой дистанции пока в
        // distance-cull зоне.
        const lootArr = (m as { loot?: { length?: number } }).loot;
        const hasLoot = !!(m.dead && lootArr && (lootArr.length ?? 0) > 0);
        let lootMarker = entry.node.getChildren().find((c) => c.name === `mob-${id}-loot-marker`) as Mesh | undefined;
        if (hasLoot && !lootMarker) {
          const lootMat = ensureMat('loot-marker', '#FFD580', '#FBBF24');
          lootMarker = MeshBuilder.CreateSphere(`mob-${id}-loot-marker`, { diameter: 0.5, segments: 12 }, scene);
          lootMarker.material = lootMat;
          lootMarker.parent = entry.node;
          lootMarker.position.y = 1.6;
          lootMarker.metadata = { mobId: id };
          lootMarker.isPickable = true;
        } else if (!hasLoot && lootMarker) {
          lootMarker.dispose();
        }
        if (lootMarker) {
          // Bobbing animation — float up/down.
          lootMarker.position.y = 1.6 + Math.sin(performance.now() * 0.003) * 0.15;
          lootMarker.rotation.y += dt * 1.5;
        }
        // Targeted mob: lift slightly and bob.
        const isTargeted = id === liveTargetId && !m.dead;
        if (isTargeted) entry.node.position.y += 0.3 + Math.sin(performance.now() * 0.005) * 0.1;
        // R232o: ИСПРАВЛЕНО — раньше moving=hp>0 (всегда true) → мобы
        // постоянно бегут анимацией на месте. Юзер: "мобы бегут на месте".
        // Detect движение по разнице server position в posBuf samples.
        const animBuf = entry.posBuf;
        let moving = false;
        if (animBuf.length >= 2) {
          const last = animBuf[animBuf.length - 1]!;
          const prev = animBuf[animBuf.length - 2]!;
          const dPos = Math.hypot(last.x - prev.x, last.z - prev.z);
          // 0.04m per ~33ms broadcast tick = ~1.2 m/s threshold (slow walk).
          moving = dPos > 0.04;
        }
        if (m.dead) moving = false;
        if (entry.glb && entry.lastMoving !== moving) {
          entry.glb.setMoving(moving);
          entry.lastMoving = moving;
        }
      }
      // Clean up dead mobs.
      for (const id of Array.from(mobMeshes.keys())) {
        if (!netState.mobs[id]) {
          const entry = mobMeshes.get(id)!;
          if (entry.glb) entry.glb.dispose();
          else entry.node.dispose(false, true);
          mobMeshes.delete(id);
        }
      }
    });

    // Mob attack animation — CombatText dispatches this when a mob swings.
    const onMobSwings = (ev: Event) => {
      const { mobId } = (ev as CustomEvent<{ mobId: string }>).detail;
      mobMeshes.get(mobId)?.glb?.triggerAttack();
    };
    window.addEventListener('aiwari-mob-swings', onMobSwings);

    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keydown', onEscapeStop);
      window.removeEventListener('keyup', onUp);
      window.removeEventListener('blur', clearAllInput);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      canvasEl.removeEventListener('contextmenu', onContextMenu);
      window.removeEventListener('aiwari-mob-swings', onMobSwings);
      scene.onPointerObservable.remove(pointerObs);
      scene.onBeforeRenderObservable.remove(obs);
      unsubEquip();
      unsubCosmetic();
      remoteRigs.forEach((r) => r.dispose());
      remoteRigs.clear();
      mobMeshes.forEach((e) => { if (e.glb) e.glb.dispose(); else e.node.dispose(false, true); });
      mobMeshes.clear();
      // Clear module-level GLB master-mesh cache so next scene gets fresh
      // meshes. Without this, cloning from disposed-scene masters causes
      // Babylon to double-allocate GPU buffers → OOM on scene remount/resurrection.
      clearGlbMobCache();
      // R232q23: same for RealisticCharacter cache (per-NPC GLB master,
      // newly added). Fire-and-forget to avoid pulling the heavy module
      // statically. If unmount happens before module ever loaded, it's a no-op.
      void import('../scene/RealisticCharacter.js').then((rc) => rc.clearRealisticCharacterCache?.()).catch(() => {});
      selfRigBox.rig.dispose();
      delete (window as unknown as { __aiwariSelfRig?: unknown }).__aiwariSelfRig;
      atmosphere.dispose();
      postFX.dispose();
      scenery.dispose(false, true);
      ground.dispose();
      groundMat.dispose();
      if (adtCache) adtCache.disposeAll();
      Object.values(mobMats).forEach((m) => m.dispose());
    };
  }, [scene, draft.gender, draft.bodyType, draft.hairColour, sub]);

  const self = selfId ? players[selfId] : null;
  // Achievement: record every zone the player walks into.
  useEffect(() => {
    if (self?.zoneId) recordZoneVisited(self.zoneId);
  }, [self?.zoneId]);

  // R232q5t: per-zone music switch — мелодия меняется при входе в зону.
  // Mapping: PvP zones → combat tune, capitals → calm overworld,
  // others по faction (tidal/rooted/anchorless), boss zones → combat.
  useEffect(() => {
    if (!self?.zoneId) return;
    void import('../data/zones.js').then(({ zoneById }) => {
      const z = zoneById(self.zoneId!);
      if (!z) return;
      const key: 'tidal' | 'rooted' | 'anchorless' | 'overworld' | 'combat' =
        z.pvp === 'warzone' ? 'combat'
        : z.ownedBy === 'tidal' ? 'tidal'
        : z.ownedBy === 'rooted' ? 'rooted'
        : z.ownedBy === 'anchorless' ? 'anchorless'
        : 'overworld';
      setMusicZone(key);
    }).catch(() => {});
  }, [self?.zoneId]);

  // Per-zone WMO city load.  When the player walks into a zone whose
  // internal id matches CITY_MANIFESTS, we lazy-import WmoLoader and stream
  // the matching city GLB(s) under a fresh transform-node.  Disposed when
  // the player leaves the zone.  Cities are ~11-43 MB — never loaded eagerly
  // to avoid the ~192 MB startup OOM that previous eager-load caused.
  useEffect(() => {
    if (!scene || !self?.zoneId) return;
    let cancelled = false;
    let dispose: (() => void) | null = null;
    void import('../scene/WmoLoader.js').then(async (W) => {
      if (cancelled) return;
      const d = await W.loadCityForZone(scene, self.zoneId);
      if (cancelled) {
        d?.();
      } else {
        dispose = d;
      }
    }).catch((err) => console.warn('[WmoLoader] zone load failed:', err));
    return () => {
      cancelled = true;
      if (dispose) dispose();
    };
  }, [scene, self?.zoneId]);

  // BG flag visualisation — pole + coloured banner at each flag's live
  // position. Mounted only while a battleground match is active. Pure
  // additive scene work; doesn't touch realm geometry.
  const bgActive = useBg((s) => s.active);
  const bgFlags = useBg((s) => s.flags);
  useEffect(() => {
    if (!scene || !bgActive) return;
    const meshes: Array<{ name: string; pole: import('@babylonjs/core').Mesh; banner: import('@babylonjs/core').Mesh }> = [];
    // Create one pole+banner per flag; we track them by team name and
    // update positions in a render-tick observer.
    for (const f of bgFlags) {
      const colour = f.team === 'blue' ? new Color3(0.3, 0.55, 1.0) : new Color3(1.0, 0.35, 0.35);
      const pole = MeshBuilder.CreateCylinder(`bg-flag-pole-${f.team}`, { height: 4, diameter: 0.18 }, scene);
      const poleMat = new StandardMaterial(`bg-flag-pole-mat-${f.team}`, scene);
      poleMat.diffuseColor = new Color3(0.25, 0.20, 0.15);
      poleMat.specularColor = new Color3(0, 0, 0);
      pole.material = poleMat;
      pole.position.x = f.pos.x;
      pole.position.z = f.pos.z;
      pole.position.y = 2;
      const banner = MeshBuilder.CreatePlane(`bg-flag-banner-${f.team}`, { width: 1.4, height: 1.0 }, scene);
      const bannerMat = new StandardMaterial(`bg-flag-banner-mat-${f.team}`, scene);
      bannerMat.diffuseColor = colour;
      bannerMat.emissiveColor = colour.scale(0.4);
      bannerMat.specularColor = new Color3(0, 0, 0);
      bannerMat.backFaceCulling = false;
      banner.material = bannerMat;
      banner.position.x = f.pos.x + 0.7;
      banner.position.z = f.pos.z;
      banner.position.y = 3.2;
      meshes.push({ name: f.team, pole, banner });
    }
    // Per-frame update: position from latest bg-store flags.
    const obs = scene.onBeforeRenderObservable.add(() => {
      const live = useBg.getState().flags;
      for (const m of meshes) {
        const f = live.find((x) => x.team === m.name);
        if (!f) continue;
        m.pole.position.x = f.pos.x;
        m.pole.position.z = f.pos.z;
        m.banner.position.x = f.pos.x + 0.7;
        m.banner.position.z = f.pos.z;
        // Lift slightly when carried so it floats with the carrier.
        const lift = f.status === 'carried' ? 1 : 0;
        m.pole.position.y = 2 + lift;
        m.banner.position.y = 3.2 + lift;
      }
    });
    return () => {
      scene.onBeforeRenderObservable.remove(obs);
      for (const m of meshes) {
        m.pole.material?.dispose();
        m.pole.dispose();
        m.banner.material?.dispose();
        m.banner.dispose();
      }
    };
  }, [scene, bgActive, bgFlags.length]);
  // Server now sends live XP — expose level + progress.
  const selfXp = (self as { xp?: number } | null)?.xp ?? 0;
  const xpInfo = levelFromXp(selfXp);

  // TAB key — auto-target nearest hostile mob (WoW classic). Tabbing
  // again cycles to the next-nearest. Single best key for combat UX.
  // R232q5az M02: cap search radius to 80m so Tab doesn't pick a mob
  // 1.3km away (per playtest M02). Surface a toast on no-target so the
  // player isn't left wondering whether the key did anything.
  useEffect(() => {
    const onTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      e.preventDefault();
      const selfRig = (window as unknown as { __aiwariSelfRig?: { root?: { position: import('@babylonjs/core').Vector3 } } }).__aiwariSelfRig;
      const pos = selfRig?.root?.position;
      if (!pos) return;
      const mobsState = useNet.getState().mobs;
      const TAB_RADIUS = 80; // metres
      const candidates = Object.values(mobsState)
        .filter((m): m is NonNullable<typeof m> => !!m && !m.dead && m.hp > 0)
        .map((m) => ({ id: m.id, d: Math.hypot(m.pos.x - pos.x, m.pos.z - pos.z) }))
        .filter((c) => c.d <= TAB_RADIUS)
        .sort((a, b) => a.d - b.d);
      if (candidates.length === 0) {
        window.dispatchEvent(new CustomEvent('aiwari-toast', {
          detail: { kind: 'warn', text: 'Нет врагов поблизости (≤80м).' },
        }));
        return;
      }
      const curIdx = candidates.findIndex((c) => c.id === targetMobIdRef.current);
      const next = candidates[(curIdx + 1) % candidates.length]!;
      setTargetMobId(next.id);
      targetMobIdRef.current = next.id;
    };
    window.addEventListener('keydown', onTab);
    return () => window.removeEventListener('keydown', onTab);
  }, []);

  // R232q5az M01: global modal-stack Esc handler. Pops the topmost
  // registered modal first; if no modal is open, falls through to the
  // R232d target-deselect Esc handler below.
  useEffect(() => {
    const onEscStack = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      const stack = useActiveModals.getState().stack;
      if (stack.length > 0) {
        e.stopPropagation();
        useActiveModals.getState().closeTop();
      }
    };
    // Use capture so we run BEFORE per-component Esc handlers.
    window.addEventListener('keydown', onEscStack, true);
    return () => window.removeEventListener('keydown', onEscStack, true);
  }, []);

  // R232d: ESCAPE — отменяет target (mob/player) + ESC останавливает auto-attack.
  // Юзер: "по нажатию Escape отменялось действие, убиралась цель".
  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      // Если открыт какой-то popup/modal, не вмешиваемся (они сами слушают Escape).
      // Heuristic: если в DOM есть z-50 modal с pointer-events-auto, пропустим.
      const modals = document.querySelectorAll('.z-50.pointer-events-auto, .z-50 [class*="pointer-events-auto"]');
      if (modals.length > 0) return;
      setTargetMobId(null);
      setTargetPlayerId(null);
      setTargetNpcId(null);
      targetMobIdRef.current = null;
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, []);

  // R232q86zH: bridge __aiwariCastSlot for sirus/HotbarSlot click → server.
  // The HotbarSlot component is store-only (no Colyseus access); we wire the
  // click here so all 5 hotbars (bar1..bar5) actually fire abilities through
  // the network instead of just ticking local cooldown.
  useEffect(() => {
    const w = window as unknown as {
      __aiwariCastSlot?: (slot: number, abilityId: string) => void;
      __aiwariUseItem?: (itemId: string) => void;
    };
    w.__aiwariCastSlot = (_slot, abilityId) => {
      try { sendAbility(abilityId, targetMobIdRef.current ?? undefined); } catch { /* ignore */ }
    };
    // Tiny inline DOM toast — no global aiwari-toast listener exists, so
    // we own the visible feedback for item-use ourselves.
    const flashToast = (text: string, kind: 'info' | 'warn' = 'info') => {
      const el = document.createElement('div');
      el.textContent = text;
      el.style.cssText = `position:fixed;top:16%;left:50%;transform:translateX(-50%);background:#1F1813;color:${kind === 'warn' ? '#FF8A80' : '#FFD479'};padding:8px 16px;border:1px solid #5C4520;border-radius:4px;z-index:9999;font-family:Cinzel,serif;font-size:14px;pointer-events:none;box-shadow:0 4px 14px rgba(0,0,0,0.7);`;
      document.body.appendChild(el);
      window.setTimeout(() => el.remove(), 2400);
    };
    w.__aiwariUseItem = (itemId) => {
      // R232q86zW: real server-side use. Hearthstone keeps its own
      // dedicated channel (legacy); everything else goes through
      // 'use-item' which the server validates + applies HP/Mana effect.
      const room = getRoom();
      if (itemId === 'hearthstone') {
        try {
          const raw = localStorage.getItem('aiwari-hearthstone-bind');
          const bind = raw ? JSON.parse(raw) as { x: number; z: number; name?: string } : null;
          if (!bind) { flashToast('Сначала привяжи камень у трактирщика.', 'warn'); return; }
          room?.send('hearthstone-tp', { x: bind.x, z: bind.z });
          flashToast(`Возвращение к ${bind.name ?? 'таверне'}…`, 'info');
        } catch { /* */ }
        return;
      }
      if (!room) { flashToast('Нет соединения с сервером.', 'warn'); return; }
      room.send('use-item', { itemId });
    };
    // R232q86zW: listen to the window event forwarded by realmClient.ts —
    // attaching room.onMessage() at React-mount time loses messages because
    // the room handle isn't ready yet. Window event bridges the gap.
    const onItemResult = (e: Event) => {
      const msg = (e as CustomEvent<{ ok?: boolean; reason?: string; itemId?: string; hp?: number; hpMax?: number; resource?: number; resourceMax?: number; remainingMs?: number; cooldownMs?: number }>).detail ?? {};
      if (msg.ok) {
        const isHp = msg.itemId === 'minor-healing-potion' || msg.itemId === 'soft-bread';
        const isRes = msg.itemId === 'minor-mana-potion' || msg.itemId === 'spring-water';
        if (isHp) flashToast(`Восстановлено здоровье: ${msg.hp}/${msg.hpMax}`, 'info');
        else if (isRes) flashToast(`Восстановлен ресурс: ${msg.resource}/${msg.resourceMax}`, 'info');
        else flashToast(`Предмет использован.`, 'info');
      } else if (msg.reason === 'cooldown') {
        const sec = Math.ceil((msg.remainingMs ?? 0) / 1000);
        flashToast(`Перезарядка предмета: ${sec}с`, 'warn');
      } else if (msg.reason === 'dead') {
        flashToast('Нельзя использовать в виде духа.', 'warn');
      } else if (msg.reason === 'unknown_item') {
        flashToast('Этот предмет ещё нельзя использовать.', 'warn');
      }
    };
    window.addEventListener('aiwari-item-use-result', onItemResult);
    const onUse = (e: Event) => {
      const detail = (e as CustomEvent<{ itemId?: string }>).detail;
      if (detail?.itemId) w.__aiwariUseItem?.(detail.itemId);
    };
    window.addEventListener('aiwari-use-item', onUse);
    return () => {
      delete w.__aiwariCastSlot;
      delete w.__aiwariUseItem;
      window.removeEventListener('aiwari-use-item', onUse);
      window.removeEventListener('aiwari-item-use-result', onItemResult);
    };
  }, []);

  // Hotkey handler — 1..9, 0, -, = cast ability slot 1..12 (WoW WotLK 3.3.5a layout).
  // R232q5an: now reads from hotbar bindings store (drag-drop persisted)
  // instead of cls.abilities[i] — Sirus parity (lvl 1 hotbar = 1-3 starter
  // spells only, rest is empty until the player drags from Spellbook).
  useEffect(() => {
    const SLOT_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '-', '='];
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      const slotIdx = SLOT_KEYS.indexOf(e.key);
      if (slotIdx >= 0 && cls && draft.classId) {
        const row = useHotbar.getState().rowFor(draft.classId, 'bar1');
        const slotVal = row[slotIdx] ?? null;
        // Strip optional `spell:` / `item:` prefix added by R232q86zH multi-row store.
        const abilityId = slotVal && slotVal.startsWith('spell:') ? slotVal.slice('spell:'.length) : slotVal;
        if (!abilityId) return;
        const ab = cls.abilities.find((a) => a.id === abilityId);
        if (ab) {
          // R232q5an: gate by learnedAbilities + level — Spellbook flow.
          const liveSelfL = useNet.getState().selfId ? useNet.getState().players[useNet.getState().selfId!] : null;
          const learnedL = (liveSelfL as { learnedAbilities?: string[] } | null)?.learnedAbilities ?? [];
          const xpL = (liveSelfL as { xp?: number } | null)?.xp ?? 0;
          const lvlL = levelFromXp(xpL).level;
          if (ab.minLevel > lvlL && !learnedL.includes(ab.id)) return;
          // Block while dead.
          const liveSelf = useNet.getState().selfId ? useNet.getState().players[useNet.getState().selfId!] : null;
          if ((liveSelf as { dead?: boolean } | null)?.dead) return;
          // Block during cooldown.
          const cd = useAbilityCD.getState();
          if (cd.remaining(ab.id) > 0) return;
          // R232q5ay: enforce Global Cooldown (1.5s base, 1.0s floor) — WotLK 3.3.5a parity.
          if (cd.gcdRemaining() > 0) {
            const t = document.createElement('div');
            t.textContent = 'Глобальное восстановление…';
            t.style.cssText = 'position:fixed;top:18%;left:50%;transform:translateX(-50%);background:#3A2818;color:#E8C77B;padding:6px 14px;border-radius:4px;z-index:9999;font-family:Cinzel,serif;font-size:13px;border:1px solid #5C4520;pointer-events:none;';
            document.body.appendChild(t);
            setTimeout(() => t.remove(), 700);
            return;
          }
          sendAbility(ab.id, targetMobId ?? undefined);
          // Trigger GCD using player's current haste (cap 60% per server combat math).
          cd.triggerGcd((liveSelfL as { hastePct?: number } | null)?.hastePct ?? 0);
          (window as unknown as { __aiwariSelfRig?: { triggerAttack(): void } }).__aiwariSelfRig?.triggerAttack();
          // R131x: school-aware cast SFX.
          import('../audio/AudioManager.js').then(({ audio: a }) => {
            const id = ab.id.toLowerCase();
            if (/heal|mend|restore|holy/.test(id)) a.playCast('holy');
            else if (/frost|ice|chill/.test(id)) a.playCast('frost');
            else if (/fire|flame|burn|immol/.test(id)) a.playCast('fire');
            else if (/shadow|curse|drain|fear/.test(id)) a.playCast('shadow');
            else if (/lightning|thunder|chain/.test(id)) a.playCast('lightning');
            else if (/nature|moon|root|hurricane/.test(id)) a.playCast('nature');
            else if (/arcane|blink|polymorph/.test(id)) a.playCast('arcane');
            else if (/shoot|aim|multi|arrow/.test(id)) a.playRanged('bow-shoot');
            else if (/charge|cleave|heroic|bash|sunder/.test(id)) {
              // Physical warrior strikes use weapon-aware melee
              const mh = useEquip.getState().equipped.mainhand ?? '';
              const w = /sword.*2h|2h.*sword/.test(mh) ? 'sword-2h'
                : /axe.*2h|2h.*axe/.test(mh) ? 'axe-2h'
                : /sword/.test(mh) ? 'sword-1h'
                : /axe/.test(mh) ? 'axe-1h'
                : /mace|club/.test(mh) ? 'mace-1h'
                : /hammer|maul/.test(mh) ? 'hammer-2h'
                : /dagger/.test(mh) ? 'dagger'
                : 'sword-1h';
              a.playMelee(w as never, true);
            }
            else a.playCast('arcane');  // generic shimmer
          }).catch(() => {});
          cd.startCooldown(ab.id, ab.cooldown * 1000);
          if (ab.windup > 0) {
            cd.startCast(ab.id, ab.name.ru, ab.windup * 1000);
            window.setTimeout(() => useAbilityCD.getState().clearCast(), ab.windup * 1000);
          } else {
            // R232q5h T2.1: instant cast → 280ms pulse on bar.
            cd.pulseInstant(ab.id, ab.name.ru);
          }
          // VFX at caster + optional projectile to target.
          if (scene) {
            const selfRig = (window as unknown as { __aiwariSelfRig?: { root?: { position: import('@babylonjs/core').Vector3 } } }).__aiwariSelfRig;
            const pos = selfRig?.root?.position;
            if (pos) {
              spawnAbilityVFX({ scene, position: pos, abilityId: ab.id });
              // School-driven layered cast burst (denser, longer than the
              // element burst above). Skips physical abilities entirely.
              const abSchool = (ab as { school?: string }).school?.toLowerCase();
              if (!abSchool || abSchool !== 'physical') {
                const cls = (liveSelfL as { classId?: string } | null)?.classId ?? '';
                const inferred = inferSpellSchool(ab.id);
                const school = (abSchool && abSchool !== 'physical'
                  ? abSchool
                  : (cls ? defaultSchoolForClass(cls) : inferred)) as Parameters<typeof createSpellEffect>[1];
                if (['fire','frost','holy','shadow','nature','arcane'].includes(school)) {
                  createSpellEffect(scene, school, pos, undefined, 'cast');
                }
              }
              // R232q5h T1.4: projectile gated by ability range, not just class.
              // Paladin's "judgement" (range 18) is ranged-holy → must show projectile.
              // Auto-attack still uses class gate (no ability spec there).
              const isRangedAbility = ab.range >= 6;
              if (ab.type === 'damage' && targetMobId && isRangedAbility) {
                const tid = targetMobId;
                spawnProjectileVFX({
                  scene,
                  from: pos.clone(),
                  toResolver: () => {
                    const m = useNet.getState().mobs[tid];
                    return m ? new Vector3(m.pos.x, 0, m.pos.z) : null;
                  },
                  abilityId: ab.id,
                });
                // Spawn an impact burst at the target on a delay matching
                // average projectile flight (~0.4s for ranged spells).
                if (!abSchool || abSchool !== 'physical') {
                  const cls = (liveSelfL as { classId?: string } | null)?.classId ?? '';
                  const inferred = inferSpellSchool(ab.id);
                  const school = ((ab as { school?: string }).school?.toLowerCase() && (ab as { school?: string }).school !== 'physical'
                    ? (ab as { school?: string }).school
                    : (cls ? defaultSchoolForClass(cls) : inferred)) as Parameters<typeof createSpellEffect>[1];
                  if (['fire','frost','holy','shadow','nature','arcane'].includes(school)) {
                    setTimeout(() => {
                      const m = useNet.getState().mobs[tid];
                      if (!m) return;
                      createSpellEffect(scene, school, new Vector3(m.pos.x, 0, m.pos.z), undefined, 'impact');
                    }, 380);
                  }
                }
              }
            }
          }
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cls, targetMobId, draft.classId]);

  // R141: социал-сообщения теперь обрабатывает <SocialOverlay /> (модальные
  // карточки + тосты вместо alert/confirm). Подключён в JSX ниже.

  // R166: pet system — простой animal mesh следует за player.
  // Hunter → wolf, Warlock → demon (sphere imp), other → dog.
  useEffect(() => {
    const onSummon = () => {
      const cls = draft.classId;
      if (cls !== 'ranger' && cls !== 'warlock') {
        const t = document.createElement('div');
        t.textContent = 'Питомцы доступны только Охотникам и Чернокнижникам';
        t.style.cssText = 'position:fixed;top:30%;left:50%;transform:translateX(-50%);background:#7F1D1D;color:white;padding:8px 16px;border-radius:6px;z-index:9999;font-family:Cinzel,serif;';
        document.body.appendChild(t);
        setTimeout(() => t.remove(), 2500);
        return;
      }
      const sceneRef = (window as unknown as { __aiwariScene?: import('@babylonjs/core').Scene }).__aiwariScene;
      if (!sceneRef || petMeshRef.current) return;
      const meshFile = cls === 'warlock' ? 'monsters/Big/glTF/Demon.gltf' : 'animals/glTF/Wolf.gltf';
      void import('@babylonjs/core/Loading/sceneLoader.js').then(({ SceneLoader }) => {
        const baseDir = `${import.meta.env.BASE_URL}models/`;
        const lastSlash = meshFile.lastIndexOf('/');
        const dir = baseDir + meshFile.substring(0, lastSlash + 1);
        const file = meshFile.substring(lastSlash + 1);
        void SceneLoader.ImportMeshAsync('', dir, file, sceneRef).then((r) => {
          const root = new TransformNode('pet-root', sceneRef);
          for (const m of r.meshes) {
            if (!m.parent) m.parent = root;
            m.isPickable = false;
          }
          root.scaling = new Vector3(0.7, 0.7, 0.7);
          petMeshRef.current = root;
        }).catch(() => {});
      });
    };
    const onDismiss = () => {
      petMeshRef.current?.dispose(false, true);
      petMeshRef.current = null;
    };
    window.addEventListener('aiwari-pet-toggle', onSummon);
    window.addEventListener('aiwari-pet-dismiss', onDismiss);
    return () => {
      window.removeEventListener('aiwari-pet-toggle', onSummon);
      window.removeEventListener('aiwari-pet-dismiss', onDismiss);
    };
  }, [draft.classId]);

  // R136: emote-команды — слушаем custom event от Chat и играем
  // подходящую animation group на self rig.
  useEffect(() => {
    const onEmote = (ev: Event) => {
      const detail = (ev as CustomEvent<{ name: string }>).detail;
      const rig = (window as unknown as { __aiwariSelfRig?: { playEmote?: (n: string) => void; triggerEmote?: (n: string) => void; triggerAttack?: () => void } }).__aiwariSelfRig;
      if (!rig) return;
      const name = detail?.name ?? 'wave';
      // R232q5dh: realistic rigs (UBC, Northcliffe Sketchfab) имеют playEmote.
      if (typeof rig.playEmote === 'function') rig.playEmote(name);
      // Quaternius UAC GLB rigs знают triggerEmote (см. GlbCharacter.ts).
      else if (typeof rig.triggerEmote === 'function') rig.triggerEmote(name);
      // KayKit Adventurer rigs — fallback на triggerAttack чтобы хоть что-то двигалось.
      else rig.triggerAttack?.();
    };
    window.addEventListener('aiwari-emote', onEmote);
    return () => window.removeEventListener('aiwari-emote', onEmote);
  }, []);

  // R232q5b1 (Sirus visuals): wire server cast-* events to caster animation
  // + school-tuned particle bursts. Three streams:
  //   1. cast-start / cast-observed → triggerAnim('cast') + spawn 'cast' burst
  //      at caster pos. cast-observed mirrors cast-start to non-casters.
  //   2. cast-impact → spawn 'impact' burst at the supplied target pos.
  //   3. cast-end / cast-interrupt → return rig to idle (handled implicitly
  //      by triggerAnim's auto-return-to-idle).
  // School number → SpellSchool string mapping mirrors WotLK 3.3.5a spell.dbc.
  useEffect(() => {
    const room = getRoom();
    if (!room) return;
    type SchoolStr = Parameters<typeof createSpellEffect>[1];
    const schoolFromDbc = (n: number, name: string): SchoolStr => {
      // Vanilla Spell.dbc school mask values most commonly seen in the
      // mass-vanilla-spells dataset: 0=physical/none, 1=holy, 2=fire,
      // 3=nature, 4=frost, 5=shadow, 6=arcane.
      switch (n) {
        case 1: return 'holy';
        case 2: return 'fire';
        case 3: return 'nature';
        case 4: return 'frost';
        case 5: return 'shadow';
        case 6: return 'arcane';
        default: return inferSpellSchool(name);
      }
    };
    const findCasterPos = (casterId: string | undefined): import('@babylonjs/core').Vector3 | null => {
      if (!casterId) return null;
      // Self-cast: prefer the live self rig position (interpolated).
      const selfId = useNet.getState().selfId;
      if (selfId && casterId === selfId) {
        const rig = (window as unknown as { __aiwariSelfRig?: { root?: { position: import('@babylonjs/core').Vector3 } } }).__aiwariSelfRig;
        if (rig?.root?.position) return rig.root.position.clone();
      }
      // Remote player or mob caster — pull from net-store schema pos.
      const players = useNet.getState().players;
      const mobs = useNet.getState().mobs;
      const p = players[casterId] ?? null;
      if (p?.pos) return new Vector3(p.pos.x, 0, p.pos.z);
      const m = mobs[casterId] ?? null;
      if (m?.pos) return new Vector3(m.pos.x, 0, m.pos.z);
      return null;
    };
    const triggerCasterAnim = (casterId: string | undefined, school: SchoolStr) => {
      const animByScool: Record<string, string> = {
        fire: 'castFire', frost: 'castFrost', holy: 'castHoly',
        shadow: 'castShadow', nature: 'castNature', arcane: 'castArcane',
      };
      const animName = animByScool[school] ?? 'cast';
      const selfId = useNet.getState().selfId;
      if (casterId && selfId && casterId === selfId) {
        const rig = (window as unknown as { __aiwariSelfRig?: { triggerAnim?: (n: string) => boolean; triggerAttack?: () => void } }).__aiwariSelfRig;
        if (rig?.triggerAnim) {
          const ok = rig.triggerAnim(animName);
          if (!ok) rig.triggerAnim('cast');
        } else if (rig?.triggerAttack) {
          rig.triggerAttack();
        }
      }
      // Remote caster anim sync requires a per-rig registry — follow-up.
    };
    const onCastStart = (msg: { wowId?: number; casterId?: string; name?: string; school?: number; mode?: 'cast' | 'channel'; pos?: { x: number; y: number; z: number }; targetId?: string }) => {
      const sceneRef = (window as unknown as { __aiwariScene?: import('@babylonjs/core').Scene }).__aiwariScene;
      if (!sceneRef) return;
      const school = schoolFromDbc(msg.school ?? 0, msg.name ?? '');
      const pos = msg.pos
        ? new Vector3(msg.pos.x, 0, msg.pos.z)
        : findCasterPos(msg.casterId);
      if (pos) {
        const phase: Parameters<typeof createSpellEffect>[4] = msg.mode === 'channel' ? 'channel' : 'cast';
        if (['fire', 'frost', 'holy', 'shadow', 'nature', 'arcane'].includes(school)) {
          createSpellEffect(sceneRef, school, pos, undefined, phase);
        }
      }
      triggerCasterAnim(msg.casterId, school);
    };
    const onCastImpact = (msg: { wowId?: number; targetId?: string; pos?: { x: number; y: number; z: number }; school?: number; name?: string }) => {
      const sceneRef = (window as unknown as { __aiwariScene?: import('@babylonjs/core').Scene }).__aiwariScene;
      if (!sceneRef) return;
      let pos: import('@babylonjs/core').Vector3 | null = null;
      if (msg.pos) {
        pos = new Vector3(msg.pos.x, 0, msg.pos.z);
      } else if (msg.targetId) {
        const players = useNet.getState().players;
        const mobs = useNet.getState().mobs;
        const t = players[msg.targetId] ?? mobs[msg.targetId] ?? null;
        if (t?.pos) pos = new Vector3(t.pos.x, 0, t.pos.z);
      }
      if (!pos) return;
      const school = schoolFromDbc(msg.school ?? 0, msg.name ?? '');
      if (['fire', 'frost', 'holy', 'shadow', 'nature', 'arcane'].includes(school)) {
        createSpellEffect(sceneRef, school, pos, undefined, 'impact');
      }
    };
    room.onMessage('cast-start', onCastStart);
    room.onMessage('cast-observed', onCastStart);
    room.onMessage('cast-impact', onCastImpact);
    // R232q86ze: server broadcasts `mob-cast` for every mob ability cast
    // (RealmRoom.ts:1141, 1158). We currently render mob telegraphs from
    // server-driven schemas (BossMechanic + boss-scripts), so the per-mob
    // cast event is informational only — but Colyseus warns
    // «onMessage() not registered for type 'mob-cast'» on every receipt,
    // flooding the console. Bind a no-op to silence it; future mob-cast
    // bar UI can hook in here.
    room.onMessage('mob-cast', () => { /* informational, no client-side render yet */ });
    // R232q86zf: 'combat' message has handlers (CombatLog/CombatText/DpsMeter/…)
    // but those components mount lazily after World, so the FIRST few combat
    // events arrive before any of them register → console flood with
    // «onMessage() not registered for type 'combat'». Bind a placeholder no-op
    // here so colyseus.js sees a handler immediately; lazy components register
    // their own listeners on top (Colyseus 0.16 calls all registered
    // handlers in arrival order).
    // R232q86zh4: this is no longer a no-op — it also feeds the chat «Бой»
    // tab, which sat forever empty (user: «не вижу, что бьётся»). WoW-style
    // lines, self-relevant events only (your hits + hits on you).
    room.onMessage('combat', (ev: { id: string; srcId: string; tgtId: string; amount: number; kind: string; ts?: number }) => {
      try {
        const netS = useNet.getState();
        const meId = netS.selfId;
        if (!meId || (ev.srcId !== meId && ev.tgtId !== meId)) return;
        const nameOf = (id: string): string =>
          id === meId ? 'Вы'
            : netS.players[id]?.name
              ?? (netS.mobs[id] as { displayName?: string } | undefined)?.displayName
              ?? netS.mobs[id]?.kind
              ?? 'Существо';
        const src = nameOf(ev.srcId);
        const tgt = nameOf(ev.tgtId);
        let body = '';
        if (ev.kind === 'damage' || ev.kind === 'crit') {
          const critSfx = ev.kind === 'crit' ? ' (критический удар!)' : '';
          body = src === 'Вы'
            ? `Вы наносите ${tgt} ${ev.amount} ед. урона${critSfx}.`
            : `${src} наносит вам ${ev.amount} ед. урона${critSfx}.`;
        } else if (ev.kind === 'miss') {
          body = src === 'Вы' ? `Вы промахиваетесь по ${tgt}.` : `${src} промахивается по вам.`;
        } else if (ev.kind === 'heal') {
          body = `${src} восстанавливает ${tgt === 'Вы' ? 'вам' : tgt} ${ev.amount} ед. здоровья.`;
        } else if (ev.kind === 'kill') {
          body = src === 'Вы' ? `Вы убили ${tgt}!` : `${src} убил ${tgt}.`;
        } else if (ev.kind === 'xp') {
          body = `Вы получаете ${ev.amount} опыта.`;
        } else if (ev.kind === 'levelup') {
          body = `Поздравляем — вы достигли нового уровня!`;
        } else if (ev.kind === 'oor') {
          body = 'Слишком далеко.';
        } else {
          return;
        }
        void import('./sirus/Chat.js').then(({ useChat }) => {
          useChat.getState().pushMessage({
            id: ev.id ?? `cmb-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            ts: ev.ts ?? Date.now(),
            channel: 'combat',
            fromName: '',
            body,
          });
        });
      } catch { /* chat bridge is best-effort */ }
    });
    // R232q86zn: same pattern for 'spell-learned' — server announces a new
    // spell unlock when a level-up grants a CLASS_SPELLBOOK entry, but we
    // currently surface that via the achievements toast pipeline (which
    // mounts lazily). Placeholder no-op silences the cold-start warning;
    // real consumer (toast + spellbook refresh) plugs in when SpellBook
    // is open.
    room.onMessage('spell-learned', () => { /* real handler in Spellbook / AchievementToast */ });
    // No teardown — Colyseus 0.16 onMessage has no remove API; on reconnect
    // realmClient flushes listeners via ROOM_LISTENER_CLEANUP.
  }, []);

  // R232q5aM: macro engine listeners — /target /focus /cleartarget /click.
  // /equip and /use-item are forwarded to inventory store handlers (those
  // panels listen separately).
  const focusIdRef = useRef<string | null>(null);
  useEffect(() => {
    const onTargetCmd = (ev: Event) => {
      const detail = (ev as CustomEvent<{ query: string }>).detail;
      const q = (detail?.query ?? '').trim().toLowerCase();
      if (!q || q === 'clear' || q === 'none') {
        setTargetMobId(null);
        setTargetPlayerId(null);
        targetMobIdRef.current = null;
        return;
      }
      if (q === 'focus') {
        if (focusIdRef.current) {
          // Focus could be a player or mob — prefer mob match.
          if (useNet.getState().mobs[focusIdRef.current]) {
            setTargetMobId(focusIdRef.current);
            targetMobIdRef.current = focusIdRef.current;
          } else {
            setTargetPlayerId(focusIdRef.current);
          }
        }
        return;
      }
      if (q === 'nearestenemy' || q === 'nearest') {
        const selfRig = (window as unknown as { __aiwariSelfRig?: { root?: { position: import('@babylonjs/core').Vector3 } } }).__aiwariSelfRig;
        const pos = selfRig?.root?.position;
        if (!pos) return;
        const candidates = Object.values(useNet.getState().mobs)
          .filter((m): m is NonNullable<typeof m> => !!m && !m.dead && m.hp > 0)
          .map((m) => ({ id: m.id, d: Math.hypot(m.pos.x - pos.x, m.pos.z - pos.z) }))
          .filter((c) => c.d <= 80)
          .sort((a, b) => a.d - b.d);
        const next = candidates[0];
        if (next) {
          setTargetMobId(next.id);
          targetMobIdRef.current = next.id;
        }
        return;
      }
      // By name: search players first, then mobs (case-insensitive).
      const playersState = useNet.getState().players;
      const pp = Object.values(playersState).find((p) => p.name?.toLowerCase() === q);
      if (pp) { setTargetPlayerId(pp.id); return; }
      const mobsState = useNet.getState().mobs;
      const mm = Object.values(mobsState).find((m) => (m as { name?: string }).name?.toLowerCase() === q);
      if (mm) { setTargetMobId(mm.id); targetMobIdRef.current = mm.id; return; }
    };
    const onFocusCmd = () => {
      // Lock current target as focus.
      const cur = targetMobIdRef.current ?? targetPlayerId;
      if (cur) focusIdRef.current = cur;
    };
    const onClearTarget = () => {
      setTargetMobId(null);
      setTargetPlayerId(null);
      targetMobIdRef.current = null;
    };
    const onClickSlot = (ev: Event) => {
      const detail = (ev as CustomEvent<{ slot: number }>).detail;
      const slot = detail?.slot;
      if (!slot || slot < 1 || slot > 12) return;
      // Synthesise a keypress on the equivalent SLOT_KEYS char so the
      // existing hotbar handler runs (gating, GCD, VFX all handled there).
      const SLOT_KEYS = ['1','2','3','4','5','6','7','8','9','0','-','='];
      const key = SLOT_KEYS[slot - 1]!;
      window.dispatchEvent(new KeyboardEvent('keydown', { key, bubbles: true }));
    };
    const onUseItem = (ev: Event) => {
      const detail = (ev as CustomEvent<{ itemQuery: string }>).detail;
      window.dispatchEvent(new CustomEvent('aiwari-use-item', { detail }));
    };
    const onEquipItem = (ev: Event) => {
      const detail = (ev as CustomEvent<{ itemQuery: string }>).detail;
      window.dispatchEvent(new CustomEvent('aiwari-equip-item', { detail }));
    };
    window.addEventListener('aiwari-macro-target', onTargetCmd);
    window.addEventListener('aiwari-macro-focus', onFocusCmd);
    window.addEventListener('aiwari-macro-cleartarget', onClearTarget);
    window.addEventListener('aiwari-macro-click-slot', onClickSlot);
    window.addEventListener('aiwari-macro-use-item', onUseItem);
    window.addEventListener('aiwari-macro-equip', onEquipItem);
    return () => {
      window.removeEventListener('aiwari-macro-target', onTargetCmd);
      window.removeEventListener('aiwari-macro-focus', onFocusCmd);
      window.removeEventListener('aiwari-macro-cleartarget', onClearTarget);
      window.removeEventListener('aiwari-macro-click-slot', onClickSlot);
      window.removeEventListener('aiwari-macro-use-item', onUseItem);
      window.removeEventListener('aiwari-macro-equip', onEquipItem);
    };
  }, [targetPlayerId]);

  // R232q76: Phase 0 demolition — short-circuit world HUD until the Sirus
  // 1:1 panels (sirus/ActionBar, sirus/CastBar, sirus/PlayerFrame, …) land
  // in Phase 2-3. Engine/Colyseus/scene/movement keep mounting via the
  // hooks above this point — only the *visible* HUD is suppressed.
  //
  // R232q76: old World HUD demolition — restore here when sirus/* panels land.
  // The legacy return-tree below is preserved verbatim inside an `if (RESTORE_LEGACY_HUD)`
  // block so the new sirus/* panels can port behaviours over (drag/drop, GCD
  // sweep, target frame range pulse, XP strip, etc). Do NOT remove until
  // Phase 4 sign-off — the entire conditional body is intentionally dead.
  const RESTORE_LEGACY_HUD = false as boolean;
  if (RESTORE_LEGACY_HUD) {
    return (
    <div className="absolute inset-0 z-40 pointer-events-none">
      {/* R232q5w: Heraldic self-frame — gold border, warm-near-black panel,
          Cinzel name. Replaces the previous orange #B45309 WotLK 3.3.5a look. */}
      {/* R232q5ac: width shrinks 240→180 on mobile (<640px) so self-frame +
          minimap fit on 320px viewport. Use Tailwind utilities so responsive
          override actually beats the inline style. */}
      <div className="absolute top-3 left-3 pointer-events-auto w-[180px] sm:w-[240px]">
        <div
          className="flex items-stretch gap-1.5 p-1.5"
          style={{
            background: 'linear-gradient(180deg, #161210, #0B0907)',
            border: '1.5px solid #C9A96A',
            boxShadow: '0 0 14px rgba(0,0,0,0.7), inset 0 0 10px rgba(201,169,106,0.10)',
          }}
        >
          {/* Heraldic square portrait — gold-bordered, class icon. */}
          <div
            className="w-14 h-14 flex-shrink-0 relative overflow-hidden flex items-center justify-center"
            style={{
              background: `linear-gradient(180deg, ${(cls?.colour ?? '#3A2818')}25 0%, #0B0907 100%)`,
              border: `2px solid #5C4520`,
              boxShadow: 'inset 0 0 10px rgba(201,169,106,0.12)',
            }}
          >
            <img
              src={`${import.meta.env.BASE_URL}icons/classes/${draft.classId ?? 'warrior'}.png`}
              alt=""
              className="w-full h-full object-cover"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
                (e.currentTarget.nextElementSibling as HTMLElement | null)?.style.setProperty('display', 'flex');
              }}
            />
            <span className="hidden absolute inset-0 items-center justify-center text-2xl font-bold" style={{ color: cls?.colour ?? '#fff' }}>
              {(draft.name || 'W').charAt(0).toUpperCase()}
            </span>
            {/* Heraldic level chip — bottom-right, gold on near-black. */}
            <div
              className="absolute bottom-0 right-0 px-1.5 py-0.5 text-[10px] leading-none"
              style={{
                background: 'linear-gradient(180deg, #1F1813, #0B0907)',
                color: '#E8C77B',
                fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                fontWeight: 700,
                borderTop: '1px solid #5C4520',
                borderLeft: '1px solid #5C4520',
              }}
            >
              {xpInfo.level}
            </div>
          </div>
          <div className="flex flex-col gap-1 flex-1 min-w-0 justify-center">
            {/* Heraldic name + active title. */}
            <div className="text-[13px] truncate leading-none" style={{ fontFamily: 'Cinzel, serif', fontWeight: 700, color: '#E8DCC0', letterSpacing: '0.04em' }}>
              {draft.name || 'Странник'}
            </div>
            <PlayerTitleLine />
            {/* HP bar — heraldic frame, green fill. */}
            <div className="relative h-4 overflow-hidden" style={{ background: '#1a0807', border: '1px solid #5C4520' }}>
              <div
                className="h-full transition-[width] duration-200"
                style={{
                  width: `${self ? (self.hp / Math.max(1, self.hpMax)) * 100 : 0}%`,
                  background: 'linear-gradient(180deg, #5BAE57 0%, #3F8C3D 100%)',
                }}
              />
              <div className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white drop-shadow-[0_0_2px_rgba(0,0,0,0.95)] leading-none" style={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}>
                {self?.hp ?? 0} / {self?.hpMax ?? 0}
              </div>
            </div>
            {/* Mana / resource bar — heraldic frame. */}
            <div className="relative h-3 overflow-hidden" style={{ background: '#0a0a1a', border: '1px solid #5C4520' }}>
              <div
                className="h-full transition-[width] duration-200"
                style={{
                  width: `${self ? (self.resource / Math.max(1, self.resourceMax)) * 100 : 0}%`,
                  background: cls?.id === 'mage' || cls?.id === 'cleric'
                    ? 'linear-gradient(180deg, #60A5FA 0%, #2563EB 100%)'
                    : cls?.id === 'ranger'
                    ? 'linear-gradient(180deg, #FBBF24 0%, #D97706 100%)'
                    : 'linear-gradient(180deg, #F87171 0%, #B91C1C 100%)',
                }}
              />
              <div className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-white drop-shadow-[0_0_2px_rgba(0,0,0,0.95)] leading-none" style={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}>
                {self?.resource ?? 0} / {self?.resourceMax ?? 0}
              </div>
            </div>
          </div>
        </div>
        <div className="text-[9px] mt-1 flex items-center gap-2 px-1" style={{ fontFamily: 'Spectral SC, serif', color: '#9C8E72', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
          {connected ? <span style={{ color: '#5BAE57' }}>◆ онлайн</span> : <span style={{ color: '#A33232' }}>◆ офлайн</span>}
          <span>{Object.keys(players).length} героев</span>
          {sub && <span style={{ color: sub.colour }} className="truncate">{sub.name.ru}</span>}
        </div>
        {/* R159: buff/debuff icons strip — WotLK 3.3.5a style 24×24px. */}
        <BuffIconStrip />
      </div>

      {/* R232h: AuthPanel + Wallet полностью убраны из игрового мира.
          Раньше "Войти" + "💎 0 гость" торчали в углу. Юзер: "убери".
          Wallet остаётся доступен через MicroMenu / inventory currency display. */}
      {/* R131q: MicroMenu pushed down 156→210 so it starts AFTER the
          minimap (170 px circle + 12 top + 16 padding = ~198) instead of
          overlapping its bottom edge. */}
      <MicroMenu topPx={210} />
      {/* Bag slots bottom-right — WotLK 3.3.5a position */}
      <BagSlots />

      <CurrentZoneBadge />

      {/* Bottom: ability bar with cooldown sweeps + cast bar above */}
      {cls && (
        <ActionBar
          cls={cls}
          targetMobId={targetMobId}
          self={self}
          scene={scene}
          level={xpInfo.level}
          learnedAbilities={(self as { learnedAbilities?: string[] } | null)?.learnedAbilities ?? []}
        />
      )}

      {/* Target frame (mob HP/level/portrait + range indicator).
          R226: actually shows for mob OR remote player; NPCs пока без frame
          (клик по ним сразу открывает диалог). */}
      {targetMobId && mobs[targetMobId] && self && (() => {
        const tm = mobs[targetMobId]!;
        const d = Math.hypot(tm.pos.x - self.pos.x, tm.pos.z - self.pos.z);
        const c = draft.classId;
        // R223: WotLK 3.3.5a reach (matches client click handler + server reachForClass).
        const r = c === 'ranger' ? 22
          : (c === 'mage' || c === 'cleric') ? 26
          : c === 'rogue' ? 5
          : 6;
        return <TargetFrame mob={tm} distance={d} reach={r} />;
      })()}
      {/* R226: target frame для другого игрока. Hostility = self.side ≠ other.side */}
      {targetPlayerId && players[targetPlayerId] && self && (() => {
        const tp = players[targetPlayerId]!;
        const d = Math.hypot(tp.pos.x - self.pos.x, tp.pos.z - self.pos.z);
        const c = draft.classId;
        const r = c === 'ranger' ? 22
          : (c === 'mage' || c === 'cleric') ? 26
          : c === 'rogue' ? 5
          : 6;
        return <PlayerTargetFrame
          player={tp}
          distance={d}
          reach={r}
          hostile={tp.side !== self.side}
          onClose={() => setTargetPlayerId(null)}
        />;
      })()}

      {/* WoW-style XP bar — chunkier strip across the bottom of the screen. */}
      {!xpInfo.isCapped && (
        <div className="absolute bottom-0 left-0 right-0 pointer-events-none z-30">
          <div className="relative h-6 bg-black/55 backdrop-blur-sm border-t-2 border-aiwari-bolt/60">
            <div
              className="h-full transition-all"
              style={{
                width: `${xpInfo.xpToNextLevel > 0 ? (xpInfo.xpInLevel / xpInfo.xpToNextLevel) * 100 : 0}%`,
                background: 'linear-gradient(90deg, #7C3AED 0%, #A855F7 50%, #C084FC 100%)',
                boxShadow: 'inset 0 0 8px rgba(192, 132, 252, 0.8)',
              }}
            />
            <div className="absolute inset-0 flex items-center justify-center text-[12px] font-bold text-white font-mono drop-shadow-[0_0_3px_rgba(0,0,0,0.95)]">
              Ур. {xpInfo.level} · {xpInfo.xpInLevel.toLocaleString()} / {xpInfo.xpToNextLevel.toLocaleString()} опыта
              ({Math.floor((xpInfo.xpInLevel / Math.max(1, xpInfo.xpToNextLevel)) * 100)}%)
            </div>
          </div>
        </div>
      )}
      {xpInfo.isCapped && (
        <div className="absolute bottom-0 left-0 right-0 pointer-events-none z-30">
          <div className="h-6 bg-aiwari-bolt/40 border-t-2 border-aiwari-bolt flex items-center justify-center text-[12px] font-bold text-aiwari-bolt">
            УРОВЕНЬ МАКСИМУМ · Ур. {xpInfo.level}
          </div>
        </div>
      )}

      {/* Controls hint — dismissable */}
      <ControlsHint />

      <WorldBossBanner />
      <ZoneToast />
      <DailyLoginToast />
      <MobileControls />
      <Inventory />
      <Minimap />
      <DungeonFinder />
      <DungeonHud />
      <LootRollModal />
      <Crafting />
      <Auction />
      <Endgame />
      <FpsCounter />
      <DebugTerrainHud />
      <Chat />
      <CombatText />
      <NpcNameplates />
      <MobNameplates />
      <NpcQuestMarkers />
      <HoverTooltip />
      <Leaderboard />
      <CastBar />
      <WorldMapV2 />
      <QuestTracker />
      <Spellbook />
      <FactionLore />
      <Achievements />
      <AchievementToast />
      <SocialOverlay />
      <ReputationPanel />
      <Bank />
      <DailyQuests />
      <TradeWindow />
      <FriendsList />
      <MacroPanel />
      <KeybindEditor />
      <GameClock />
      <CombatLog />
      <MountSelector />
      <MountPicker />
      <CurrencyTracker />
      <DpsMeter />
      <IgnoreList />
      <GroupFinder />
      <Harvester />
      <GatherNodeFeedback />
      <Skinner />
      <ProfessionTrainer />
      <MatBagPanel />
      <ClassQuests />
      <FlightMaster />
      <InnkeeperDialog />
      <StableMaster />
      <PetFrame />
      <SpiritHealer />
      <BattlegroundBrowser />
      <HolidayCalendar />
      <HolidayActiveBadge />
      <GuestBanner />
      <HonorPanel />
      <HelpScreen />
      <Quartermaster />
      <StatsPanel />
      <SettingsPanel />
      <ChangeLog />
      <DeathRecap />
      <RecentLoot />
      <LegendaryQuestPanel />
      <LootPrompt />
      <LootToast />
      <DeathOverlay />
      <TutorialBanner />
      <CombatDebugHud />
      <NpcDialog />
      <VendorShop />
      <TalentTree />
      <MailBox />
      <Guild />
      <CharacterPanel />
      <Battleground />
      <BgFlagTracker />
      <BgHudOverlay />
      <BattlegroundMap />
      <BattlegroundScore />
      <CastBar />
      <VanillaCastBar />
      <BuffsRow />
    </div>
  );
  } // R232q76 end of preserved legacy HUD if-block.
  // R232q78: Sirus 1:1 HUD объединение всех 17 panel-компонентов в едином layer.
  // PreSirusGameplayBlocked больше не показывается — заменён реальным HUD.
  void PreSirusGameplayBlocked;
  return (
    <>
      <SirusHud />
      <SpawnLoadingOverlay />
    </>
  );
}

/**
 * R232q86zS: covers the Babylon canvas with «Загрузка мира…» until the
 * render loop dispatches `aiwari-spawn-applied` (i.e. rig has been snapped
 * to the server-supplied spawn position). Eliminates the brief (0,0,0)
 * intermediate frame that users mistook for «персонаж падает в текстуры».
 */
function SpawnLoadingOverlay() {
  const [show, setShow] = useState(true);
  useEffect(() => {
    let settledFrames = 0;
    let lastY = NaN;
    // R232q86zV: dual-condition hide — only when:
    //   1. spawn-applied event fired (rig snapped to server pos), AND
    //   2. rig.y has been STABLE for 6 consecutive sample ticks (~1.2s).
    //      Stable = |dy| < 0.05m between ticks. Catches the case where rig
    //      teleports to spawn at y=100, then gravity drops it to terrain
    //      y=80 over 1s — overlay hides only AFTER landing, so the user
    //      never sees the «падение в землю».
    let spawned = false;
    const onSpawn = () => { spawned = true; };
    window.addEventListener('aiwari-spawn-applied', onSpawn);
    const tick = window.setInterval(() => {
      if (!spawned) return;
      const dbg = (globalThis as { __aiwariDebug?: { selfRigY?: number; selfRigX?: number; selfRigZ?: number; hasTouchedGround?: boolean } }).__aiwariDebug;
      const y = dbg?.selfRigY;
      // Sanity: rig must NOT be at the legacy origin tower (|x|<200 && |z|<200).
      const x = dbg?.selfRigX ?? 0;
      const z = dbg?.selfRigZ ?? 0;
      const atOrigin = Math.abs(x) < 200 && Math.abs(z) < 200;
      if (atOrigin || typeof y !== 'number' || !Number.isFinite(y)) {
        settledFrames = 0; lastY = NaN; return;
      }
      if (Number.isFinite(lastY) && Math.abs(y - lastY) < 0.05) {
        settledFrames++;
        if (settledFrames >= 6 && dbg?.hasTouchedGround) {
          setShow(false);
        }
      } else {
        settledFrames = 0;
      }
      lastY = y;
    }, 200);
    // Safety: hide after 60s even if event never fires (don't lock UI forever).
    // Raised from 25s — on cold caches the terrain tiles land later than 25s
    // and players were released into the sky («парю в небе»).
    const safety = window.setTimeout(() => setShow(false), 60000);
    return () => {
      window.removeEventListener('aiwari-spawn-applied', onSpawn);
      window.clearInterval(tick);
      window.clearTimeout(safety);
    };
  }, []);
  if (!show) return null;
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'radial-gradient(circle at center, #1a1410 0%, #0a0703 70%, #000 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#FFD479',
        fontFamily: 'Cinzel, serif',
        fontSize: 24,
        letterSpacing: '0.2em',
        textTransform: 'uppercase',
        pointerEvents: 'none',
      }}
    >
      <div style={{ fontSize: 14, color: '#8A6B2E', marginBottom: 16, letterSpacing: '0.4em' }}>
        AIWARI
      </div>
      <div>Загрузка мира…</div>
      <div style={{ marginTop: 18, width: 220, height: 3, background: '#3a2818', borderRadius: 2, overflow: 'hidden' }}>
        <div
          style={{
            width: '40%',
            height: '100%',
            background: 'linear-gradient(90deg, transparent, #FAB300, transparent)',
            animation: 'aiwari-spawn-pulse 1.4s linear infinite',
          }}
        />
      </div>
      <style>{`
        @keyframes aiwari-spawn-pulse {
          0%   { transform: translateX(-150%); }
          100% { transform: translateX(350%); }
        }
      `}</style>
    </div>
  );
}

/**
 * R137: Surface picker by world position. Используется для footstep SFX.
 * Города → stone (мощёная брусчатка), Тёмный Склеп → carpet (приглушённый шум),
 * снежные зоны → snow, мёртвый лес/болота → wood (мостки + dead branches).
 */
function pickSurface(x: number, z: number): 'grass' | 'stone' | 'wood' | 'snow' | 'carpet' {
  // Cities — stone cobblestone (Железный Костёр = подземная mine, Шестерённый Город = метал floor)
  // Львиная Твердыня
  if (x >= -3500 && x <= -2700 && z >= -500 && z <= 500) return 'stone';
  // Железный Костёр
  if (x >= -3500 && x <= -2900 && z >= -1700 && z <= -1300) return 'stone';
  // Шестерённый Город
  if (x >= -3500 && x <= -3100 && z >= -1300 && z <= -900) return 'stone';
  // Тёмный Склеп — карпет/каменный пол (звук прицельный, приглушённый)
  if (x >= -1700 && x <= -1300 && z >= 1300 && z <= 1700) return 'carpet';
  // Лунная Ива (на ветвях древа — wood)
  if (x >= 700 && x <= 1100 && z >= 1100 && z <= 1500) return 'wood';
  // Грозовой Утёс (плато с верёвочными мостами — wood)
  if (x >= 200 && x <= 600 && z >= 700 && z <= 1100) return 'wood';
  // Цитадель Бранного Колеса — стона/гравий
  if (x >= 2700 && x <= 3500 && z >= -500 && z <= 500) return 'stone';
  // Прибой — wood villages on stilts
  if (x >= 2700 && x <= 3100 && z >= 700 && z <= 1100) return 'wood';

  // Снежные биомы (Альтерак, Хинтерхиллс, Зимний Оплот)
  if (x >= -1450 && x <= -1100 && z >= -700 && z <= -300) return 'snow';   // Альтерак
  if (x >= -1450 && x <= -1100 && z >= 600 && z <= 1100) return 'snow';    // Хинтерхиллс
  if (x >= 1100 && x <= 1450 && z >= 1100 && z <= 1500) return 'snow';     // Зимний Оплот

  // Болота / мёртвый лес (Сумрачное Болото, Болото Скорби)
  if (x >= -1100 && x <= -800 && z >= -1100 && z <= -700) return 'wood';   // Twilight Swamp
  if (x >= -700 && x <= -300 && z >= -1100 && z <= -700) return 'wood';    // Swamp of Sorrows

  // Default: grass для open world
  return 'grass';
}

// R162: PlayerTitleLine — active title из localStorage. Слушает change-event.
function PlayerTitleLine() {
  const [title, setTitle] = useState<string>(() => localStorage.getItem('aiwari-active-title') ?? '');
  useEffect(() => {
    const onChange = (ev: Event) => {
      const detail = (ev as CustomEvent<{ title: string }>).detail;
      setTitle(detail?.title ?? '');
    };
    window.addEventListener('aiwari-title-changed', onChange);
    return () => window.removeEventListener('aiwari-title-changed', onChange);
  }, []);
  if (!title) return null;
  return (
    <div className="text-[10px] italic truncate leading-none -mt-0.5" style={{ color: '#FBBF24', fontFamily: 'Cinzel, serif' }}>
      «{title}»
    </div>
  );
}

/**
 * R159/R160: BuffIconStrip — visible buffs/active casts/debuffs под player frame.
 * Vanilla WoW: 24px квадратные иконки с timer overlay. Источники:
 *  - useAbilityCD (active cooldowns локально)
 *  - server self.buffs (real buffs от сервера через ActiveBuff schema)
 */
function BuffIconStrip() {
  const cd = useAbilityCD();
  const draft = useUI((s) => s.draft);
  const cls = draft.classId ? CLASSES.find((c) => c.id === draft.classId) : null;
  const players = useNet((s) => s.players);
  const selfId = useNet((s) => s.selfId);
  const self = selfId ? players[selfId] : null;
  const [, setTick] = useState(0);
  useEffect(() => {
    const i = setInterval(() => setTick((t) => (t + 1) & 0xfff), 250);
    return () => clearInterval(i);
  }, []);
  if (!cls) return null;
  const now = Date.now();
  // R160: server buffs first (priority), then local cooldowns.
  type Slot = { key: string; icon: string; name: string; remain: number; total: number; isCD: boolean };
  const slots: Slot[] = [];
  const serverBuffs = self?.buffs ?? [];
  for (const b of serverBuffs) {
    const remain = b.endsAt - now;
    if (remain <= 0) continue;
    slots.push({ key: `buff-${b.id}`, icon: '✨', name: b.id, remain, total: 30_000, isCD: false });
  }
  for (const a of cls.abilities) {
    const remain = cd.remaining(a.id);
    if (remain <= 0) continue;
    slots.push({ key: `cd-${a.id}`, icon: a.icon, name: a.name.ru, remain, total: a.cooldown * 1000, isCD: true });
  }
  if (slots.length === 0) return null;
  return (
    <div className="flex items-center gap-1 mt-1.5 px-1 flex-wrap">
      {slots.slice(0, 12).map((s) => {
        const pct = Math.max(0, Math.min(1, s.remain / s.total));
        return (
          <div
            key={s.key}
            // R232q5aj: heraldic restyle — warm-near-black + muted-gold border.
            className="relative w-6 h-6 rounded-sm overflow-hidden"
            style={{
              background: 'linear-gradient(180deg, #161210 0%, #0B0907 100%)',
              border: '1px solid #5C4520',
              boxShadow: s.isCD ? '0 0 4px rgba(201,169,106,0.45)' : '0 0 4px rgba(201,169,106,0.25)',
            }}
            title={`${s.name} · ${(s.remain / 1000).toFixed(1)}с${s.isCD ? ' (откат)' : ' (усиление)'}`}
          >
            <span
              className="absolute inset-0 flex items-center justify-center text-[14px]"
              style={{ filter: s.isCD ? 'grayscale(0.6) brightness(0.3)' : 'none' }}
            >{s.icon}</span>
            <div
              className="absolute bottom-0 left-0 right-0 transition-[height]"
              style={{
                height: s.isCD ? `${pct * 100}%` : `${(1 - pct) * 100}%`,
                background: 'rgba(11,9,7,0.65)',
              }}
            />
            <div
              className="absolute bottom-0 right-0 px-0.5 text-[8px] leading-none"
              style={{
                background: 'rgba(11,9,7,0.85)',
                color: '#E8C77B',
                fontFamily: '"JetBrains Mono", ui-monospace, monospace',
                fontWeight: 600,
              }}
            >
              {s.remain >= 1000 ? `${Math.ceil(s.remain / 1000)}` : ''}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Compact dismissable controls hint — collapses into a "?" tab when closed. */
function ControlsHint() {
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem('aiwari-hint-open') !== '0'; } catch { return true; }
  });
  const close = () => {
    setOpen(false);
    try { localStorage.setItem('aiwari-hint-open', '0'); } catch { /* ignore */ }
  };
  const reopen = () => {
    setOpen(true);
    try { localStorage.setItem('aiwari-hint-open', '1'); } catch { /* ignore */ }
  };
  if (!open) {
    return (
      <button
        type="button"
        onClick={reopen}
        className="absolute bottom-4 right-4 w-9 h-9 flex items-center justify-center rounded-full text-amber-300 font-bold text-lg pointer-events-auto"
        style={{
          background: 'linear-gradient(180deg, #2a1d10 0%, #14110c 100%)',
          border: '2px solid #8B6014',
          boxShadow: '0 0 8px rgba(255,200,80,0.3), inset 0 0 6px rgba(0,0,0,0.5)',
          fontFamily: 'Cinzel, serif',
        }}
        title="Подсказки управления"
      >?</button>
    );
  }
  return (
    <div
      className="absolute right-4 px-3 py-2 text-[10px] text-aiwari-fgMuted leading-relaxed pointer-events-auto"
      style={{
        // R232i: подняли значительно выше из bottom-4 (там пересекалось с
        // bag-slots). Теперь правее минимапа.
        top: 250,
        background: 'linear-gradient(180deg, rgba(20,15,8,0.92), rgba(8,5,3,0.92))',
        border: '1.5px solid #8B6014',
        borderRadius: 8,
        maxWidth: 280,
        boxShadow: '0 0 10px rgba(0,0,0,0.7)',
      }}
    >
      <button
        type="button"
        onClick={close}
        className="absolute top-1 right-1 w-5 h-5 flex items-center justify-center text-amber-300 hover:text-amber-100"
        style={{ fontFamily: 'Cinzel, serif' }}
        title="Скрыть"
      >×</button>
      <div className="text-aiwari-fg font-bold text-[10px] mb-0.5 pr-6" style={{ color: '#FFE9A6', fontFamily: 'Cinzel, serif' }}>УПРАВЛЕНИЕ</div>
      WASD ход · Shift бег · Space прыжок · ПКМ камера<br/>
      Клик по мобу — атака · 1-5 — способности · Esc снять цель<br/>
      C статы · B сумка · I заклинания · L квесты · M карта · T таланты
    </div>
  );
}

// =============================================================================
// R232q76 demolition — Phase 0: replaced by sirus/ActionBar.tsx in Phase 2.
// The ActionBar component (interfaces + function below) is the legacy
// self-rolled main bar. Keep its source intact so the new sirus/ActionBar
// implementation can reference it (drag-n-drop wiring, GCD sweep math,
// haste-aware cooldown rendering, school-aware cast SFX, etc).
// Runtime call-site is gated behind `false &&` in the World return tree, so
// this function is never invoked in Phase 0. Do NOT delete until Phase 4
// sign-off; do NOT "improve" — port verbatim into sirus/ActionBar.tsx.
// =============================================================================
interface ActionBarAbility {
  id: string;
  name: { ru: string };
  desc: { ru: string };
  icon: string;
  cooldown: number;
  windup: number;
  minLevel: number;
  range: number;
  type: string;
}

interface ActionBarClass {
  id: import('../data/classes.js').ClassId;
  colour: string;
  abilities: ActionBarAbility[];
}

function ActionBar({
  cls,
  targetMobId,
  self,
  scene,
  level,
  learnedAbilities,
}: {
  cls: ActionBarClass;
  targetMobId: string | null;
  self: { hp: number; hpMax: number } | null | undefined;
  scene: import('@babylonjs/core').Scene | null;
  level: number;
  learnedAbilities: string[];
}) {
  // R232q76 Phase 0: this function is never invoked — its sole call-site
  // `<ActionBar .../>` lives inside the legacy `if (RESTORE_LEGACY_HUD)`
  // dead branch. Body preserved verbatim so sirus/ActionBar.tsx (Phase 2)
  // can port the behaviours: drag/drop hotbar binding, GCD radial sweep,
  // haste-aware cooldown render, school-aware cast SFX, range projectile
  // VFX, DBC tooltip overlay, etc.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => (t + 1) & 0xffff), 100);
    return () => window.clearInterval(id);
  }, []);
  const cd = useAbilityCD.getState();

  // R232q5an: ensure hotbar row is populated for this class (idempotent).
  // Subscribe to bindings so drag/drop re-renders. Read row from store.
  // R232q86zH: multi-row API — bar1 here.
  useEffect(() => { useHotbar.getState().ensureRow(cls.id, 'bar1'); }, [cls.id]);
  const row = useHotbar((s) => s.bindings[cls.id]?.bar1) ?? useHotbar.getState().rowFor(cls.id, 'bar1');
  // R232q5aS Wave 5: pull DBC ability metadata once per class (idempotent).
  // Used below to overlay canonical Sirus 3.3.5a tooltip text on hover.
  useEffect(() => { useDbcAbilities.getState().ensureClassAbilities(cls.id); }, [cls.id]);
  const dbcByCurated = useDbcAbilities((s) => s.byClass[cls.id]);

  // 12 slots — WotLK 3.3.5a main action bar. Slots driven by hotbar bindings,
  // not raw class.abilities — so lvl 1 only has 1-3 starter spells bound.
  const SLOT_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '-', '='] as const;
  const abilityById = (id: string | null): ActionBarAbility | null =>
    id ? cls.abilities.find((a) => a.id === id) ?? null : null;
  const slots = SLOT_KEYS.map((key, i) => {
    const raw = row[i] ?? null;
    const id = raw && raw.startsWith('spell:') ? raw.slice('spell:'.length) : raw;
    return { key, idx: i, ability: abilityById(id) };
  });

  return (
    <div
      // R232q61: flex-wrap + max-w-[96vw] so 12 slots fold onto two rows on
      // phones (<=480px) instead of clipping off-screen. Desktop keeps the
      // single-row WotLK 3.3.5a look because viewport is wide enough.
      className="absolute left-1/2 -translate-x-1/2 flex items-end gap-1 sm:gap-1.5 flex-wrap justify-center pointer-events-auto max-w-[96vw]"
      style={{
        bottom: 84,  /* R131h: lift above chat box (was 8) — chat covered slots 1-5 */
        padding: '6px 10px',
        // R232q5aj: heraldic restyle — warm-near-black bg + muted gold trim.
        background: 'linear-gradient(180deg, #161210 0%, #0B0907 100%)',
        border: '1.5px solid #5C4520',
        borderRadius: '6px',
        boxShadow: '0 0 14px rgba(0,0,0,0.9), inset 0 1px 0 rgba(201,169,106,0.10)',
      }}
    >
      {slots.map((slot) => {
        const a = slot.ability;
        // R232q5an: drop handler — accepts ability dragged from Spellbook
        // OR from another hotbar slot (slot-to-slot swap). Sirus parity.
        const onDrop = (e: React.DragEvent<HTMLElement>) => {
          e.preventDefault();
          // R232q5aM: macro-slot drop binds the macro to this bar slot's key.
          const macroRaw = e.dataTransfer.getData(MACRO_DND_MIME);
          if (macroRaw) {
            try {
              const m = JSON.parse(macroRaw) as { scope?: 'player' | 'character'; idx?: number };
              if (m.scope && typeof m.idx === 'number') {
                useMacros.getState().update(m.scope, m.idx, { hotkey: slot.key.toUpperCase() });
              }
              return;
            } catch { /* ignore */ }
          }
          const raw = e.dataTransfer.getData(HOTBAR_DND_MIME);
          if (!raw) return;
          try {
            const payload = JSON.parse(raw) as { abilityId?: string; fromSlot?: number };
            const fromSlot = typeof payload.fromSlot === 'number' ? payload.fromSlot : -1;
            const abilityId = payload.abilityId;
            if (!abilityId) return;
            if (!cls.abilities.some((ab) => ab.id === abilityId)) return;
            if (fromSlot >= 0 && fromSlot < HOTBAR_SIZE) {
              useHotbar.getState().swap(cls.id, 'bar1', fromSlot, 'bar1', slot.idx);
            } else {
              useHotbar.getState().bind(cls.id, 'bar1', slot.idx, { kind: 'spell', id: abilityId });
            }
          } catch { /* ignore malformed payload */ }
        };
        const onDragOver = (e: React.DragEvent<HTMLElement>) => {
          if (e.dataTransfer.types.includes(HOTBAR_DND_MIME)
              || e.dataTransfer.types.includes(MACRO_DND_MIME)) {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'copy';
          }
        };
        if (!a) {
          return (
            <div
              key={`empty-${slot.key}`}
              // R232q5aj: heraldic empty slot — dashed bronze + faded ◇ glyph.
              // R232q5an: empty slot is a drop target.
              className="w-9 h-9 sm:w-11 sm:h-11 rounded relative flex items-center justify-center"
              style={{
                border: '1px dashed #3A2818',
                background: 'linear-gradient(180deg, #161210 0%, #0B0907 100%)',
              }}
              onDragOver={onDragOver}
              onDrop={onDrop}
              title="Перетащи заклинание из книги (I)"
            >
              <span
                className="text-[14px] sm:text-[16px] select-none pointer-events-none"
                style={{ color: 'rgba(92,69,32,0.55)', fontFamily: 'Cinzel, serif' }}
              >◇</span>
              <span
                className="text-[10px] absolute top-0 left-1 drop-shadow-[0_0_2px_rgba(0,0,0,0.95)]"
                style={{ color: '#9C8E72', fontFamily: '"JetBrains Mono", ui-monospace, monospace', fontWeight: 600 }}
              >{slot.key}</span>
            </div>
          );
        }
        const remaining = cd.remaining(a.id);
        const cdSec = remaining > 0 ? Math.ceil(remaining / 100) / 10 : 0;
        const sweepPct = a.cooldown > 0 ? Math.min(100, (remaining / (a.cooldown * 1000)) * 100) : 0;
        const onCd = remaining > 0;
        // R232q5b1 (Sirus visuals): radial GCD sweep — 1.5s base, hastened by
        // player haste % (floored at 1.0s by triggerGcd in ability-cd-store).
        // Renders as a clockwise conic-gradient sweep over the icon when the
        // player is in the post-cast 1.5s window. Independent of per-ability
        // cooldown which uses a vertical fill above.
        const gcdRem = cd.gcdRemaining();
        const gcdActive = gcdRem > 0 && !onCd; // ability-CD overlay supersedes GCD spinner
        // Convert remaining → 0..360deg sweep angle. Bar empties (sweep
        // shrinks) clockwise — conic-gradient transparency boundary travels
        // 360→0 as gcdRem → 0.
        const gcdSweepDeg = gcdActive ? Math.min(360, (gcdRem / 1500) * 360) : 0;
        // R232q5an: lock if ability not learned (level gate / spellbook gate).
        const notLearned = a.minLevel > level && !learnedAbilities.includes(a.id);
        return (
          <button
            key={`slot-${slot.idx}-${a.id}`}
            type="button"
            draggable
            onDragStart={(e) => {
              e.dataTransfer.effectAllowed = 'move';
              e.dataTransfer.setData(HOTBAR_DND_MIME, JSON.stringify({ fromBar: 'bar1', fromSlot: slot.idx, binding: { kind: 'spell', id: a.id } }));
            }}
            onDragOver={onDragOver}
            onDrop={onDrop}
            onContextMenu={(e) => {
              // R232q5an: right-click → "Снять с панели" (Sirus parity).
              e.preventDefault();
              useHotbar.getState().clear(cls.id, 'bar1', slot.idx);
            }}
            onClick={() => {
              if (onCd) return;
              if (notLearned) return;
              // R232q5ay: enforce Global Cooldown (1.5s base, 1.0s floor) — WotLK 3.3.5a parity.
              const cdNow = useAbilityCD.getState();
              if (cdNow.gcdRemaining() > 0) {
                const t = document.createElement('div');
                t.textContent = 'Глобальное восстановление…';
                t.style.cssText = 'position:fixed;top:18%;left:50%;transform:translateX(-50%);background:#3A2818;color:#E8C77B;padding:6px 14px;border-radius:4px;z-index:9999;font-family:Cinzel,serif;font-size:13px;border:1px solid #5C4520;pointer-events:none;';
                document.body.appendChild(t);
                setTimeout(() => t.remove(), 700);
                return;
              }
              sendAbility(a.id, targetMobId ?? undefined);
              // Trigger GCD with player's haste — read from net store.
              {
                const sid = useNet.getState().selfId;
                const sp = sid ? useNet.getState().players[sid] : null;
                cdNow.triggerGcd((sp as { hastePct?: number } | null)?.hastePct ?? 0);
              }
              // R131x: school-aware
              import('../audio/AudioManager.js').then(({ audio: au }) => {
                const id = a.id.toLowerCase();
                if (/heal|mend|restore|holy/.test(id)) au.playCast('holy');
                else if (/frost|ice/.test(id)) au.playCast('frost');
                else if (/fire|flame/.test(id)) au.playCast('fire');
                else if (/shadow|curse/.test(id)) au.playCast('shadow');
                else if (/lightning|thunder/.test(id)) au.playCast('lightning');
                else if (/nature|moon/.test(id)) au.playCast('nature');
                else if (/arcane|blink/.test(id)) au.playCast('arcane');
                else if (/shoot|aim|arrow/.test(id)) au.playRanged('bow-shoot');
                else au.playCast('arcane');
              }).catch(() => {});
              useAbilityCD.getState().startCooldown(a.id, a.cooldown * 1000);
              if (a.windup > 0) {
                useAbilityCD.getState().startCast(a.id, a.name.ru, a.windup * 1000);
                window.setTimeout(() => useAbilityCD.getState().clearCast(), a.windup * 1000);
              } else {
                useAbilityCD.getState().pulseInstant(a.id, a.name.ru);
              }
              if (scene) {
                const selfRig = (window as unknown as { __aiwariSelfRig?: { root?: { position: import('@babylonjs/core').Vector3 } } }).__aiwariSelfRig;
                const pos = selfRig?.root?.position;
                if (pos) {
                  spawnAbilityVFX({ scene, position: pos, abilityId: a.id });
                  // School-aware cast burst (layered with element burst).
                  const aSchool = (a as { school?: string }).school?.toLowerCase();
                  if (!aSchool || aSchool !== 'physical') {
                    const inferred = inferSpellSchool(a.id);
                    const cls2 = (useNet.getState().players[useNet.getState().selfId ?? ''] as { classId?: string } | undefined)?.classId ?? '';
                    const school2 = (aSchool && aSchool !== 'physical'
                      ? aSchool
                      : (cls2 ? defaultSchoolForClass(cls2) : inferred)) as Parameters<typeof createSpellEffect>[1];
                    if (['fire','frost','holy','shadow','nature','arcane'].includes(school2)) {
                      createSpellEffect(scene, school2, pos, undefined, 'cast');
                    }
                  }
                  // R232q5h T1.4: ability-range gate — handles paladin-judgement.
                  const aRange = (a as { range?: number }).range ?? 0;
                  const aType = (a as { type?: string }).type ?? '';
                  const isRangedAbilityH = aRange >= 6 && aType === 'damage';
                  if (isRangedAbilityH && targetMobId) {
                    const tid = targetMobId;
                    spawnProjectileVFX({
                      scene,
                      from: pos.clone(),
                      toResolver: () => {
                        const m = useNet.getState().mobs[tid];
                        return m ? new Vector3(m.pos.x, 0, m.pos.z) : null;
                      },
                      abilityId: a.id,
                    });
                    // Impact burst on flight delay.
                    if (!aSchool || aSchool !== 'physical') {
                      const inferred2 = inferSpellSchool(a.id);
                      const cls3 = (useNet.getState().players[useNet.getState().selfId ?? ''] as { classId?: string } | undefined)?.classId ?? '';
                      const school3 = (aSchool && aSchool !== 'physical'
                        ? aSchool
                        : (cls3 ? defaultSchoolForClass(cls3) : inferred2)) as Parameters<typeof createSpellEffect>[1];
                      if (['fire','frost','holy','shadow','nature','arcane'].includes(school3)) {
                        setTimeout(() => {
                          const m = useNet.getState().mobs[tid];
                          if (!m) return;
                          createSpellEffect(scene, school3, new Vector3(m.pos.x, 0, m.pos.z), undefined, 'impact');
                        }, 380);
                      }
                    }
                  }
                }
              }
            }}
            title={(() => {
              // R232q5aS Wave 5: enrich tooltip with canonical Sirus 3.3.5a
              // DBC text when available (cooldown, range, mana cost, raw
              // description). Russian curated copy stays primary; DBC tail
              // appended in italics so the player still sees both.
              const dbc = dbcByCurated?.find((row) => row.id === a.id) ?? null;
              const head = notLearned
                ? `${a.name.ru} — заблокировано (откр. с ур. ${a.minLevel}). ПКМ — снять с панели.`
                : `${a.name.ru} — ${a.desc.ru}\nПКМ — снять с панели`;
              if (!dbc) return head;
              const cdSec2 = dbc.cooldownMs > 0 ? (dbc.cooldownMs / 1000).toFixed(1) + ' сек' : '—';
              const rangeM = dbc.range > 0 ? Math.round(dbc.range) + ' м' : 'ближний';
              const cost = dbc.cost > 0 ? `${dbc.cost} ресурс` : 'без затрат';
              const rankLine = dbc.dbcRank ? ` (${dbc.dbcRank})` : '';
              const nameLine = dbc.dbcName ? `\n[DBC] ${dbc.dbcName}${rankLine}` : '';
              const stats = `\nКД: ${cdSec2} · радиус: ${rangeM} · ${cost}`;
              const desc = dbc.dbcDescription ? `\n${dbc.dbcDescription}` : '';
              return `${head}${nameLine}${stats}${desc}`;
            })()}
            // R232q5aj: heraldic restyle — 44px desktop, 36px mobile, muted-gold border.
            // R232q5an: notLearned slots show locked state.
            className={`w-9 h-9 sm:w-11 sm:h-11 rounded-md flex items-center justify-center text-xl sm:text-2xl transition relative overflow-hidden ${
              onCd || notLearned ? 'cursor-not-allowed' : 'hover:scale-105 active:scale-95 hover:border-[#C9A96A]'
            }`}
            style={{
              background: 'linear-gradient(180deg, #161210 0%, #0B0907 100%)',
              border: `1.5px solid ${notLearned ? '#3A2818' : '#5C4520'}`,
              boxShadow: onCd
                ? 'inset 0 0 10px rgba(0,0,0,0.85), 0 0 6px rgba(201,169,106,0.45)'
                : 'inset 0 1px 0 rgba(201,169,106,0.10), 0 0 6px rgba(0,0,0,0.6)',
              opacity: notLearned ? 0.5 : 1,
            }}
            disabled={onCd || notLearned}
          >
            <span
              className="absolute inset-1 rounded-md overflow-hidden flex items-center justify-center"
              // R232q5aj: dim icon ~30% on cooldown via brightness filter.
              style={{ filter: onCd || notLearned ? 'grayscale(0.7) brightness(0.4)' : 'none' }}
            >
              <img
                src={`${import.meta.env.BASE_URL}icons/abilities/${a.id}.png`}
                alt={a.name.ru}
                className="w-full h-full object-cover"
                onError={(e) => {
                  // Fallback to emoji if PNG missing.
                  const fallback = document.createElement('span');
                  fallback.textContent = a.icon;
                  fallback.style.fontSize = '28px';
                  e.currentTarget.replaceWith(fallback);
                }}
              />
            </span>
            {onCd && (
              <>
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    height: `${sweepPct}%`,
                    top: 0,
                    background: 'rgba(11,9,7,0.65)',
                  }}
                />
                <span
                  className="absolute inset-0 flex items-center justify-center text-[13px] sm:text-[14px] font-bold pointer-events-none drop-shadow-[0_0_3px_rgba(0,0,0,0.95)]"
                  style={{ color: '#E8C77B', fontFamily: '"JetBrains Mono", ui-monospace, monospace' }}
                >
                  {cdSec.toFixed(cdSec < 10 ? 1 : 0)}
                </span>
              </>
            )}
            {/* R232q5b1 (Sirus visuals): GCD radial sweep — clockwise gold
                conic-gradient covering the post-cast 1.5s lockout window.
                Hidden when ability cooldown overlay is shown (vertical fill
                supersedes the GCD spinner so we don't double up). */}
            {gcdActive && (
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: `conic-gradient(from 0deg, rgba(201,169,106,0.42) 0deg, rgba(201,169,106,0.42) ${gcdSweepDeg}deg, rgba(0,0,0,0) ${gcdSweepDeg}deg)`,
                  borderRadius: 6,
                  mixBlendMode: 'screen',
                }}
              />
            )}
            <span
              className="text-[10px] absolute top-0 left-1 drop-shadow-[0_0_2px_rgba(0,0,0,0.95)]"
              style={{
                color: onCd ? '#E8C77B' : '#9C8E72',
                fontFamily: '"JetBrains Mono", ui-monospace, monospace',
                fontWeight: 600,
              }}
            >{slot.key}</span>
          </button>
        );
      })}
      {/* HP strip under the action bar — R232q5aj: heraldic frame */}
      {self && (
        <div
          className="absolute -top-3 left-0 right-0 h-1.5 rounded-full overflow-hidden"
          style={{ background: '#0B0907', border: '1px solid #3A2818' }}
        >
          <div
            className="h-full transition-all"
            style={{
              width: `${(self.hp / Math.max(1, self.hpMax)) * 100}%`,
              background: cls.colour,
              boxShadow: `0 0 6px ${cls.colour}80`,
            }}
          />
        </div>
      )}
    </div>
  );
}

// R93/R132/R228: расширенная карта русских имён мобов. Синхронизирована
// с RU_MOB_KIND в MobNameplates.tsx. R228: boar→'Бык' (mesh = Bull.gltf),
// spider→'Кактоид' (mesh = Cactoro.gltf). Mesh ↔ имя осмысленно совмещены.
const MOB_NAME_RU: Record<string, string> = {
  wolf: 'Волк', fox: 'Лис', deer: 'Олень', stag: 'Олень',
  bull: 'Бык', cow: 'Корова', husky: 'Хаски', horse: 'Лошадь',
  boar: 'Бык', spider: 'Кактоид',
  bandit: 'Разбойник', tribal: 'Дикарь', goblin: 'Гоблин',
  orc: 'Орк', orc_skull: 'Орк-Череп', yeti: 'Йети', golem: 'Голем',
  demon: 'Демон', blue_demon: 'Синий Демон', mushroom: 'Грибной Король',
  cactoro: 'Кактоид', dino: 'Динозавр', alien: 'Чужой',
  wraith: 'Призрак', ghost_skull: 'Череп-Призрак',
  dragonkin: 'Драконид', dragon_elite: 'Древний Дракон', 'world-boss': 'Пожиратель Бури',
  alpaca: 'Альпака', donkey: 'Осёл', horse_white: 'Белый конь', shibainu: 'Шиба-ину',
  bunny: 'Кролик', birb: 'Птица', frog: 'Лягушка', fish: 'Рыба', monkroose: 'Лосорог',
  alpaking: 'Альпака-Король', alpaking_elite: 'Древняя Альпака',
  armabee: 'Армопчела', armabee_elite: 'Древняя Армопчела', demon_flying: 'Летучий Демон',
};

function TargetFrame({ mob, distance, reach }: {
  mob: { kind: string; level: number; hp: number; hpMax: number };
  distance: number;
  reach: number;
}) {
  const hpPct = (mob.hp / Math.max(1, mob.hpMax)) * 100;
  const inRange = distance <= reach;
  const portrait =
    mob.kind === 'wolf' ? '🐺' :
    mob.kind === 'boar' ? '🐗' :
    mob.kind === 'bandit' ? '🗡️' :
    mob.kind === 'goblin' ? '👹' :
    mob.kind === 'spider' ? '🕷️' :
    mob.kind === 'wraith' ? '👻' :
    mob.kind === 'golem' ? '🗿' :
    mob.kind === 'dragonkin' ? '🐉' :
    mob.kind === 'world-boss' ? '💀' :
    '⚔️';
  const ruName = MOB_NAME_RU[mob.kind] ?? mob.kind;
  return (
    <div
      className="absolute top-3 pointer-events-none flex items-stretch gap-1 p-1 rounded transition-colors"
      style={{
        // R93: рядом с player frame (top-3 left-3 width 230 → начинаем с 248).
        left: 248,
        width: 230,
        background: 'linear-gradient(180deg, rgba(20,15,8,0.92), rgba(8,5,3,0.92))',
        border: `2px solid ${inRange ? '#B45309' : '#FB923C'}`,
        boxShadow: inRange ? '0 0 10px rgba(0,0,0,0.6)' : '0 0 14px rgba(251,146,60,0.6)',
        animation: inRange ? 'none' : 'oor-pulse 1s ease-in-out infinite',
        fontFamily: 'system-ui',
      }}
    >
      <style>{`@keyframes oor-pulse { 0%,100%{box-shadow:0 0 10px rgba(251,146,60,0.4)} 50%{box-shadow:0 0 20px rgba(251,146,60,0.95)} }`}</style>
      <div
        className="w-14 h-14 rounded-sm flex-shrink-0 flex items-center justify-center text-2xl relative"
        style={{
          background: 'linear-gradient(180deg, rgba(220,38,38,0.20) 0%, rgba(0,0,0,0.85) 100%)',
          border: '1.5px solid #DC2626',
        }}
      >
        {portrait}
        <div
          className="absolute bottom-0 right-0 px-1 text-[10px] font-bold leading-none"
          style={{ background: 'rgba(0,0,0,0.85)', color: '#FCA5A5', fontFamily: 'Cinzel, serif', borderTopLeftRadius: 3 }}
        >
          {mob.level}
        </div>
      </div>
      <div className="flex flex-col gap-1 flex-1 min-w-0 justify-center">
        <div className="text-[12px] font-bold text-aiwari-fg truncate leading-none" style={{ fontFamily: 'Cinzel, serif' }}>
          {ruName}
        </div>
        <div className="relative h-4 rounded-sm overflow-hidden" style={{ background: '#1a0807', border: '1px solid #3a0d0a' }}>
          <div
            className="h-full transition-[width] duration-200"
            style={{
              width: `${hpPct}%`,
              background: 'linear-gradient(180deg, #F87171 0%, #DC2626 50%, #991B1B 100%)',
            }}
          />
          <div className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white drop-shadow-[0_0_2px_rgba(0,0,0,0.95)] font-mono leading-none">
            {mob.hp.toLocaleString()} / {mob.hpMax.toLocaleString()}
          </div>
        </div>
        <div className="text-[9px] font-mono leading-none" style={{ color: inRange ? '#34D399' : '#FB923C', fontWeight: 700 }}>
          {inRange ? `${distance.toFixed(0)}м · в радиусе` : `${distance.toFixed(0)}м · слишком далеко (≤${reach}м)`}
        </div>
      </div>
    </div>
  );
}

// R226: target frame для другого игрока — показывается мгновенно при клике.
// Цвет рамки = friendly (зелёный) или hostile (красный). Без attack-кнопки —
// атака ability через панель abilities при выделенной цели.
function PlayerTargetFrame({ player, distance, reach, hostile, onClose }: {
  player: { id: string; name?: string; level?: number; hp?: number; hpMax?: number; classId?: string; side?: string };
  distance: number;
  reach: number;
  hostile: boolean;
  onClose: () => void;
}) {
  const hp = player.hp ?? 1;
  const hpMax = Math.max(1, player.hpMax ?? 1);
  const hpPct = (hp / hpMax) * 100;
  const inRange = distance <= reach;
  const portrait =
    player.classId === 'warrior' ? '⚔️' :
    player.classId === 'paladin' ? '🛡️' :
    player.classId === 'mage' ? '🔮' :
    player.classId === 'cleric' ? '✨' :
    player.classId === 'ranger' ? '🏹' :
    player.classId === 'rogue' ? '🗡️' :
    player.classId === 'druid' ? '🌿' :
    player.classId === 'shaman' ? '⚡' :
    player.classId === 'warlock' ? '👁' :
    '🧝';
  const borderColor = hostile ? '#DC2626' : '#34D399';
  const labelText = hostile ? 'враг' : 'союзник';
  const labelColor = hostile ? '#FCA5A5' : '#86EFAC';
  return (
    <div
      className="absolute pointer-events-auto flex items-stretch gap-1 p-1 rounded transition-colors"
      style={{
        // Под mob target frame чтобы не накладывались.
        top: 76, left: 248, width: 230,
        background: 'linear-gradient(180deg, rgba(20,15,8,0.92), rgba(8,5,3,0.92))',
        border: `2px solid ${borderColor}`,
        boxShadow: `0 0 10px ${borderColor}55`,
        fontFamily: 'system-ui',
      }}
    >
      <div
        className="w-14 h-14 rounded-sm flex-shrink-0 flex items-center justify-center text-2xl relative"
        style={{
          background: `linear-gradient(180deg, ${borderColor}33 0%, rgba(0,0,0,0.85) 100%)`,
          border: `1.5px solid ${borderColor}`,
        }}
      >
        {portrait}
        {player.level != null && (
          <div
            className="absolute bottom-0 right-0 px-1 text-[10px] font-bold leading-none"
            style={{ background: 'rgba(0,0,0,0.85)', color: labelColor, fontFamily: 'Cinzel, serif', borderTopLeftRadius: 3 }}
          >
            {player.level}
          </div>
        )}
      </div>
      <div className="flex flex-col gap-1 flex-1 min-w-0 justify-center">
        <div className="flex items-center gap-1">
          <div className="text-[12px] font-bold text-aiwari-fg truncate leading-none flex-1" style={{ fontFamily: 'Cinzel, serif' }}>
            {player.name ?? 'Странник'}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-aiwari-fgMuted hover:text-aiwari-fg text-xs leading-none px-1"
            title="Снять выделение"
          >×</button>
        </div>
        <div className="relative h-4 rounded-sm overflow-hidden" style={{ background: '#1a0807', border: '1px solid #3a0d0a' }}>
          <div
            className="h-full transition-[width] duration-200"
            style={{
              width: `${hpPct}%`,
              background: hostile
                ? 'linear-gradient(180deg, #F87171 0%, #DC2626 50%, #991B1B 100%)'
                : 'linear-gradient(180deg, #34D399 0%, #16A34A 50%, #14532D 100%)',
            }}
          />
          <div className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white drop-shadow-[0_0_2px_rgba(0,0,0,0.95)] font-mono leading-none">
            {hp.toLocaleString()} / {hpMax.toLocaleString()}
          </div>
        </div>
        <div className="text-[9px] font-mono leading-none" style={{ color: inRange ? '#34D399' : '#FB923C', fontWeight: 700 }}>
          {labelText} · {inRange ? `${distance.toFixed(0)}м · в радиусе` : `${distance.toFixed(0)}м · далеко (≤${reach}м)`}
        </div>
      </div>
    </div>
  );
}

function CastBar() {
  const [, setTick] = useState(0);
  const activeCast = useAbilityCD((s) => s.activeCast);
  const instantPulse = useAbilityCD((s) => s.instantPulse);
  useEffect(() => {
    if (!activeCast && !instantPulse) return;
    const id = window.setInterval(() => setTick((t) => (t + 1) & 0xffff), 50);
    return () => window.clearInterval(id);
  }, [activeCast, instantPulse]);
  // R232q5h T2.1: instant-cast pulse — quick yellow flash 280ms with ability name.
  if (!activeCast && instantPulse) {
    const age = performance.now() - instantPulse.firedAt;
    const opacity = Math.max(0, 1 - age / 280);
    return (
      <div
        className="absolute bottom-32 left-1/2 -translate-x-1/2 w-72 h-5 bg-black/65 border border-aiwari-bolt/80 rounded-md overflow-hidden pointer-events-none z-40"
        style={{ opacity }}
      >
        <div
          className="h-full"
          style={{
            width: '100%',
            background: 'linear-gradient(90deg,#FCD34D 0%,#F59E0B 100%)',
            boxShadow: 'inset 0 0 12px rgba(252,211,77,0.85)',
          }}
        />
        <div className="absolute inset-0 flex items-center justify-center text-[11px] font-bold text-white drop-shadow-[0_0_3px_rgba(0,0,0,0.95)] font-mono">
          {instantPulse.name} · мгновенно
        </div>
      </div>
    );
  }
  if (!activeCast) return null;
  const elapsed = performance.now() - activeCast.startedAt;
  const pct = Math.min(100, (elapsed / activeCast.durationMs) * 100);
  return (
    <div className="absolute bottom-32 left-1/2 -translate-x-1/2 w-72 h-5 bg-black/65 border border-aiwari-bolt/80 rounded-md overflow-hidden pointer-events-none z-40">
      <div
        className="h-full transition-none"
        style={{
          width: `${pct}%`,
          background: 'linear-gradient(90deg,#FBBF24 0%,#F97316 100%)',
          boxShadow: 'inset 0 0 8px rgba(251,191,36,0.5)',
        }}
      />
      <div className="absolute inset-0 flex items-center justify-center text-[11px] font-bold text-white drop-shadow-[0_0_3px_rgba(0,0,0,0.95)] font-mono">
        {activeCast.name} · {((activeCast.durationMs - elapsed) / 1000).toFixed(1)}s
      </div>
    </div>
  );
}

// R232q5db: Class trainers spawn в столицах. Каждый trainer = realistic
// Sketchfab rig (Northcliffe Footman / Orc Grunt) с idle anim. Master mesh
// cache внутри Babylon — все 8 тренеров одной фракции делят 1 GLB parse.
function addClassTrainers(scene: import('@babylonjs/core').Scene, parent: TransformNode) {
  void import('../data/class-trainers.js').then(({ CLASS_TRAINERS }) => {
    void StandardMaterial;
    void parent;
    void Promise.all([
      import('../scene/RaceModelMap.js'),
      import('../scene/RealisticCharacter.js'),
    ]).then(([rmm, rc]) => {
      for (const t of CLASS_TRAINERS) {
        const x = t.pos.x, z = t.pos.z;
        const race = (t.faction === 'alliance' ? 'human' : 'orc') as import('../scene/RaceModelMap.js').Race;
        const pick = rmm.getRaceModelGlb(race, 'male');
        void (async () => {
          let loaded = false;
          // R232q5b0: iterate Sirus-only chain (procedural sentinel + content-type guard).
          for (const step of pick.chain) {
            try {
              let okToLoad = false;
              if (step.glb === rmm.AIWARI_CHAR_PLACEHOLDER) {
                okToLoad = true;
              } else {
                const head = await fetch(`${step.baseDir}${step.glb}`, { method: 'HEAD' });
                if (!head.ok) continue;
                const ct = (head.headers.get('content-type') ?? '').toLowerCase();
                if (ct.includes('text/html')) continue;
                okToLoad = true;
              }
              if (okToLoad) {
                const rig = await rc.loadRealisticCharacter(scene, { glb: step.glb, baseDir: step.baseDir, scale: pick.scale, targetTallest: pick.targetHeight, raceId: race, gender: 'male' });
                rig.root.position = new Vector3(x, 0, z);
                rig.root.parent = parent;
                for (const m of rig.root.getChildMeshes(false)) {
                  m.metadata = { trainerId: t.id, classId: t.classId, trainerName: t.name.ru, trainerTitle: t.title.ru };
                  m.isPickable = true;
                }
                NPC_RIG_REGISTRY.push({ root: rig.root, rig: rig as unknown as KayKitRig, x, z, enabled: true });
                loaded = true; break;
              }
            } catch (e) { console.error('[aiwari] rig chain step failed (trainer)', step, e); }
          }
          if (!loaded) {
            // AIWARI character set ещё не извлечён для этой расы — fallback
            // через KayKitCharacter обёртку (она тоже грузит из Sirus namespace).
            void loadKayKitCharacter(scene, { classId: t.classId, raceId: race }).then((rig) => {
              rig.root.position = new Vector3(x, 0, z);
              rig.root.parent = parent;
              for (const m of rig.root.getChildMeshes(false)) {
                m.metadata = { trainerId: t.id, classId: t.classId, trainerName: t.name.ru, trainerTitle: t.title.ru };
                m.isPickable = true;
              }
              NPC_RIG_REGISTRY.push({ root: rig.root, rig, x, z, enabled: true });
            }).catch(() => { /* nothing visible — agent batch не доехал */ });
          }
        })();
      }
    }).catch(() => { /* keep KayKit fallback */ });
  }).catch(() => { /* class-trainers not available */ });
}

function addQuestGivers(scene: import('@babylonjs/core').Scene, parent: TransformNode) {
  // R230 REMOVED: torch-pole + flame-sphere "палки" убраны. У NPC теперь есть
  // quest-indicator (! ? над головой) — этого достаточно как в WotLK 3.3.5a.
  void StandardMaterial;
  // KayKit Adventurer per quest giver — same skeleton/scale as player.
  const giverClasses: Record<string, string> = {
    'old-fisherman':  'warrior',
    'master-smith':   'warrior',
    'caravan-master': 'warrior',
    'storm-oracle':   'mage',
    'king-stormwind': 'paladin',
    'sw-auctioneer':  'mage',
    'thrall':         'warrior',
    'org-auctioneer': 'rogue',
  };
  const givers = new Map<string, { x: number; z: number; name: string }>();
  for (const q of QUESTS) {
    if (!givers.has(q.giver)) {
      givers.set(q.giver, { x: q.giverPos.x, z: q.giverPos.z, name: q.giverName.ru });
    }
  }
  // R232q5i: квестодатели грузятся из Sirus AIWARI NPC namespace.
  // Каждый class маппится в одну из 6 AIWARI рас (через RaceModelMap) +
  // gender. Если конкретный rig ещё не извлечён — fallback в loading
  // placeholder.
  const npcLoadRealistic = async (cls: string) => {
    const rc = await import('../scene/RealisticCharacter.js');
    const rmm = await import('../scene/RaceModelMap.js');
    // Heuristic class → race: warrior/paladin → felclan, rogue → frostkin,
    // mage/cleric → mistborn, shaman/druid → woodkin.
    const raceByClass: Record<string, import('../scene/RaceModelMap.js').Race> = {
      warrior: 'human', paladin: 'human', rogue: 'undead',
      mage: 'night-elf', cleric: 'night-elf', druid: 'tauren', shaman: 'orc',
    };
    const race = raceByClass[cls] ?? 'human';
    const pick = rmm.getRaceModelGlb(race, 'male');
    // R232q5b0: iterate Sirus-only chain (procedural sentinel + content-type guard).
    for (const step of pick.chain) {
      try {
        if (step.glb === rmm.AIWARI_CHAR_PLACEHOLDER) {
          return await rc.loadRealisticCharacter(scene, {
            glb: step.glb,
            baseDir: step.baseDir,
            targetTallest: pick.targetHeight,
            raceId: race,
            gender: 'male',
          });
        }
        const probe = await fetch(`${step.baseDir}${step.glb}`, { method: 'HEAD' });
        if (!probe.ok) continue;
        const ct = (probe.headers.get('content-type') ?? '').toLowerCase();
        if (ct.includes('text/html')) continue;
        return await rc.loadRealisticCharacter(scene, {
          glb: step.glb,
          baseDir: step.baseDir,
          targetTallest: pick.targetHeight,
          raceId: race,
          gender: 'male',
        });
      } catch (e) { console.error('[aiwari] rig chain step failed (npc)', step, e); }
    }
    // Defensive net (chain already terminates with sentinel).
    return await rc.loadRealisticCharacter(scene, {
      glb: pick.placeholder,
      baseDir: rmm.AIWARI_CHAR_BASE,
      targetTallest: pick.targetHeight,
      raceId: race,
      gender: 'male',
    });
  };
  for (const [giverId, g] of givers) {
    const cls = giverClasses[giverId] ?? 'warrior';
    void npcLoadRealistic(cls).then((rig) => {
      rig.root.position = new Vector3(g.x, 0, g.z);
      rig.root.parent = parent;
      for (const m of rig.root.getChildMeshes(false)) {
        m.metadata = { questGiverId: giverId, leaderName: g.name };
        m.isPickable = true;
      }
      NPC_RIG_REGISTRY.push({ root: rig.root, rig: rig as unknown as KayKitRig, x: g.x, z: g.z, enabled: true });
    }).catch(() => { /* loader failed entirely */ });
  }
  // R232q5g: lazy-spawn vanilla-quest-adapter givers. 30 NPCs across all
  // WoW zones — spawning all at boot tanks FPS to 17. Now we spawn only
  // when the player approaches within 250m, periodically polled.
  void import('../data/vanilla-quest-adapter.js').then(({ VQ_GIVERS }) => {
    const spawned = new Set<string>();
    const SPAWN_RADIUS = 250;
    const SPAWN_RADIUS_SQ = SPAWN_RADIUS * SPAWN_RADIUS;
    const tryNearbySpawn = () => {
      // Use the global self-rig handle so this works inside addQuestGivers
      // (no `selfRigBox` closure variable here). Fall back to (0,0) until
      // the self-rig is created so the loop simply spawns nothing yet.
      const selfRigGlobal = (window as unknown as { __aiwariSelfRig?: { root?: { position: { x: number; z: number } } } }).__aiwariSelfRig;
      const sx = selfRigGlobal?.root?.position?.x ?? 0;
      const sz = selfRigGlobal?.root?.position?.z ?? 0;
      for (const g of VQ_GIVERS) {
        if (spawned.has(g.giverId)) continue;
        const dx = g.pos.x - sx;
        const dz = g.pos.z - sz;
        if (dx * dx + dz * dz > SPAWN_RADIUS_SQ) continue;
        spawned.add(g.giverId);
        const cls = g.side === 'tidal' ? 'paladin' : g.side === 'anchorless' ? 'shaman' : 'mage';
        void npcLoadRealistic(cls).then((rig) => {
          rig.root.position = new Vector3(g.pos.x, 0, g.pos.z);
          rig.root.parent = parent;
          for (const m of rig.root.getChildMeshes(false)) {
            m.metadata = { questGiverId: g.giverId, leaderName: g.nameRu };
            m.isPickable = true;
          }
          NPC_RIG_REGISTRY.push({ root: rig.root, rig: rig as unknown as KayKitRig, x: g.pos.x, z: g.pos.z, enabled: true });
        }).catch(() => { spawned.delete(g.giverId); /* retry next tick */ });
      }
    };
    // Initial check + every 2 seconds.
    tryNearbySpawn();
    setInterval(tryNearbySpawn, 2000);
  }).catch(() => { /* adapter not available */ });
}

// R173: Resource nodes — mining/herb spawn-points в открытом мире.
// Каждая нода = маленький pillar с цветным glowing top + iconic mesh.
// При клике (TODO Phase 2) выдаст материал в инвентарь.
function addResourceNodes(scene: import('@babylonjs/core').Scene, parent: TransformNode) {
  void Promise.all([
    import('../data/resource-nodes.js'),
    import('../data/professions.js'),
  ]).then(([{ RESOURCE_NODES }, { MATERIALS }]) => {
    for (const node of RESOURCE_NODES) {
      const yieldsRu = MATERIALS.find((m) => m.id === node.yields)?.name.ru ?? node.yields;
      const mat = new StandardMaterial(`res-mat-${node.id}`, scene);
      const c = Color3.FromHexString(node.colour.startsWith('#') ? node.colour : '#FFFFFF');
      mat.emissiveColor = c.scale(0.6);
      mat.diffuseColor = c.scale(0.8);
      for (let i = 0; i < node.spawns.length; i++) {
        const s = node.spawns[i];
        if (!s) continue;
        // Slight per-instance jitter so они не выровнены в гриде.
        const jx = ((i * 17 + node.id.length * 7) % 9) - 4;
        const jz = ((i * 23 + node.id.length * 11) % 9) - 4;
        const px = s.x + jx * 0.6;
        const pz = s.z + jz * 0.6;
        if (node.kind === 'mining') {
          // Mining = низкий каменный блок с цветным "шом".
          const rock = MeshBuilder.CreateBox(`res-${node.id}-${i}`, { width: 1.2, height: 0.7, depth: 1.2 }, scene);
          rock.position = new Vector3(px, 0.35, pz);
          rock.material = mat;
          rock.parent = parent;
          rock.metadata = { resourceNodeId: node.id, kind: 'mining', yields: node.yields, yieldsRu, skillRequired: node.skillRequired };
          rock.isPickable = true;
          // Glowing crystal top.
          const top = MeshBuilder.CreatePolyhedron(`res-top-${node.id}-${i}`, { type: 1, size: 0.22 }, scene);
          top.position = new Vector3(px, 0.85, pz);
          top.material = mat;
          top.parent = parent;
        } else if (node.kind === 'fishing') {
          // R205: Fishing pool — flat blue shimmering disc на воде.
          const pool = MeshBuilder.CreateDisc(`res-${node.id}-${i}`, { radius: 1.5, tessellation: 16 }, scene);
          pool.position = new Vector3(px, 0.05, pz);
          pool.rotation.x = Math.PI / 2;
          pool.material = mat;
          pool.parent = parent;
          pool.metadata = { resourceNodeId: node.id, kind: 'fishing', yields: node.yields, yieldsRu, skillRequired: node.skillRequired };
          pool.isPickable = true;
          // Маленький bubble-pulse.
          const bubble = MeshBuilder.CreateSphere(`res-${node.id}-bubble-${i}`, { diameter: 0.25, segments: 6 }, scene);
          bubble.position = new Vector3(px, 0.2, pz);
          bubble.material = mat;
          bubble.parent = parent;
        } else {
          // Herb = небольшой "куст" — sphere + thin pillar.
          const stem = MeshBuilder.CreateCylinder(`res-${node.id}-stem-${i}`, { diameter: 0.06, height: 0.3 }, scene);
          stem.position = new Vector3(px, 0.15, pz);
          stem.material = mat;
          stem.parent = parent;
          const flower = MeshBuilder.CreateSphere(`res-${node.id}-flower-${i}`, { diameter: 0.32, segments: 6 }, scene);
          flower.position = new Vector3(px, 0.4, pz);
          flower.material = mat;
          flower.parent = parent;
          flower.metadata = { resourceNodeId: node.id, kind: 'herbalism', yields: node.yields, yieldsRu, skillRequired: node.skillRequired };
          flower.isPickable = true;
        }
      }
    }
  }).catch(() => { /* resource-nodes data unavailable */ });
}

// R173: Profession trainers — kayKit NPC с golden beacon в каждой столице.
// Расставлены по 7 столицам. При клике (TODO) даст возможность learn craft.
function addProfessionTrainers(scene: import('@babylonjs/core').Scene, parent: TransformNode) {
  // Городские координаты — рядом с центрами столиц, но смещены, чтобы не
  // налегать на trainers/auctioneers.
  const trainers: Array<{ id: string; profession: string; pos: { x: number; z: number }; classProxy: string }> = [
    // Lionhold (Alliance) — все основные.
    { id: 'sw-mining',     profession: 'mining',          pos: { x: -1525, z: 130  }, classProxy: 'warrior' },
    { id: 'sw-blacksmith', profession: 'smithing',   pos: { x: -1530, z: 145  }, classProxy: 'warrior' },
    { id: 'sw-tailor',     profession: 'tailoring',       pos: { x: -1515, z: 160  }, classProxy: 'mage' },
    { id: 'sw-alchemist',  profession: 'alchemy',         pos: { x: -1490, z: 145  }, classProxy: 'mage' },
    { id: 'sw-herbalism',  profession: 'herbalism',       pos: { x: -1500, z: 175  }, classProxy: 'priest' },
    // Wartus Citadel (Horde).
    { id: 'org-mining',     profession: 'mining',         pos: { x: 1530, z: 30   }, classProxy: 'warrior' },
    { id: 'org-blacksmith', profession: 'smithing',  pos: { x: 1540, z: 50   }, classProxy: 'warrior' },
    { id: 'org-leather',    profession: 'leatherworking', pos: { x: 1525, z: 70   }, classProxy: 'rogue' },
    { id: 'org-skinning',   profession: 'skinning',       pos: { x: 1510, z: 60   }, classProxy: 'ranger' },
    { id: 'org-cooking',    profession: 'cooking',        pos: { x: 1500, z: 40   }, classProxy: 'warrior' },
    // IronPyre.
    { id: 'if-mining',      profession: 'mining',         pos: { x: -1170, z: 380 }, classProxy: 'warrior' },
    { id: 'if-engineering', profession: 'engineering',    pos: { x: -1155, z: 395 }, classProxy: 'warrior' },
    // Stormcliff.
    { id: 'tb-leather',     profession: 'leatherworking', pos: { x: 745,  z: 1185 }, classProxy: 'ranger' },
    { id: 'tb-skinning',    profession: 'skinning',       pos: { x: 730,  z: 1170 }, classProxy: 'ranger' },
    // Crypthold.
    { id: 'uc-alchemy',     profession: 'alchemy',        pos: { x: 1185, z: -1180 }, classProxy: 'mage' },
    { id: 'uc-tailor',      profession: 'tailoring',      pos: { x: 1170, z: -1165 }, classProxy: 'mage' },
    // Moonwillow.
    { id: 'dn-herbalism',   profession: 'herbalism',      pos: { x: -1465, z: 1370 }, classProxy: 'priest' },
    { id: 'dn-alchemy',     profession: 'alchemy',        pos: { x: -1450, z: 1385 }, classProxy: 'priest' },
    // Silvermoon (placeholder — рядом с UC).
    { id: 'sm-enchanting',  profession: 'enchanting',     pos: { x: 1230, z: -1100 }, classProxy: 'mage' },
    { id: 'sm-tailor',      profession: 'tailoring',      pos: { x: 1245, z: -1085 }, classProxy: 'mage' },
    // R205: Fishing trainers у воды.
    { id: 'fish-bootybay',  profession: 'fishing',        pos: { x: -715, z: -1480 }, classProxy: 'rogue' },
    { id: 'fish-menethil',  profession: 'fishing',        pos: { x: -1305, z: 605 }, classProxy: 'warrior' },
    { id: 'fish-ratchet',   profession: 'fishing',        pos: { x: 805,  z: 1205 }, classProxy: 'rogue' },
    { id: 'fish-stormwind', profession: 'fishing',        pos: { x: -1490, z: 200 }, classProxy: 'priest' },
  ];
  // R230 REMOVED: золотые beacon-кольца у профессиональных тренеров убраны.
  void StandardMaterial; void parent;
  for (const t of trainers) {
    void loadKayKitCharacter(scene, { classId: t.classProxy }).then((rig) => {
      rig.root.position = new Vector3(t.pos.x, 0, t.pos.z);
      rig.root.parent = parent;
      for (const m of rig.root.getChildMeshes(false)) {
        m.metadata = { professionTrainerId: t.id, profession: t.profession };
        m.isPickable = true;
      }
      NPC_RIG_REGISTRY.push({ root: rig.root, rig, x: t.pos.x, z: t.pos.z, enabled: true });
    }).catch(() => { /* keep beacon only */ });
  }
}

// R232q5o: Dungeon portals — glowing arch markers at each dungeon entrance.
// Click → opens DungeonFinder filtered to that dungeon (или сразу enter).
function addDungeonPortals(scene: import('@babylonjs/core').Scene, parent: TransformNode) {
  // R232q86zi0: legacy glowing arches retired for the canon pass — they sat
  // in wrong (pre-1:1) spots and cost 72 always-on meshes. Dungeon entry
  // returns via the real WMO entrances later.
  void scene; void parent;
  return;
  // eslint-disable-next-line no-unreachable
  void Promise.all([
    import('../data/dungeon-portals.js'),
    import('../data/dungeons.js'),
  ]).then(([{ DUNGEON_PORTALS }, { DUNGEONS }]) => {
    const archMeshes: import('@babylonjs/core').AbstractMesh[] = [];
    const innerMeshes: import('@babylonjs/core').AbstractMesh[] = [];
    for (const p of DUNGEON_PORTALS) {
      const blurb = DUNGEONS.find((d) => d.id === p.dungeonId)?.blurb.ru;
      const meta = { dungeonPortalId: p.dungeonId, ru: p.ru, level: p.level, blurb };
      const tint = p.colour;
      const mat = new StandardMaterial(`dportal-mat-${p.dungeonId}`, scene);
      mat.emissiveColor = Color3.FromHexString(tint);
      mat.diffuseColor = Color3.FromHexString(tint).scale(0.6);
      mat.alpha = 0.85;
      // Vertical torus-arch — recognisable as portal.
      const arch = MeshBuilder.CreateTorus(`dportal-arch-${p.dungeonId}`, { diameter: 5, thickness: 0.5, tessellation: 24 }, scene);
      arch.position = new Vector3(p.pos.x, 2.4, p.pos.z);
      arch.rotation.x = Math.PI / 2;
      arch.material = mat;
      arch.parent = parent;
      arch.metadata = meta;
      arch.isPickable = true;
      archMeshes.push(arch);
      // Inner glow plane.
      const inner = MeshBuilder.CreateDisc(`dportal-inner-${p.dungeonId}`, { radius: 1.8, tessellation: 24 }, scene);
      inner.position = new Vector3(p.pos.x, 2.4, p.pos.z);
      inner.rotation.x = Math.PI / 2;
      const innerMat = new StandardMaterial(`dportal-inner-mat-${p.dungeonId}`, scene);
      innerMat.emissiveColor = Color3.FromHexString(tint).scale(1.2);
      innerMat.diffuseColor = new Color3(0, 0, 0);
      innerMat.alpha = 0.4;
      inner.material = innerMat;
      inner.parent = parent;
      inner.metadata = meta;
      inner.isPickable = true;
      innerMeshes.push(inner);
      // Wider invisible click target.
      const clickHit = MeshBuilder.CreateCylinder(`dportal-hit-${p.dungeonId}`, { diameter: 6, height: 5 }, scene);
      clickHit.position = new Vector3(p.pos.x, 2.5, p.pos.z);
      clickHit.visibility = 0.0;
      clickHit.parent = parent;
      clickHit.metadata = meta;
      clickHit.isPickable = true;
    }
    // R232q5p: slow rotation вокруг Y-оси для visual life. Inner crystal
    // вращается в обратную сторону. Пульс scaling — лёгкое breathing.
    let t0 = performance.now();
    scene.onBeforeRenderObservable.add(() => {
      const dt = (performance.now() - t0) / 1000;
      t0 = performance.now();
      for (const m of archMeshes) {
        m.rotation.y += dt * 0.3;
      }
      for (const m of innerMeshes) {
        m.rotation.y -= dt * 0.5;
        const pulse = 1 + 0.08 * Math.sin(performance.now() * 0.003);
        m.scaling.set(pulse, pulse, pulse);
      }
    });
  }).catch(() => { /* portal data unavailable */ });
}

// R183: Flight Masters — синяя/красная/жёлтая колонна по фракции в каждой
// flight node позиции. Click → 'aiwari-open-flight-master' event → FlightMaster dialog.
function addFlightMasters(scene: import('@babylonjs/core').Scene, parent: TransformNode) {
  // R232q86zi0: legacy pole markers retired (171 always-on meshes at
  // pre-canon coords). Real flight masters are promoted NPCs with dialogs.
  void scene; void parent;
  return;
  // eslint-disable-next-line no-unreachable
  void import('../data/flight-paths.js').then(({ FLIGHT_NODES }) => {
    const matAlliance = new StandardMaterial('fp-mat-alliance', scene);
    matAlliance.emissiveColor = new Color3(0.3, 0.55, 0.95);
    matAlliance.diffuseColor = new Color3(0.2, 0.4, 0.7);
    const matHorde = new StandardMaterial('fp-mat-horde', scene);
    matHorde.emissiveColor = new Color3(0.95, 0.25, 0.20);
    matHorde.diffuseColor = new Color3(0.7, 0.2, 0.15);
    const matNeutral = new StandardMaterial('fp-mat-neutral', scene);
    matNeutral.emissiveColor = new Color3(0.95, 0.75, 0.20);
    matNeutral.diffuseColor = new Color3(0.7, 0.55, 0.20);
    for (const n of FLIGHT_NODES) {
      const mat = n.faction === 'alliance' ? matAlliance : n.faction === 'horde' ? matHorde : matNeutral;
      // High vertical pole — заметна с большого расстояния.
      const pole = MeshBuilder.CreateCylinder(`fp-pole-${n.id}`, { diameter: 0.18, height: 4.0 }, scene);
      pole.position = new Vector3(n.pos.x + 1.2, 2.0, n.pos.z);
      pole.material = mat;
      pole.parent = parent;
      // Wing-like top (proxy for griffin perch).
      const top = MeshBuilder.CreateBox(`fp-top-${n.id}`, { width: 0.8, height: 0.3, depth: 0.8 }, scene);
      top.position = new Vector3(n.pos.x + 1.2, 4.1, n.pos.z);
      top.material = mat;
      top.parent = parent;
      top.metadata = { flightMasterId: n.id, fpName: n.name };
      top.isPickable = true;
      // Click-target — wider invisible cylinder around the pole так easier to click.
      const clickHit = MeshBuilder.CreateCylinder(`fp-hit-${n.id}`, { diameter: 1.6, height: 4.5 }, scene);
      clickHit.position = new Vector3(n.pos.x + 1.2, 2.2, n.pos.z);
      clickHit.visibility = 0.0;
      clickHit.parent = parent;
      clickHit.metadata = { flightMasterId: n.id, fpName: n.name };
      clickHit.isPickable = true;
    }
  }).catch(() => { /* flight-paths data unavailable */ });
}

// R185: General goods vendors — расставлены в каждой столице. Click →
// открывает VendorShop (ивент 'aiwari-open-vendor'). Reagents/ammo/потоны.
function addGeneralGoodsVendors(scene: import('@babylonjs/core').Scene, parent: TransformNode) {
  // 7 столиц + neutral hubs.
  const vendors: Array<{ id: string; vendorId: string; pos: { x: number; z: number }; faction: 'alliance' | 'horde' | 'neutral' }> = [
    // Alliance
    { id: 'gen-vendor-stormwind',  vendorId: 'merchant-rooted',     pos: { x: -1490, z: 110  }, faction: 'alliance' },
    { id: 'gen-vendor-ironforge',  vendorId: 'merchant-rooted',     pos: { x: -1180, z: 390  }, faction: 'alliance' },
    { id: 'gen-vendor-darnassus',  vendorId: 'merchant-rooted',     pos: { x: -1455, z: 1380 }, faction: 'alliance' },
    // Horde
    { id: 'gen-vendor-orgrimmar',  vendorId: 'merchant-tidal',      pos: { x: 1490, z: 10    }, faction: 'horde' },
    { id: 'gen-vendor-thunder',    vendorId: 'merchant-tidal',      pos: { x: 755,  z: 1190  }, faction: 'horde' },
    { id: 'gen-vendor-undercity',  vendorId: 'merchant-tidal',      pos: { x: 1175, z: -1175 }, faction: 'horde' },
    // Neutral hubs
    { id: 'gen-vendor-bootybay',   vendorId: 'merchant-anchorless', pos: { x: -700, z: -1490 }, faction: 'neutral' },
    { id: 'gen-vendor-gadgetzan',  vendorId: 'merchant-anchorless', pos: { x: 510,  z: -1090 }, faction: 'neutral' },
    { id: 'gen-vendor-everlook',   vendorId: 'merchant-anchorless', pos: { x: 1290, z: 1310  }, faction: 'neutral' },
  ];
  const matAlliance = new StandardMaterial('vend-mat-alliance', scene);
  matAlliance.emissiveColor = new Color3(0.20, 0.55, 0.85);
  matAlliance.diffuseColor = new Color3(0.15, 0.40, 0.65);
  const matHorde = new StandardMaterial('vend-mat-horde', scene);
  matHorde.emissiveColor = new Color3(0.85, 0.20, 0.15);
  matHorde.diffuseColor = new Color3(0.65, 0.15, 0.10);
  const matNeutral = new StandardMaterial('vend-mat-neutral', scene);
  matNeutral.emissiveColor = new Color3(0.85, 0.65, 0.20);
  matNeutral.diffuseColor = new Color3(0.65, 0.50, 0.15);
  for (const v of vendors) {
    const mat = v.faction === 'alliance' ? matAlliance : v.faction === 'horde' ? matHorde : matNeutral;
    // Box stall — proxy для merchant table.
    const stall = MeshBuilder.CreateBox(`vend-stall-${v.id}`, { width: 1.4, height: 0.9, depth: 1.0 }, scene);
    stall.position = new Vector3(v.pos.x + 0.8, 0.45, v.pos.z);
    stall.material = mat;
    stall.parent = parent;
    // Bag-shape (sphere) — visible token of "goods".
    const bag = MeshBuilder.CreateSphere(`vend-bag-${v.id}`, { diameter: 0.5, segments: 8 }, scene);
    bag.position = new Vector3(v.pos.x + 0.8, 1.15, v.pos.z);
    bag.material = mat;
    bag.parent = parent;
    bag.metadata = { vendorId: v.vendorId };
    bag.isPickable = true;
    // Click-target — wider invisible cylinder.
    const clickHit = MeshBuilder.CreateCylinder(`vend-hit-${v.id}`, { diameter: 1.6, height: 1.6 }, scene);
    clickHit.position = new Vector3(v.pos.x + 0.8, 0.8, v.pos.z);
    clickHit.visibility = 0.0;
    clickHit.parent = parent;
    clickHit.metadata = { vendorId: v.vendorId };
    clickHit.isPickable = true;
  }
}

// R186: Innkeepers — WotLK 3.3.5a таверны в каждой столице + крупных хабах.
// Click → 'aiwari-open-innkeeper' → InnkeeperDialog для bind/возврата.
function addInnkeepers(scene: import('@babylonjs/core').Scene, parent: TransformNode) {
  const inns: Array<{ id: string; innName: string; pos: { x: number; z: number }; faction: 'alliance' | 'horde' | 'neutral' }> = [
    // Alliance
    { id: 'inn-stormwind',  innName: 'Льва Гордый',                pos: { x: -1510, z: 130  }, faction: 'alliance' },
    { id: 'inn-goldshire',  innName: 'Жёлтый Лев (Златоземье)',    pos: { x: -1305, z: 195  }, faction: 'alliance' },
    { id: 'inn-ironforge',  innName: 'Молот Стелы (Железный Костёр)',    pos: { x: -1175, z: 395  }, faction: 'alliance' },
    { id: 'inn-darnassus',  innName: 'Тенеречье (Лунная Ива)',         pos: { x: -1460, z: 1385 }, faction: 'alliance' },
    { id: 'inn-darkshire',  innName: 'Сцилла и Харибда',           pos: { x: -1305, z: -345 }, faction: 'alliance' },
    // Horde
    { id: 'inn-orgrimmar',  innName: 'Сломанный клык',             pos: { x: 1510, z: 30    }, faction: 'horde' },
    { id: 'inn-razor-hill', innName: 'Расщеплённая дюна',          pos: { x: 1455, z: 105   }, faction: 'horde' },
    { id: 'inn-thunder',    innName: 'Гостиница Тауро',            pos: { x: 760,  z: 1195  }, faction: 'horde' },
    { id: 'inn-undercity',  innName: 'Гнилая бочка',               pos: { x: 1180, z: -1170 }, faction: 'horde' },
    { id: 'inn-brill',      innName: 'Гостиница Брилла',           pos: { x: 1105, z: -1095 }, faction: 'horde' },
    { id: 'inn-crossroads', innName: 'Гостиница Перекрёстка',      pos: { x: 705,  z: 1105  }, faction: 'horde' },
    // Neutral
    { id: 'inn-bootybay',   innName: 'Сальти-Сэйлор (Кишащая бухта)', pos: { x: -705, z: -1495 }, faction: 'neutral' },
    { id: 'inn-gadgetzan',  innName: 'Жирный Гриф (Прибамбасск)',  pos: { x: 515,  z: -1095 }, faction: 'neutral' },
    { id: 'inn-everlook',   innName: 'Холодная Чаша (Зимоград)',   pos: { x: 1295, z: 1305  }, faction: 'neutral' },
  ];
  const matAlliance = new StandardMaterial('inn-mat-alliance', scene);
  matAlliance.emissiveColor = new Color3(0.85, 0.50, 0.20);
  matAlliance.diffuseColor = new Color3(0.65, 0.40, 0.15);
  const matHorde = new StandardMaterial('inn-mat-horde', scene);
  matHorde.emissiveColor = new Color3(0.85, 0.40, 0.20);
  matHorde.diffuseColor = new Color3(0.65, 0.30, 0.15);
  const matNeutral = new StandardMaterial('inn-mat-neutral', scene);
  matNeutral.emissiveColor = new Color3(0.95, 0.65, 0.30);
  matNeutral.diffuseColor = new Color3(0.70, 0.50, 0.20);
  for (const inn of inns) {
    const mat = inn.faction === 'alliance' ? matAlliance : inn.faction === 'horde' ? matHorde : matNeutral;
    // Fire pit — небольшой чан с пламенем (huge orange emissive sphere on top of cylinder).
    const cauldron = MeshBuilder.CreateCylinder(`inn-cauldron-${inn.id}`, { diameterTop: 0.7, diameterBottom: 0.5, height: 0.5 }, scene);
    cauldron.position = new Vector3(inn.pos.x + 0.6, 0.25, inn.pos.z);
    cauldron.material = mat;
    cauldron.parent = parent;
    const fire = MeshBuilder.CreateSphere(`inn-fire-${inn.id}`, { diameter: 0.55, segments: 8 }, scene);
    fire.position = new Vector3(inn.pos.x + 0.6, 0.7, inn.pos.z);
    fire.material = mat;
    fire.parent = parent;
    // Inn-keeper attached innkeeper marker — wider hit cylinder.
    const clickHit = MeshBuilder.CreateCylinder(`inn-hit-${inn.id}`, { diameter: 1.6, height: 1.8 }, scene);
    clickHit.position = new Vector3(inn.pos.x + 0.6, 0.9, inn.pos.z);
    clickHit.visibility = 0.0;
    clickHit.parent = parent;
    clickHit.metadata = { innkeeperId: inn.id, innName: inn.innName, innPos: inn.pos, innFaction: inn.faction };
    clickHit.isPickable = true;
    fire.metadata = { innkeeperId: inn.id, innName: inn.innName, innPos: inn.pos, innFaction: inn.faction };
    fire.isPickable = true;
  }
}

// R187: Stable Masters — WotLK 3.3.5a NPC где hunters хранят питомцев. По одному в
// каждой столице + крупных хабах. Click → 'aiwari-open-stable-master'.
function addStableMasters(scene: import('@babylonjs/core').Scene, parent: TransformNode) {
  const positions: Array<{ id: string; pos: { x: number; z: number } }> = [
    { id: 'sm-stormwind',  pos: { x: -1495, z: 175  } },
    { id: 'sm-ironforge',  pos: { x: -1165, z: 360  } },
    { id: 'sm-darnassus',  pos: { x: -1470, z: 1355 } },
    { id: 'sm-orgrimmar',  pos: { x: 1495, z: 50    } },
    { id: 'sm-thunder',    pos: { x: 740,  z: 1170  } },
    { id: 'sm-undercity',  pos: { x: 1170, z: -1190 } },
    { id: 'sm-bootybay',   pos: { x: -710, z: -1480 } },
    { id: 'sm-gadgetzan',  pos: { x: 520,  z: -1080 } },
  ];
  const mat = new StandardMaterial('stable-mat', scene);
  mat.emissiveColor = new Color3(0.40, 0.75, 0.15);
  mat.diffuseColor = new Color3(0.30, 0.55, 0.10);
  for (const sm of positions) {
    // Низкий каменный пьедестал.
    const base = MeshBuilder.CreateCylinder(`stable-base-${sm.id}`, { diameter: 0.9, height: 0.3 }, scene);
    base.position = new Vector3(sm.pos.x + 0.6, 0.15, sm.pos.z);
    base.material = mat;
    base.parent = parent;
    // Шкура-флаг.
    const flag = MeshBuilder.CreateBox(`stable-flag-${sm.id}`, { width: 0.6, height: 0.6, depth: 0.05 }, scene);
    flag.position = new Vector3(sm.pos.x + 0.6, 0.9, sm.pos.z);
    flag.material = mat;
    flag.parent = parent;
    flag.metadata = { stableMasterId: sm.id };
    flag.isPickable = true;
    const clickHit = MeshBuilder.CreateCylinder(`stable-hit-${sm.id}`, { diameter: 1.4, height: 1.5 }, scene);
    clickHit.position = new Vector3(sm.pos.x + 0.6, 0.75, sm.pos.z);
    clickHit.visibility = 0.0;
    clickHit.parent = parent;
    clickHit.metadata = { stableMasterId: sm.id };
    clickHit.isPickable = true;
  }
}

// R187: Spirit Healers — WotLK 3.3.5a эфирные NPC в graveyards. Открывают
// resurrect dialog. Расставлены ~10 точек по миру (по 1 на крупную зону).
function addSpiritHealers(scene: import('@babylonjs/core').Scene, parent: TransformNode) {
  const graveyards: Array<{ id: string; pos: { x: number; z: number } }> = [
    { id: 'gy-elwynn',      pos: { x: -1300, z: 100  } },  // Forest
    { id: 'gy-westfall',    pos: { x: -1450, z: -250 } },  // Westmarch
    { id: 'gy-redridge',    pos: { x: -1100, z: -350 } },  // Red Hills
    { id: 'gy-duskwood',    pos: { x: -1300, z: -350 } },  // Duskwood
    { id: 'gy-loch-modan',  pos: { x: -1000, z: 400  } },
    { id: 'gy-darkshore',   pos: { x: -1200, z: 1450 } },
    { id: 'gy-durotar',     pos: { x: 1450, z: 100   } },
    { id: 'gy-barrens',     pos: { x: 700,  z: 1100  } },
    { id: 'gy-mulgore',     pos: { x: 745,  z: 1185  } },
    { id: 'gy-tirisfal',    pos: { x: 1100, z: -1100 } },
    { id: 'gy-hillsbrad',   pos: { x: 1300, z: -800  } },
    { id: 'gy-stranglethorn', pos: { x: -800, z: -1300 } },
  ];
  const mat = new StandardMaterial('spirit-mat', scene);
  mat.emissiveColor = new Color3(0.55, 0.75, 0.95);  // мягкий голубой
  mat.diffuseColor = new Color3(0.40, 0.55, 0.75);
  mat.alpha = 0.85;
  for (const gy of graveyards) {
    // Парящий эфирный шар.
    const orb = MeshBuilder.CreateSphere(`spirit-orb-${gy.id}`, { diameter: 0.8, segments: 12 }, scene);
    orb.position = new Vector3(gy.pos.x + 0.5, 1.4, gy.pos.z);
    orb.material = mat;
    orb.parent = parent;
    // Каменный надгробный знак.
    const tomb = MeshBuilder.CreateBox(`spirit-tomb-${gy.id}`, { width: 0.4, height: 0.7, depth: 0.15 }, scene);
    tomb.position = new Vector3(gy.pos.x + 0.5, 0.35, gy.pos.z);
    const tombMat = new StandardMaterial(`spirit-tomb-mat-${gy.id}`, scene);
    tombMat.diffuseColor = new Color3(0.4, 0.4, 0.4);
    tomb.material = tombMat;
    tomb.parent = parent;
    orb.metadata = { spiritHealerId: gy.id };
    orb.isPickable = true;
    const clickHit = MeshBuilder.CreateCylinder(`spirit-hit-${gy.id}`, { diameter: 1.5, height: 2.2 }, scene);
    clickHit.position = new Vector3(gy.pos.x + 0.5, 1.1, gy.pos.z);
    clickHit.visibility = 0.0;
    clickHit.parent = parent;
    clickHit.metadata = { spiritHealerId: gy.id };
    clickHit.isPickable = true;
  }
}

// R195: Quartermasters — продают PvP gear за RP. По одному в столице.
function addQuartermasters(scene: import('@babylonjs/core').Scene, parent: TransformNode) {
  const positions: Array<{ id: string; faction: 'alliance' | 'horde'; city: string; pos: { x: number; z: number } }> = [
    { id: 'qm-stormwind',  faction: 'alliance', city: 'Львиная Твердыня',     pos: { x: -1480, z: 90  } },
    { id: 'qm-ironforge',  faction: 'alliance', city: 'Железный Костёр',     pos: { x: -1180, z: 405 } },
    { id: 'qm-darnassus',  faction: 'alliance', city: 'Лунная Ива',        pos: { x: -1475, z: 1395 } },
    { id: 'qm-orgrimmar',  faction: 'horde',    city: 'Цитадель Бранного Колеса',     pos: { x: 1480, z: -10  } },
    { id: 'qm-thunder',    faction: 'horde',    city: 'Грозовой Утёс', pos: { x: 760,  z: 1175 } },
    { id: 'qm-undercity',  faction: 'horde',    city: 'Тёмный Склеп',      pos: { x: 1190, z: -1190 } },
  ];
  const matAlliance = new StandardMaterial('qm-mat-alliance', scene);
  matAlliance.emissiveColor = new Color3(0.30, 0.45, 0.95);
  matAlliance.diffuseColor = new Color3(0.20, 0.30, 0.65);
  const matHorde = new StandardMaterial('qm-mat-horde', scene);
  matHorde.emissiveColor = new Color3(0.95, 0.30, 0.20);
  matHorde.diffuseColor = new Color3(0.65, 0.20, 0.15);
  for (const qm of positions) {
    const mat = qm.faction === 'alliance' ? matAlliance : matHorde;
    // Ranked banner — high tower with crown.
    const tower = MeshBuilder.CreateCylinder(`qm-tower-${qm.id}`, { diameterTop: 0.12, diameterBottom: 0.32, height: 3.5 }, scene);
    tower.position = new Vector3(qm.pos.x + 0.7, 1.75, qm.pos.z);
    tower.material = mat;
    tower.parent = parent;
    // Crown at top (medal).
    const medal = MeshBuilder.CreatePolyhedron(`qm-medal-${qm.id}`, { type: 2, size: 0.30 }, scene);
    medal.position = new Vector3(qm.pos.x + 0.7, 3.6, qm.pos.z);
    medal.material = mat;
    medal.parent = parent;
    medal.metadata = { quartermasterId: qm.id, faction: qm.faction, city: qm.city };
    medal.isPickable = true;
    const clickHit = MeshBuilder.CreateCylinder(`qm-hit-${qm.id}`, { diameter: 1.6, height: 4.0 }, scene);
    clickHit.position = new Vector3(qm.pos.x + 0.7, 2.0, qm.pos.z);
    clickHit.visibility = 0.0;
    clickHit.parent = parent;
    clickHit.metadata = { quartermasterId: qm.id, faction: qm.faction, city: qm.city };
    clickHit.isPickable = true;
  }
}

// R206: Iconic faction leaders visible в столицах. Каждый — KayKit рig
// с золотым "коронным" beacon. Click → NpcDialog с lore-flavour-text.
function addFactionLeaders(scene: import('@babylonjs/core').Scene, parent: TransformNode) {
  // 12 WotLK 3.3.5a iconic figures.
  const leaders: Array<{ id: string; ruName: string; pos: { x: number; z: number }; classId: string; faction: 'alliance' | 'horde' | 'neutral' }> = [
    // Alliance leaders
    { id: 'leader-varian',   ruName: 'Король Эдгар Светогрив',     pos: { x: -1490, z: 60   }, classId: 'paladin', faction: 'alliance' },
    { id: 'leader-magni',    ruName: 'Тан Бромвин Каменный Молот',   pos: { x: -1170, z: 410  }, classId: 'warrior', faction: 'alliance' },
    { id: 'leader-tyrande',  ruName: 'Нириэль Шёлковый Ветер', pos: { x: -1480, z: 1400 }, classId: 'cleric',  faction: 'alliance' },
    { id: 'leader-mekkatork', ruName: 'Зимм Шестерёнка',             pos: { x: -1140, z: 420  }, classId: 'mage',    faction: 'alliance' },
    // Horde leaders
    { id: 'leader-thrall',   ruName: 'Наррад Богатыр',          pos: { x: 1480, z: -10  }, classId: 'shaman',  faction: 'horde' },
    { id: 'leader-cairne',   ruName: 'Громовая Грива Багровое Копыто',   pos: { x: 750,  z: 1180 }, classId: 'druid',   faction: 'horde' },
    { id: 'leader-sylvanas', ruName: 'Велиссара Падшая',   pos: { x: 1180, z: -1200 }, classId: 'rogue',   faction: 'horde' },
    { id: 'leader-vol-jin',  ruName: 'Вол\'джин',              pos: { x: 1500, z: 60   }, classId: 'shaman',  faction: 'horde' },
    // Neutral iconic
    { id: 'leader-thrall-warchief', ruName: 'Архидруид Малфурион', pos: { x: -1450, z: 1370 }, classId: 'druid', faction: 'neutral' },
    { id: 'leader-tirion',   ruName: 'Тирион Фордринг',        pos: { x: 1140, z: -1095 }, classId: 'paladin', faction: 'neutral' },
    { id: 'leader-archmage', ruName: 'Архимаг Малин',          pos: { x: -1500, z: 80   }, classId: 'mage',    faction: 'alliance' },
    { id: 'leader-kelthuzad', ruName: 'Кел\'Тузед (изгнан)',   pos: { x: 1100, z: -1110 }, classId: 'warlock', faction: 'hostile' as never },
  ];
  const matCrown = new StandardMaterial('leader-crown-mat', scene);
  matCrown.emissiveColor = new Color3(1.00, 0.85, 0.30);  // gold
  matCrown.diffuseColor = new Color3(0.85, 0.65, 0.20);
  for (const l of leaders) {
    // R230 REMOVED: золотая корона-pillar над лидерами убрана. Лидер
    // распознаётся по чуть большему scaling (1.15×) + nameplate.
    void matCrown;
    // KayKit body для лидера.
    void loadKayKitCharacter(scene, { classId: l.classId }).then((rig) => {
      rig.root.position = new Vector3(l.pos.x, 0, l.pos.z);
      rig.root.parent = parent;
      // Слегка крупнее обычных NPC.
      rig.root.scaling = new Vector3(1.15, 1.15, 1.15);
      for (const m of rig.root.getChildMeshes(false)) {
        m.metadata = { questGiverId: l.id, leaderName: l.ruName };
        m.isPickable = true;
      }
      NPC_RIG_REGISTRY.push({ root: rig.root, rig, x: l.pos.x, z: l.pos.z, enabled: true });
    }).catch(() => { /* keep beacon */ });
  }
}

// R210: City props — anvils, forges, looms, alchemy tables, signs.
// Visual immersion для capital hubs.
function addCityProps(scene: import('@babylonjs/core').Scene, parent: TransformNode) {
  // По 6-8 props на каждую главную столицу.
  const props: Array<{ id: string; kind: 'anvil' | 'forge' | 'loom' | 'alchemy' | 'sign' | 'bank-chest' | 'cauldron' | 'training-dummy'; pos: { x: number; z: number } }> = [
    // Lionhold props
    { id: 'sw-anvil',     kind: 'anvil',          pos: { x: -1535, z: 145  } },
    { id: 'sw-forge',     kind: 'forge',          pos: { x: -1530, z: 165  } },
    { id: 'sw-loom',      kind: 'loom',           pos: { x: -1515, z: 175  } },
    { id: 'sw-alchemy',   kind: 'alchemy',        pos: { x: -1490, z: 165  } },
    { id: 'sw-sign',      kind: 'sign',           pos: { x: -1470, z: 100  } },
    { id: 'sw-bank',      kind: 'bank-chest',     pos: { x: -1485, z: 50   } },
    { id: 'sw-dummy',     kind: 'training-dummy', pos: { x: -1450, z: 70   } },
    // IronPyre
    { id: 'if-anvil',     kind: 'anvil',          pos: { x: -1175, z: 380  } },
    { id: 'if-forge',     kind: 'forge',          pos: { x: -1180, z: 360  } },
    { id: 'if-bank',      kind: 'bank-chest',     pos: { x: -1160, z: 405  } },
    { id: 'if-dummy',     kind: 'training-dummy', pos: { x: -1145, z: 395  } },
    // Moonwillow
    { id: 'dn-loom',      kind: 'loom',           pos: { x: -1465, z: 1380 } },
    { id: 'dn-cauldron',  kind: 'cauldron',       pos: { x: -1450, z: 1370 } },
    { id: 'dn-bank',      kind: 'bank-chest',     pos: { x: -1480, z: 1410 } },
    // Wartus Citadel
    { id: 'org-anvil',    kind: 'anvil',          pos: { x: 1530, z: 50    } },
    { id: 'org-forge',    kind: 'forge',          pos: { x: 1540, z: 35    } },
    { id: 'org-loom',     kind: 'loom',           pos: { x: 1525, z: 70    } },
    { id: 'org-cauldron', kind: 'cauldron',       pos: { x: 1510, z: 60    } },
    { id: 'org-bank',     kind: 'bank-chest',     pos: { x: 1515, z: -10   } },
    { id: 'org-dummy',    kind: 'training-dummy', pos: { x: 1485, z: 5     } },
    // Stormcliff
    { id: 'tb-loom',      kind: 'loom',           pos: { x: 740,  z: 1180  } },
    { id: 'tb-bank',      kind: 'bank-chest',     pos: { x: 765,  z: 1200  } },
    { id: 'tb-dummy',     kind: 'training-dummy', pos: { x: 720,  z: 1170  } },
    // Crypthold
    { id: 'uc-alchemy',   kind: 'alchemy',        pos: { x: 1185, z: -1180 } },
    { id: 'uc-cauldron',  kind: 'cauldron',       pos: { x: 1170, z: -1190 } },
    { id: 'uc-bank',      kind: 'bank-chest',     pos: { x: 1195, z: -1170 } },
  ];

  // Materials per prop kind.
  const matAnvil = new StandardMaterial('prop-mat-anvil', scene);
  matAnvil.diffuseColor = new Color3(0.30, 0.30, 0.32);  // dark steel
  const matForge = new StandardMaterial('prop-mat-forge', scene);
  matForge.emissiveColor = new Color3(0.95, 0.40, 0.10);
  matForge.diffuseColor = new Color3(0.55, 0.20, 0.10);
  const matLoom = new StandardMaterial('prop-mat-loom', scene);
  matLoom.diffuseColor = new Color3(0.55, 0.40, 0.25);  // wood
  const matAlchemy = new StandardMaterial('prop-mat-alchemy', scene);
  matAlchemy.emissiveColor = new Color3(0.30, 0.85, 0.45);
  matAlchemy.diffuseColor = new Color3(0.20, 0.50, 0.30);
  const matSign = new StandardMaterial('prop-mat-sign', scene);
  matSign.diffuseColor = new Color3(0.50, 0.35, 0.20);
  const matBank = new StandardMaterial('prop-mat-bank', scene);
  matBank.emissiveColor = new Color3(0.95, 0.75, 0.20);
  matBank.diffuseColor = new Color3(0.65, 0.50, 0.15);
  const matCauldron = new StandardMaterial('prop-mat-cauldron', scene);
  matCauldron.diffuseColor = new Color3(0.20, 0.20, 0.25);
  matCauldron.emissiveColor = new Color3(0.15, 0.45, 0.20);
  const matDummy = new StandardMaterial('prop-mat-dummy', scene);
  matDummy.diffuseColor = new Color3(0.55, 0.40, 0.25);

  for (const p of props) {
    if (p.kind === 'anvil') {
      const base = MeshBuilder.CreateBox(`prop-${p.id}-base`, { width: 0.7, height: 0.4, depth: 0.5 }, scene);
      base.position = new Vector3(p.pos.x, 0.20, p.pos.z);
      base.material = matAnvil; base.parent = parent;
      const top = MeshBuilder.CreateBox(`prop-${p.id}-top`, { width: 0.55, height: 0.20, depth: 0.30 }, scene);
      top.position = new Vector3(p.pos.x, 0.50, p.pos.z);
      top.material = matAnvil; top.parent = parent;
    } else if (p.kind === 'forge') {
      const base = MeshBuilder.CreateBox(`prop-${p.id}-base`, { width: 1.2, height: 0.6, depth: 1.2 }, scene);
      base.position = new Vector3(p.pos.x, 0.30, p.pos.z);
      base.material = matAnvil; base.parent = parent;
      const fire = MeshBuilder.CreateSphere(`prop-${p.id}-fire`, { diameter: 0.45, segments: 8 }, scene);
      fire.position = new Vector3(p.pos.x, 0.70, p.pos.z);
      fire.material = matForge; fire.parent = parent;
    } else if (p.kind === 'loom') {
      const stand = MeshBuilder.CreateBox(`prop-${p.id}-stand`, { width: 1.0, height: 1.4, depth: 0.15 }, scene);
      stand.position = new Vector3(p.pos.x, 0.70, p.pos.z);
      stand.material = matLoom; stand.parent = parent;
      const cross = MeshBuilder.CreateBox(`prop-${p.id}-cross`, { width: 1.2, height: 0.10, depth: 0.10 }, scene);
      cross.position = new Vector3(p.pos.x, 1.20, p.pos.z);
      cross.material = matLoom; cross.parent = parent;
    } else if (p.kind === 'alchemy') {
      const table = MeshBuilder.CreateBox(`prop-${p.id}-table`, { width: 1.0, height: 0.8, depth: 0.5 }, scene);
      table.position = new Vector3(p.pos.x, 0.40, p.pos.z);
      table.material = matLoom; table.parent = parent;
      const flask = MeshBuilder.CreateSphere(`prop-${p.id}-flask`, { diameter: 0.30, segments: 8 }, scene);
      flask.position = new Vector3(p.pos.x, 0.95, p.pos.z);
      flask.material = matAlchemy; flask.parent = parent;
    } else if (p.kind === 'sign') {
      const post = MeshBuilder.CreateCylinder(`prop-${p.id}-post`, { diameter: 0.10, height: 1.5 }, scene);
      post.position = new Vector3(p.pos.x, 0.75, p.pos.z);
      post.material = matSign; post.parent = parent;
      const board = MeshBuilder.CreateBox(`prop-${p.id}-board`, { width: 0.7, height: 0.45, depth: 0.05 }, scene);
      board.position = new Vector3(p.pos.x + 0.25, 1.40, p.pos.z);
      board.material = matSign; board.parent = parent;
    } else if (p.kind === 'bank-chest') {
      const chest = MeshBuilder.CreateBox(`prop-${p.id}-chest`, { width: 0.9, height: 0.5, depth: 0.6 }, scene);
      chest.position = new Vector3(p.pos.x, 0.25, p.pos.z);
      chest.material = matBank; chest.parent = parent;
      const lid = MeshBuilder.CreateBox(`prop-${p.id}-lid`, { width: 0.95, height: 0.18, depth: 0.65 }, scene);
      lid.position = new Vector3(p.pos.x, 0.55, p.pos.z);
      lid.material = matBank; lid.parent = parent;
    } else if (p.kind === 'cauldron') {
      const pot = MeshBuilder.CreateCylinder(`prop-${p.id}-pot`, { diameterTop: 0.7, diameterBottom: 0.5, height: 0.6 }, scene);
      pot.position = new Vector3(p.pos.x, 0.30, p.pos.z);
      pot.material = matCauldron; pot.parent = parent;
      const brew = MeshBuilder.CreateSphere(`prop-${p.id}-brew`, { diameter: 0.55, segments: 8 }, scene);
      brew.position = new Vector3(p.pos.x, 0.65, p.pos.z);
      brew.material = matAlchemy; brew.parent = parent;
    } else if (p.kind === 'training-dummy') {
      const post = MeshBuilder.CreateCylinder(`prop-${p.id}-post`, { diameter: 0.18, height: 1.4 }, scene);
      post.position = new Vector3(p.pos.x, 0.70, p.pos.z);
      post.material = matDummy; post.parent = parent;
      const head = MeshBuilder.CreateSphere(`prop-${p.id}-head`, { diameter: 0.45, segments: 8 }, scene);
      head.position = new Vector3(p.pos.x, 1.55, p.pos.z);
      head.material = matDummy; head.parent = parent;
    }
  }
}

function FactionPanel() {
  const factionCounts = useNet((s) => s.factionCounts);
  const stormBonuses = useNet((s) => s.stormBonuses);
  return (
    <div className="storm-glass bg-aiwari-deep/70 backdrop-blur-md rounded-xl px-3 py-2 text-xs pointer-events-auto border border-aiwari-border min-w-[180px]">
      <div className="text-[10px] uppercase tracking-wider text-aiwari-fgMuted mb-1.5">Три Ветра</div>
      <div className="space-y-1.5">
        {(['tidal', 'rooted', 'anchorless'] as const).map((s) => {
          const bonus = stormBonuses[s];
          return (
            <div key={s} className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full" style={{ background: SIDES[s].colour }} />
              <span className="text-[11px]" style={{ color: SIDES[s].colour }}>{SIDES[s].name.ru}</span>
              <span className="text-aiwari-fgDim text-[11px] ml-auto font-mono">{factionCounts[s]}</span>
              {bonus > 0.01 && (
                <span className="text-[9px] bg-aiwari-bolt/30 text-aiwari-bolt px-1.5 py-0.5 rounded font-bold ml-1">
                  +{Math.round(bonus * 100)}%
                </span>
              )}
            </div>
          );
        })}
      </div>
      <div className="text-[9px] text-aiwari-fgMuted mt-2 leading-tight">
        Storm Bonus: меньшая фракция получает +XP/+loot до выравнивания
      </div>
    </div>
  );
}

function addTreesAndRocks(scene: import('@babylonjs/core').Scene, parent: TransformNode) {
  // Bark gradient: lighter (oak) → darker (pine).
  const trunkMat = new StandardMaterial('trunk-mat', scene);
  trunkMat.diffuseColor = new Color3(0.32, 0.22, 0.13);
  trunkMat.specularColor = new Color3(0, 0, 0);
  const trunkDarkMat = new StandardMaterial('trunk-dark-mat', scene);
  trunkDarkMat.diffuseColor = new Color3(0.20, 0.13, 0.07);
  trunkDarkMat.specularColor = new Color3(0, 0, 0);

  // Three foliage shades for visual variety.
  const leafShades = [
    new Color3(0.18, 0.36, 0.18),
    new Color3(0.22, 0.42, 0.18),
    new Color3(0.32, 0.45, 0.20),
    new Color3(0.16, 0.30, 0.16),
    new Color3(0.46, 0.40, 0.18), // autumn one in the mix
  ];
  const leafMats = leafShades.map((c, i) => {
    const m = new StandardMaterial(`leaf-mat-${i}`, scene);
    m.diffuseColor = c;
    m.specularColor = new Color3(0, 0, 0);
    return m;
  });

  const rockMat = new StandardMaterial('rock-mat', scene);
  rockMat.diffuseColor = new Color3(0.34, 0.34, 0.38);
  rockMat.specularColor = new Color3(0.05, 0.05, 0.06);
  const rockDarkMat = new StandardMaterial('rock-dark-mat', scene);
  rockDarkMat.diffuseColor = new Color3(0.22, 0.22, 0.26);
  rockDarkMat.specularColor = new Color3(0.05, 0.05, 0.06);

  const rand = mulberry32(2026);

  // Trees — clustered foliage instead of a single sphere. Two species:
  //   broadleaf (oak): 3 overlapping spheres, oblate
  //   conifer (pine): 3 stacked cones, narrow
  // Procedural trees / rocks disabled — Quaternius OBJ scatter via
  // scatterProps() replaces these with real tree models.
  const TREE_COUNT = 0;
  for (let i = 0; i < TREE_COUNT; i++) {
    const x = (rand() - 0.5) * 180;
    const z = (rand() - 0.5) * 180;
    if (Math.hypot(x, z) < 10) continue;
    const conifer = rand() < 0.35;
    const scale = 0.85 + rand() * 0.7;
    const trunkH = (conifer ? 3.6 : 2.4) * scale;
    const trunk = MeshBuilder.CreateCylinder(`trunk-${i}`, {
      diameterTop: (conifer ? 0.22 : 0.36) * scale,
      diameterBottom: (conifer ? 0.42 : 0.58) * scale,
      height: trunkH,
      tessellation: 8,
    }, scene);
    trunk.position = new Vector3(x, trunkH / 2, z);
    trunk.material = conifer ? trunkDarkMat : trunkMat;
    trunk.parent = parent;
    const leaf = leafMats[Math.floor(rand() * leafMats.length)]!;
    if (conifer) {
      // Stacked cones — top-to-bottom narrowing.
      for (let k = 0; k < 4; k++) {
        const ringD = (2.6 - k * 0.5) * scale;
        const cone = MeshBuilder.CreateCylinder(`cone-${i}-${k}`, {
          diameterTop: 0.05,
          diameterBottom: ringD,
          height: 1.2 * scale,
          tessellation: 10,
        }, scene);
        cone.position = new Vector3(x, trunkH + k * 0.8 * scale - 0.4, z);
        cone.material = leaf;
        cone.parent = parent;
      }
    } else {
      // Broadleaf cluster — 3 overlapping spheres of slightly varied size.
      const base = trunkH + 0.5 * scale;
      for (let k = 0; k < 3; k++) {
        const ang = (k / 3) * Math.PI * 2 + rand() * 0.5;
        const off = 0.6 * scale;
        const cd = (1.8 + rand() * 0.9) * scale;
        const sphere = MeshBuilder.CreateSphere(`crown-${i}-${k}`, { diameter: cd, segments: 8 }, scene);
        sphere.position = new Vector3(
          x + Math.cos(ang) * off,
          base + (rand() - 0.5) * 0.3 * scale,
          z + Math.sin(ang) * off,
        );
        sphere.scaling = new Vector3(1, 0.82, 1);
        sphere.material = leaf;
        sphere.parent = parent;
      }
    }
  }

  // Procedural rock clusters disabled — Quaternius OBJ scatter replaces them.
  for (let i = 0; i < 0; i++) {
    const x = (rand() - 0.5) * 180;
    const z = (rand() - 0.5) * 180;
    if (Math.hypot(x, z) < 8) continue;
    const cluster = 1 + Math.floor(rand() * 3);
    for (let k = 0; k < cluster; k++) {
      const sz = 0.4 + rand() * 0.7;
      const r = MeshBuilder.CreatePolyhedron(`rock-${i}-${k}`, { type: 1 + Math.floor(rand() * 3), size: sz }, scene) as Mesh;
      r.position = new Vector3(x + (rand() - 0.5) * 1.2, sz * 0.5, z + (rand() - 0.5) * 1.2);
      r.rotation.y = rand() * Math.PI * 2;
      r.rotation.x = (rand() - 0.5) * 0.2;
      r.scaling = new Vector3(1, 0.7 + rand() * 0.4, 1);
      r.material = rand() < 0.4 ? rockDarkMat : rockMat;
      r.parent = parent;
    }
  }

  // Grass tufts — crossed planes (3 intersecting blades per cluster) so they
  // read as a tuft from any angle instead of a flat floating square.
  const grassMats = [
    new StandardMaterial('grass-mat-1', scene),
    new StandardMaterial('grass-mat-2', scene),
    new StandardMaterial('grass-mat-3', scene),
  ];
  grassMats[0].diffuseColor = new Color3(0.28, 0.48, 0.18);
  grassMats[1].diffuseColor = new Color3(0.34, 0.52, 0.22);
  grassMats[2].diffuseColor = new Color3(0.20, 0.38, 0.16);
  for (const gm of grassMats) {
    gm.specularColor = new Color3(0, 0, 0);
    gm.backFaceCulling = false;
  }
  // R128: 600 procedural grass-blade rectangles DISABLED — they were the
  // "уебанские зелёные палочки" the user complained about. Real grass
  // texture is on the ground material; we don't need 1800 stick meshes.
  void grassMats;

  // Flower / mushroom dots — colour pops on the dim ground.
  const flowerColours = ['#FBBF24', '#F87171', '#A78BFA', '#FCD34D'];
  for (let i = 0; i < 80; i++) {
    const x = (rand() - 0.5) * 160;
    const z = (rand() - 0.5) * 160;
    if (Math.hypot(x, z) < 5) continue;
    const m = new StandardMaterial(`flower-${i}-mat`, scene);
    m.diffuseColor = Color3.FromHexString(flowerColours[Math.floor(rand() * flowerColours.length)]!);
    m.emissiveColor = m.diffuseColor.scale(0.3);
    const flower = MeshBuilder.CreateSphere(`flower-${i}`, { diameter: 0.18, segments: 6 }, scene);
    flower.position = new Vector3(x, 0.18, z);
    flower.material = m;
    flower.parent = parent;
  }
}

function addStartingTowns(scene: import('@babylonjs/core').Scene, parent: TransformNode) {
  // R115: убираем самодельные деревни Coppergrove/Sharpridge — юзер
  // хочет только WoW-канон: Lionhold на западе, Wartus Citadel на востоке.
  // Между ними дикие земли, как в реальном WoW WotLK 3.3.5a.
  buildLionhold(scene, parent, new Vector3(-1100, 0, 0));
  buildWartusCitadel(scene, parent, new Vector3(1100, 0, 0));
  // Дорога Lionhold ↔ Wartus Citadel через neutral wilderness.
  buildRoad(scene, parent, new Vector3(-1100, 0, 0), new Vector3(0, 0, 0));
  buildRoad(scene, parent, new Vector3(0, 0, 0), new Vector3(1100, 0, 0));
}

/** Дорога между двумя точками — длинный плоский прямоугольник на земле. */
function buildRoad(scene: import('@babylonjs/core').Scene, parent: TransformNode, from: Vector3, to: Vector3) {
  const dx = to.x - from.x;
  const dz = to.z - from.z;
  const len = Math.hypot(dx, dz);
  const angle = Math.atan2(dx, dz);
  const roadMat = new StandardMaterial(`road-${from.x}-${to.x}`, scene);
  roadMat.diffuseColor = new Color3(0.42, 0.30, 0.18); // грунтово-коричневый
  roadMat.specularColor = new Color3(0, 0, 0);
  const road = MeshBuilder.CreateGround(`road-${from.x}-${to.x}`, { width: 6, height: len, subdivisions: 2 }, scene);
  road.position = new Vector3((from.x + to.x) / 2, 0.03, (from.z + to.z) / 2);
  road.rotation.y = angle;
  road.material = roadMat;
  road.parent = parent;
  road.isPickable = false;
  road.freezeWorldMatrix();
}

/** Lionhold — крепость Серебряной Зари с белыми стенами и синими конусными башнями. */
function buildLionhold(scene: import('@babylonjs/core').Scene, parent: TransformNode, center: Vector3) {
  // Материалы.
  const stoneMat = new StandardMaterial('stormwind-stone', scene);
  stoneMat.diffuseColor = new Color3(0.78, 0.76, 0.70);
  stoneMat.specularColor = new Color3(0.05, 0.05, 0.05);
  const roofMat = new StandardMaterial('stormwind-roof', scene);
  roofMat.diffuseColor = new Color3(0.20, 0.40, 0.85);
  roofMat.specularColor = new Color3(0.08, 0.08, 0.10);
  const bannerMat = new StandardMaterial('stormwind-banner', scene);
  bannerMat.diffuseColor = new Color3(0.30, 0.55, 1.0);
  bannerMat.emissiveColor = new Color3(0.12, 0.20, 0.40);
  bannerMat.backFaceCulling = false;

  // Главные стены — четыре сегмента вокруг квадратной площадки 80×80м.
  const wallH = 8;
  for (const seg of [
    { x: 0, z: -40, w: 80, d: 4 }, // юг (вход — пробел в центре)
    { x: 0, z: 40, w: 80, d: 4 },  // север
    { x: -40, z: 0, w: 4, d: 84 }, // запад
    { x: 40, z: 0, w: 4, d: 84 },  // восток
  ]) {
    // Южная стена — два куска по бокам, оставляя ворота в центре 12м.
    if (seg.z === -40) {
      for (const part of [{ off: -22, len: 36 }, { off: 22, len: 36 }]) {
        const w = MeshBuilder.CreateBox(`sw-wall-s-${part.off}`, { width: part.len, height: wallH, depth: seg.d }, scene);
        w.position = new Vector3(center.x + part.off, wallH / 2, center.z + seg.z);
        w.material = stoneMat;
        w.parent = parent;
        w.freezeWorldMatrix();
      }
      continue;
    }
    const wall = MeshBuilder.CreateBox(`sw-wall-${seg.x}-${seg.z}`, { width: seg.w, height: wallH, depth: seg.d }, scene);
    wall.position = new Vector3(center.x + seg.x, wallH / 2, center.z + seg.z);
    wall.material = stoneMat;
    wall.parent = parent;
    wall.freezeWorldMatrix();
  }

  // Четыре круглых башни в углах с синими конусными крышами (как WoW).
  for (const corner of [
    { x: -40, z: -40 }, { x: 40, z: -40 }, { x: -40, z: 40 }, { x: 40, z: 40 },
  ]) {
    const tower = MeshBuilder.CreateCylinder(`sw-tower-${corner.x}-${corner.z}`, { diameter: 8, height: 16, tessellation: 16 }, scene);
    tower.position = new Vector3(center.x + corner.x, 8, center.z + corner.z);
    tower.material = stoneMat;
    tower.parent = parent;
    tower.freezeWorldMatrix();
    const roof = MeshBuilder.CreateCylinder(`sw-roof-${corner.x}-${corner.z}`, { diameterTop: 0.3, diameterBottom: 9, height: 8, tessellation: 16 }, scene);
    roof.position = new Vector3(center.x + corner.x, 16 + 4, center.z + corner.z);
    roof.material = roofMat;
    roof.parent = parent;
    roof.freezeWorldMatrix();
  }

  // Воротная пара башен у входа (между ними проход 12м).
  for (const sx of [-7, 7]) {
    const gateTower = MeshBuilder.CreateCylinder(`sw-gate-tower-${sx}`, { diameter: 5, height: 12, tessellation: 14 }, scene);
    gateTower.position = new Vector3(center.x + sx, 6, center.z - 40);
    gateTower.material = stoneMat;
    gateTower.parent = parent;
    gateTower.freezeWorldMatrix();
    const gateRoof = MeshBuilder.CreateCylinder(`sw-gate-roof-${sx}`, { diameterTop: 0.3, diameterBottom: 6, height: 5, tessellation: 14 }, scene);
    gateRoof.position = new Vector3(center.x + sx, 12 + 2.5, center.z - 40);
    gateRoof.material = roofMat;
    gateRoof.parent = parent;
    gateRoof.freezeWorldMatrix();
  }

  // Cathedral в центре — большой блок с двумя башенками.
  const cathedral = MeshBuilder.CreateBox('sw-cathedral', { width: 14, height: 12, depth: 22 }, scene);
  cathedral.position = new Vector3(center.x, 6, center.z + 12);
  cathedral.material = stoneMat;
  cathedral.parent = parent;
  cathedral.freezeWorldMatrix();
  for (const tx of [-5, 5]) {
    const t = MeshBuilder.CreateCylinder(`sw-cath-tower-${tx}`, { diameter: 3.5, height: 18, tessellation: 12 }, scene);
    t.position = new Vector3(center.x + tx, 9, center.z + 0);
    t.material = stoneMat;
    t.parent = parent;
    t.freezeWorldMatrix();
    const tr = MeshBuilder.CreateCylinder(`sw-cath-roof-${tx}`, { diameterTop: 0.2, diameterBottom: 4, height: 6, tessellation: 12 }, scene);
    tr.position = new Vector3(center.x + tx, 18 + 3, center.z + 0);
    tr.material = roofMat;
    tr.parent = parent;
    tr.freezeWorldMatrix();
  }

  // Знамёна на воротах — 4 синих флага.
  for (const sx of [-12, 12]) {
    for (const dz of [-38, -42]) {
      const flag = MeshBuilder.CreatePlane(`sw-flag-${sx}-${dz}`, { width: 2, height: 3 }, scene);
      flag.position = new Vector3(center.x + sx, 10, center.z + dz);
      flag.material = bannerMat;
      flag.parent = parent;
      flag.freezeWorldMatrix();
    }
  }
}

/** Wartus Citadel — оркская столица, шипованные деревянные стены, огромные башни-факелы. */
function buildWartusCitadel(scene: import('@babylonjs/core').Scene, parent: TransformNode, center: Vector3) {
  const woodMat = new StandardMaterial('org-wood', scene);
  woodMat.diffuseColor = new Color3(0.32, 0.18, 0.10);
  woodMat.specularColor = new Color3(0.05, 0.05, 0.05);
  const ironMat = new StandardMaterial('org-iron', scene);
  ironMat.diffuseColor = new Color3(0.18, 0.16, 0.14);
  ironMat.specularColor = new Color3(0.20, 0.20, 0.20);
  const fireMat = new StandardMaterial('org-fire', scene);
  fireMat.diffuseColor = new Color3(1.0, 0.50, 0.15);
  fireMat.emissiveColor = new Color3(1.0, 0.45, 0.10);
  const redBannerMat = new StandardMaterial('org-banner', scene);
  redBannerMat.diffuseColor = new Color3(0.65, 0.10, 0.10);
  redBannerMat.emissiveColor = new Color3(0.20, 0.03, 0.03);
  redBannerMat.backFaceCulling = false;

  // Палисадные стены 90×90 с проходом на юге.
  const wallH = 9;
  for (const seg of [
    { x: 0, z: -45, w: 90, d: 3, gateGap: true },
    { x: 0, z: 45, w: 90, d: 3, gateGap: false },
    { x: -45, z: 0, w: 3, d: 94, gateGap: false },
    { x: 45, z: 0, w: 3, d: 94, gateGap: false },
  ]) {
    if (seg.gateGap) {
      for (const part of [{ off: -27, len: 32 }, { off: 27, len: 32 }]) {
        const w = MeshBuilder.CreateBox(`org-wall-s-${part.off}`, { width: part.len, height: wallH, depth: seg.d }, scene);
        w.position = new Vector3(center.x + part.off, wallH / 2, center.z + seg.z);
        w.material = woodMat;
        w.parent = parent;
        w.freezeWorldMatrix();
      }
      continue;
    }
    const wall = MeshBuilder.CreateBox(`org-wall-${seg.x}-${seg.z}`, { width: seg.w, height: wallH, depth: seg.d }, scene);
    wall.position = new Vector3(center.x + seg.x, wallH / 2, center.z + seg.z);
    wall.material = woodMat;
    wall.parent = parent;
    wall.freezeWorldMatrix();
  }

  // Шипы на каждой стене — конусы сверху каждые ~5м.
  for (const z of [-45, 45]) {
    for (let dx = -42; dx <= 42; dx += 6) {
      const spike = MeshBuilder.CreateCylinder(`org-spike-${z}-${dx}`, { diameterTop: 0.05, diameterBottom: 0.5, height: 2, tessellation: 6 }, scene);
      spike.position = new Vector3(center.x + dx, wallH + 1, center.z + z);
      spike.material = ironMat;
      spike.parent = parent;
      spike.freezeWorldMatrix();
    }
  }
  for (const x of [-45, 45]) {
    for (let dz = -42; dz <= 42; dz += 6) {
      const spike = MeshBuilder.CreateCylinder(`org-spike-${x}-${dz}`, { diameterTop: 0.05, diameterBottom: 0.5, height: 2, tessellation: 6 }, scene);
      spike.position = new Vector3(center.x + x, wallH + 1, center.z + dz);
      spike.material = ironMat;
      spike.parent = parent;
      spike.freezeWorldMatrix();
    }
  }

  // Две гигантские воротные башни-факелы — высокие квадратные с пламенем.
  for (const sx of [-12, 12]) {
    const gateTower = MeshBuilder.CreateBox(`org-gate-tower-${sx}`, { width: 6, height: 18, depth: 6 }, scene);
    gateTower.position = new Vector3(center.x + sx, 9, center.z - 45);
    gateTower.material = woodMat;
    gateTower.parent = parent;
    gateTower.freezeWorldMatrix();
    // Факел сверху.
    const flame = MeshBuilder.CreateSphere(`org-flame-${sx}`, { diameter: 2.5, segments: 12 }, scene);
    flame.position = new Vector3(center.x + sx, 18 + 1.5, center.z - 45);
    flame.material = fireMat;
    flame.parent = parent;
    flame.freezeWorldMatrix();
  }

  // Угловые сторожевые башни.
  for (const corner of [
    { x: -45, z: -45 }, { x: 45, z: -45 }, { x: -45, z: 45 }, { x: 45, z: 45 },
  ]) {
    const tower = MeshBuilder.CreateBox(`org-tower-${corner.x}-${corner.z}`, { width: 7, height: 14, depth: 7 }, scene);
    tower.position = new Vector3(center.x + corner.x, 7, center.z + corner.z);
    tower.material = woodMat;
    tower.parent = parent;
    tower.freezeWorldMatrix();
    const roof = MeshBuilder.CreateCylinder(`org-tower-roof-${corner.x}-${corner.z}`, { diameterTop: 0.3, diameterBottom: 8, height: 4, tessellation: 6 }, scene);
    roof.position = new Vector3(center.x + corner.x, 14 + 2, center.z + corner.z);
    roof.material = ironMat;
    roof.parent = parent;
    roof.freezeWorldMatrix();
  }

  // Тронный зал Наррада в центре — большая шестиугольная пирамида.
  const throne = MeshBuilder.CreateCylinder('org-throne', { diameter: 18, height: 14, tessellation: 6 }, scene);
  throne.position = new Vector3(center.x, 7, center.z + 15);
  throne.material = woodMat;
  throne.parent = parent;
  throne.freezeWorldMatrix();
  const throneRoof = MeshBuilder.CreateCylinder('org-throne-roof', { diameterTop: 0.5, diameterBottom: 20, height: 12, tessellation: 6 }, scene);
  throneRoof.position = new Vector3(center.x, 14 + 6, center.z + 15);
  throneRoof.material = ironMat;
  throneRoof.parent = parent;
  throneRoof.freezeWorldMatrix();

  // Красные знамёна на воротах.
  for (const sx of [-18, 18]) {
    const flag = MeshBuilder.CreatePlane(`org-flag-${sx}`, { width: 3, height: 4.5 }, scene);
    flag.position = new Vector3(center.x + sx, 12, center.z - 47);
    flag.material = redBannerMat;
    flag.parent = parent;
    flag.freezeWorldMatrix();
  }
}

/** Coppergrove — оставлен на случай возврата деревень. R115: не вызывается. */
// @ts-expect-error preserved for future use
function buildCoppergrove(scene: import('@babylonjs/core').Scene, parent: TransformNode, center: Vector3) {
  const stoneMat = new StandardMaterial('gs-stone', scene);
  stoneMat.diffuseColor = new Color3(0.86, 0.78, 0.64);
  const woodMat = new StandardMaterial('gs-wood', scene);
  woodMat.diffuseColor = new Color3(0.42, 0.28, 0.16);
  const thatchMat = new StandardMaterial('gs-thatch', scene);
  thatchMat.diffuseColor = new Color3(0.78, 0.62, 0.30);

  // Lion's Pride Inn — главное здание.
  const inn = MeshBuilder.CreateBox('gs-inn', { width: 12, height: 6, depth: 9 }, scene);
  inn.position = new Vector3(center.x, 3, center.z);
  inn.material = stoneMat;
  inn.parent = parent;
  inn.freezeWorldMatrix();
  const innRoof = MeshBuilder.CreateCylinder('gs-inn-roof', { diameterTop: 0.4, diameterBottom: 14, height: 4, tessellation: 4 }, scene);
  innRoof.position = new Vector3(center.x, 6 + 2, center.z);
  innRoof.rotation.y = Math.PI / 4;
  innRoof.material = thatchMat;
  innRoof.parent = parent;
  innRoof.freezeWorldMatrix();

  // 4 малых дома вокруг центральной площади.
  const houses = [
    { dx: 18, dz: 0 },
    { dx: -18, dz: 0 },
    { dx: 0, dz: 16 },
    { dx: 12, dz: -14 },
    { dx: -12, dz: -14 },
  ];
  for (const h of houses) {
    const house = MeshBuilder.CreateBox(`gs-house-${h.dx}-${h.dz}`, { width: 6, height: 4, depth: 5 }, scene);
    house.position = new Vector3(center.x + h.dx, 2, center.z + h.dz);
    house.material = stoneMat;
    house.parent = parent;
    house.freezeWorldMatrix();
    const roof = MeshBuilder.CreateCylinder(`gs-house-roof-${h.dx}-${h.dz}`, { diameterTop: 0.3, diameterBottom: 7.5, height: 2.5, tessellation: 4 }, scene);
    roof.position = new Vector3(center.x + h.dx, 4 + 1.25, center.z + h.dz);
    roof.rotation.y = Math.PI / 4;
    roof.material = thatchMat;
    roof.parent = parent;
    roof.freezeWorldMatrix();
  }
  // Кузница — сруб с дымоходом.
  const forge = MeshBuilder.CreateBox('gs-forge', { width: 5, height: 3.5, depth: 5 }, scene);
  forge.position = new Vector3(center.x - 24, 1.75, center.z + 8);
  forge.material = woodMat;
  forge.parent = parent;
  forge.freezeWorldMatrix();
  const chimney = MeshBuilder.CreateBox('gs-chimney', { width: 1, height: 4, depth: 1 }, scene);
  chimney.position = new Vector3(center.x - 23, 5, center.z + 9);
  chimney.material = stoneMat;
  chimney.parent = parent;
  chimney.freezeWorldMatrix();
}

/** Sharpridge — оставлен, не вызывается в R115. */
// @ts-expect-error preserved for future use
function buildRazorHill(scene: import('@babylonjs/core').Scene, parent: TransformNode, center: Vector3) {
  const woodMat = new StandardMaterial('rh-wood', scene);
  woodMat.diffuseColor = new Color3(0.30, 0.18, 0.12);
  const hideMat = new StandardMaterial('rh-hide', scene);
  hideMat.diffuseColor = new Color3(0.55, 0.30, 0.20);
  const bannerMat = new StandardMaterial('rh-banner', scene);
  bannerMat.diffuseColor = new Color3(0.65, 0.10, 0.10);
  bannerMat.emissiveColor = new Color3(0.18, 0.03, 0.03);
  bannerMat.backFaceCulling = false;

  // Низкий палисад квадрат 30×30.
  const wallH = 4;
  for (const seg of [
    { x: 0, z: -16, w: 30, d: 1.5 },
    { x: 0, z: 16, w: 30, d: 1.5 },
    { x: -16, z: 0, w: 1.5, d: 32 },
    { x: 16, z: 0, w: 1.5, d: 32 },
  ]) {
    const wall = MeshBuilder.CreateBox(`rh-wall-${seg.x}-${seg.z}`, { width: seg.w, height: wallH, depth: seg.d }, scene);
    wall.position = new Vector3(center.x + seg.x, wallH / 2, center.z + seg.z);
    wall.material = woodMat;
    wall.parent = parent;
    wall.freezeWorldMatrix();
  }
  // 3 шатра.
  for (const dx of [-8, 0, 8]) {
    const tent = MeshBuilder.CreateCylinder(`rh-tent-${dx}`, { diameterTop: 0.3, diameterBottom: 6, height: 4, tessellation: 8 }, scene);
    tent.position = new Vector3(center.x + dx, 2, center.z);
    tent.material = hideMat;
    tent.parent = parent;
    tent.freezeWorldMatrix();
  }
  // Башня сторожевая в углу.
  const tower = MeshBuilder.CreateBox('rh-tower', { width: 4, height: 8, depth: 4 }, scene);
  tower.position = new Vector3(center.x + 14, 4, center.z - 14);
  tower.material = woodMat;
  tower.parent = parent;
  tower.freezeWorldMatrix();
  const towerRoof = MeshBuilder.CreateCylinder('rh-tower-roof', { diameterTop: 0.2, diameterBottom: 5, height: 2.5, tessellation: 6 }, scene);
  towerRoof.position = new Vector3(center.x + 14, 8 + 1.25, center.z - 14);
  towerRoof.material = hideMat;
  towerRoof.parent = parent;
  towerRoof.freezeWorldMatrix();
  // Красное знамя.
  const flag = MeshBuilder.CreatePlane('rh-flag', { width: 2.5, height: 3.5 }, scene);
  flag.position = new Vector3(center.x, 7, center.z + 1);
  flag.material = bannerMat;
  flag.parent = parent;
  flag.freezeWorldMatrix();
}

function addDistantMountains(scene: import('@babylonjs/core').Scene, parent: TransformNode) {
  // Stylised mountain ring on the horizon — narrow cones grouped in 2-3
  // peak clusters, fog-tinted so they read as part of the sky atmosphere.
  const mat = new StandardMaterial('mountain-mat', scene);
  mat.diffuseColor = new Color3(0.16, 0.20, 0.30);
  mat.specularColor = new Color3(0, 0, 0);
  const snowMat = new StandardMaterial('mountain-snow-mat', scene);
  snowMat.diffuseColor = new Color3(0.85, 0.88, 0.92);
  snowMat.specularColor = new Color3(0, 0, 0);

  // R102: горы вынесены на 1400м radius (раньше 180м было прямо в
  // game-area). Теперь они на горизонте нового big-WoW мира.
  const peakCount = 60;
  for (let i = 0; i < peakCount; i++) {
    const a = (i / peakCount) * Math.PI * 2 + (i % 2 ? 0.05 : -0.05);
    const r = 1400 + Math.sin(i * 2.3) * 80;
    const cx = Math.cos(a) * r;
    const cz = Math.sin(a) * r;
    // 2-3 peaks per cluster.
    const peaks = 2 + (i % 2);
    for (let k = 0; k < peaks; k++) {
      const offX = (k - (peaks - 1) / 2) * 12;
      const offZ = (Math.sin(i * 5 + k) - 0.5) * 4;
      const h = 80 + Math.abs(Math.sin(i * 0.6 + k * 1.7)) * 120;
      const base = 80 + Math.abs(Math.cos(i * 0.4 + k)) * 60;
      const cone = MeshBuilder.CreateCylinder(`mountain-${i}-${k}`, {
        diameterTop: 0.5,
        diameterBottom: base,
        height: h,
        tessellation: 8,
      }, scene);
      cone.position = new Vector3(cx + offX, h / 2 - 0.5, cz + offZ);
      cone.material = mat;
      cone.parent = parent;
      // Snow cap on the upper third for taller peaks.
      if (h > 30) {
        const snow = MeshBuilder.CreateCylinder(`mountain-snow-${i}-${k}`, {
          diameterTop: 0.4,
          diameterBottom: base * 0.45,
          height: h * 0.32,
          tessellation: 8,
        }, scene);
        snow.position = new Vector3(cx + offX, h * 0.78, cz + offZ);
        snow.material = snowMat;
        snow.parent = parent;
      }
    }
  }
}

/**
 * Quaternius asset pack loader — кэшируем AssetContainer per file и
 * через instantiateModelsToScene создаём независимые копии для каждого
 * моба без повторной сетевой загрузки glTF. Поддерживает разные
 * под-каталоги (animals / monsters/Big / monsters/Flying).
 */
const animalContainerCache = new Map<string, Promise<import('@babylonjs/core').AssetContainer | null>>();
async function loadAnimalModel(scene: import('@babylonjs/core').Scene, file: string, subdir: string = 'animals/glTF'): Promise<import('@babylonjs/core').AssetContainer | null> {
  const cacheKey = `${subdir}/${file}`;
  if (animalContainerCache.has(cacheKey)) return animalContainerCache.get(cacheKey)!;
  const p = (async (): Promise<import('@babylonjs/core').AssetContainer | null> => {
    try {
      const { LoadAssetContainerAsync } = await import('@babylonjs/core/Loading/sceneLoader.js');
      const url = `${import.meta.env.BASE_URL}models/${subdir}/${file}`;
      const container = await LoadAssetContainerAsync(url, scene);
      return container;
    } catch (e) {
      console.warn('[models] load failed', cacheKey, (e as Error).message);
      return null;
    }
  })();
  animalContainerCache.set(cacheKey, p);
  return p;
}

/**
 * Build a kind-specific mob mesh — a TransformNode with several primitives
 * parented to it. Falls back to a generic creature for unknown kinds.
 */
function buildMobNode(
  scene: import('@babylonjs/core').Scene,
  id: string,
  kind: string,
  ensureMat: (key: string, hex: string, emissive?: string) => StandardMaterial,
  worldBossMat: StandardMaterial,
): TransformNode {
  const root = new TransformNode(`mob-root-${id}`, scene);
  const tag = (mesh: Mesh) => { mesh.metadata = { mobId: id }; mesh.parent = root; return mesh; };
  // R230 REMOVED: красные emissive шарики ("чупа-чупсы") убраны из placeholder.
  // Mob использует nameplate + HP-bar для visibility.
  void ensureMat;

  // R232q86zg6: ALL primitive placeholder bodies retired (user: «какие-то
  // коробки, табуретки»). Until the real GLB swaps in, a mob is an invisible
  // click volume — nameplate + HP bar carry visibility, no black boxes.
  if (kind !== 'world-boss') {
    const hit = MeshBuilder.CreateBox(`mob-${id}-placeholder`, { width: 0.8, height: 1.8, depth: 0.8 }, scene);
    hit.isVisible = false;
    hit.isPickable = true;
    hit.position.y = 0.9;
    tag(hit);
    return root;
  }

  if (kind === 'world-boss') {
    const body = MeshBuilder.CreateBox(`mob-${id}-body`, { width: 4, height: 3, depth: 6 }, scene);
    body.material = worldBossMat;
    tag(body);
    body.position.y = 0;
    // Head
    const head = MeshBuilder.CreateBox(`mob-${id}-head`, { width: 2, height: 1.5, depth: 2.4 }, scene);
    head.material = worldBossMat;
    head.position = new Vector3(0, 1.4, -3.4);
    tag(head);
    // Wings
    for (const sign of [-1, 1]) {
      const wing = MeshBuilder.CreatePlane(`mob-${id}-wing-${sign}`, { width: 5, height: 3 }, scene);
      wing.material = worldBossMat;
      wing.position = new Vector3(sign * 2.5, 1.5, 0);
      wing.rotation.y = sign * 0.6;
      wing.rotation.z = sign * -0.4;
      const wm = (wing.material as StandardMaterial);
      wm.backFaceCulling = false;
      tag(wing);
    }
    return root;
  }

  if (kind === 'wolf' || kind === 'boar') {
    const placeholder = MeshBuilder.CreateBox(`mob-${id}-placeholder`, { width: 0.5, height: 0.5, depth: 0.9 }, scene);
    placeholder.material = ensureMat(`mob-${kind}-placeholder`, kind === 'wolf' ? '#5C3F22' : '#3A2A1A');
    placeholder.position.y = 0.4;
    tag(placeholder);
    // Quaternius pack не имеет boar модели (Bull.gltf — это бык).
    // Используем Wolf.gltf для обоих — для boar накладываем тёмно-коричневый
    // tint и слегка увеличенный scale, чтобы был визуально отличим.
    const file = 'Wolf.gltf';
    void loadAnimalModel(scene, file).then((proto) => {
      if (!proto) return;
      const inst = proto.instantiateModelsToScene(() => `mob-${id}-anim`, false);
      const rootMeshes = inst.rootNodes;
      for (const rn of rootMeshes) {
        rn.parent = root;
        const tn = rn as TransformNode;
        const s = kind === 'boar' ? 0.7 : 0.55;
        if (tn.scaling) tn.scaling = new Vector3(s, s, s);
      }
      // Tint материал per-kind.
      const tintColour = kind === 'boar'
        ? Color3.FromHexString('#3A2A1A')   // тёмно-коричневый кабан
        : Color3.FromHexString('#6B5440');  // серо-коричневый волк
      for (const rn of rootMeshes) {
        const stack: import('@babylonjs/core').Node[] = [rn];
        while (stack.length) {
          const n = stack.pop()!;
          const mesh = n as Mesh;
          if (mesh.getTotalVertices && mesh.getTotalVertices() > 0) {
            mesh.metadata = { mobId: id };
            const mat = mesh.material as { diffuseColor?: Color3; copyFrom?: (c: Color3) => void } | null;
            if (mat?.diffuseColor) {
              mat.diffuseColor.r = tintColour.r;
              mat.diffuseColor.g = tintColour.g;
              mat.diffuseColor.b = tintColour.b;
            }
          }
          for (const c of n.getChildren()) stack.push(c);
        }
      }
      placeholder.dispose();
    }).catch(() => { /* keep placeholder */ });
    return root;
  }

  // R73: bandit / goblin / wraith — реальные Quaternius monster модели.
  // Все три используют Big-monsters pack (Orc/Tribal/Skull/Demon).
  const monsterMap: Record<string, { file: string; subdir: string; scale: number; tint?: string }> = {
    bandit: { file: 'Tribal.gltf',    subdir: 'monsters/Big/glTF',    scale: 0.55 },
    goblin: { file: 'Orc.gltf',       subdir: 'monsters/Big/glTF',    scale: 0.50 },
    wraith: { file: 'Ghost.gltf',     subdir: 'monsters/Flying/glTF', scale: 0.55, tint: '#A6C8FF' },
    spider: { file: 'Cactoro.gltf',   subdir: 'monsters/Big/glTF',    scale: 0.45, tint: '#1F2A1F' },
    golem:  { file: 'Orc_Skull.gltf', subdir: 'monsters/Big/glTF',    scale: 0.85 },
    dragonkin: { file: 'Dragon.gltf', subdir: 'monsters/Flying/glTF', scale: 0.7 },
  };
  if (monsterMap[kind]) {
    const cfg = monsterMap[kind]!;
    const placeholder = MeshBuilder.CreateBox(`mob-${id}-placeholder`, { width: 0.4, height: 0.8, depth: 0.4 }, scene);
    placeholder.material = ensureMat(`mob-${kind}-placeholder`, cfg.tint ?? '#5A4A3A');
    placeholder.position.y = 0.45;
    tag(placeholder);
    void loadAnimalModel(scene, cfg.file, cfg.subdir).then((proto) => {
      if (!proto) return;
      const inst = proto.instantiateModelsToScene(() => `mob-${id}-anim`, false);
      const tintC = cfg.tint ? Color3.FromHexString(cfg.tint) : null;
      for (const rn of inst.rootNodes) {
        rn.parent = root;
        const tn = rn as TransformNode;
        if (tn.scaling) tn.scaling = new Vector3(cfg.scale, cfg.scale, cfg.scale);
      }
      for (const rn of inst.rootNodes) {
        const stack: import('@babylonjs/core').Node[] = [rn];
        while (stack.length) {
          const n = stack.pop()!;
          const mesh = n as Mesh;
          if (mesh.getTotalVertices && mesh.getTotalVertices() > 0) {
            mesh.metadata = { mobId: id };
            if (tintC) {
              const mat = mesh.material as { diffuseColor?: Color3 } | null;
              if (mat?.diffuseColor) {
                mat.diffuseColor.r = tintC.r;
                mat.diffuseColor.g = tintC.g;
                mat.diffuseColor.b = tintC.b;
              }
            }
          }
          for (const c of n.getChildren()) stack.push(c);
        }
      }
      placeholder.dispose();
    }).catch(() => { /* keep placeholder */ });
    return root;
  }

  if (kind === 'bandit') {
    const skin = ensureMat('bandit-skin', '#C8A684');
    const cloth = ensureMat('bandit', '#5A1818');
    const body = MeshBuilder.CreateBox(`mob-${id}-body`, { width: 0.45, height: 0.7, depth: 0.3 }, scene);
    body.material = cloth; body.position.y = 1.05; tag(body);
    const head = MeshBuilder.CreateSphere(`mob-${id}-head`, { diameter: 0.32, segments: 10 }, scene);
    head.material = skin; head.position.y = 1.6; tag(head);
    for (const sign of [-1, 1]) {
      const arm = MeshBuilder.CreateBox(`mob-${id}-arm-${sign}`, { width: 0.11, height: 0.55, depth: 0.11 }, scene);
      arm.material = skin; arm.position = new Vector3(0.28 * sign, 1.05, 0); tag(arm);
      const leg = MeshBuilder.CreateBox(`mob-${id}-leg-${sign}`, { width: 0.13, height: 0.7, depth: 0.13 }, scene);
      leg.material = ensureMat('bandit-pants', '#1B1B22');
      leg.position = new Vector3(0.1 * sign, 0.35, 0); tag(leg);
    }
    // Hat
    const hat = MeshBuilder.CreateCylinder(`mob-${id}-hat`, { diameterTop: 0.06, diameterBottom: 0.32, height: 0.18, tessellation: 12 }, scene);
    hat.material = cloth; hat.position.y = 1.83; tag(hat);
    return root;
  }

  if (kind === 'goblin') {
    const skin = ensureMat('goblin-skin', '#5C7A3A');
    const cloth = ensureMat('goblin-cloth', '#7A4A22');
    const body = MeshBuilder.CreateBox(`mob-${id}-body`, { width: 0.4, height: 0.5, depth: 0.28 }, scene);
    body.material = cloth; body.position.y = 0.72; tag(body);
    const head = MeshBuilder.CreateSphere(`mob-${id}-head`, { diameter: 0.36, segments: 10 }, scene);
    head.material = skin; head.position.y = 1.16; head.scaling = new Vector3(1, 1.05, 0.9); tag(head);
    // Pointed ears.
    for (const sign of [-1, 1]) {
      const ear = MeshBuilder.CreateCylinder(`mob-${id}-ear-${sign}`, { diameterTop: 0.02, diameterBottom: 0.12, height: 0.18, tessellation: 6 }, scene);
      ear.material = skin; ear.position = new Vector3(0.16 * sign, 1.18, 0);
      ear.rotation.z = sign * -0.7; tag(ear);
    }
    for (const sign of [-1, 1]) {
      const arm = MeshBuilder.CreateBox(`mob-${id}-arm-${sign}`, { width: 0.09, height: 0.4, depth: 0.09 }, scene);
      arm.material = skin; arm.position = new Vector3(0.24 * sign, 0.72, 0); tag(arm);
      const leg = MeshBuilder.CreateBox(`mob-${id}-leg-${sign}`, { width: 0.11, height: 0.42, depth: 0.11 }, scene);
      leg.material = cloth; leg.position = new Vector3(0.08 * sign, 0.23, 0); tag(leg);
    }
    return root;
  }

  if (kind === 'wraith') {
    const m = ensureMat('wraith', '#2C2440', '#5C4A88');
    m.alpha = 0.8;
    const body = MeshBuilder.CreateCylinder(`mob-${id}-body`, { diameterTop: 0.4, diameterBottom: 0.9, height: 1.6, tessellation: 12 }, scene);
    body.material = m; body.position.y = 0.8; tag(body);
    const head = MeshBuilder.CreateSphere(`mob-${id}-head`, { diameter: 0.38, segments: 10 }, scene);
    head.material = m; head.position.y = 1.7; tag(head);
    return root;
  }

  if (kind === 'golem') {
    const m = ensureMat('golem', '#5A5048');
    const body = MeshBuilder.CreateBox(`mob-${id}-body`, { width: 1.2, height: 1.4, depth: 0.9 }, scene);
    body.material = m; body.position.y = 1.0; tag(body);
    const head = MeshBuilder.CreateBox(`mob-${id}-head`, { width: 0.6, height: 0.5, depth: 0.5 }, scene);
    head.material = m; head.position.y = 1.95; tag(head);
    for (const sign of [-1, 1]) {
      const arm = MeshBuilder.CreateBox(`mob-${id}-arm-${sign}`, { width: 0.35, height: 1.0, depth: 0.35 }, scene);
      arm.material = m; arm.position = new Vector3(0.85 * sign, 1.1, 0); tag(arm);
      const leg = MeshBuilder.CreateBox(`mob-${id}-leg-${sign}`, { width: 0.4, height: 0.7, depth: 0.4 }, scene);
      leg.material = m; leg.position = new Vector3(0.3 * sign, 0.35, 0); tag(leg);
    }
    return root;
  }

  if (kind === 'dragonkin') {
    const m = ensureMat('dragonkin', '#4A1F2E', '#3A0F1A');
    const body = MeshBuilder.CreateBox(`mob-${id}-body`, { width: 0.85, height: 1.1, depth: 1.4 }, scene);
    body.material = m; body.position.y = 1.0; tag(body);
    const head = MeshBuilder.CreateBox(`mob-${id}-head`, { width: 0.55, height: 0.45, depth: 0.7 }, scene);
    head.material = m; head.position = new Vector3(0, 1.4, -1.0); tag(head);
    const tail = MeshBuilder.CreateBox(`mob-${id}-tail`, { width: 0.18, height: 0.18, depth: 1.0 }, scene);
    tail.material = m; tail.position = new Vector3(0, 1.0, 1.1); tag(tail);
    for (const sign of [-1, 1]) {
      const wing = MeshBuilder.CreatePlane(`mob-${id}-wing-${sign}`, { width: 1.4, height: 0.9 }, scene);
      wing.material = m; wing.position = new Vector3(sign * 0.7, 1.4, 0);
      wing.rotation.y = sign * 0.3; wing.rotation.z = sign * -0.4;
      (wing.material as StandardMaterial).backFaceCulling = false;
      tag(wing);
    }
    return root;
  }

  // Fallback — generic creature.
  const fallback = MeshBuilder.CreateBox(`mob-${id}`, { width: 0.7, height: 1.2, depth: 1.2 }, scene);
  fallback.material = ensureMat('generic', '#4A3A28');
  tag(fallback);
  return root;
}

function mulberry32(seed: number) {
  let t = seed;
  return () => {
    t |= 0; t = (t + 0x6d2b79f5) | 0;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

// Pre-existing helpers retained for future scenery iterations.
void FactionPanel;
void addStartingTowns;
void addDistantMountains;
